/**
 * Internationalization (I18N) Translations
 * Contains all UI strings for supported languages
 * 
 * Languages: en (English), es (Spanish)
 */

// Date placeholders follow the app-wide format defined in src/utils/date.js.
import { datePlaceholder } from '../utils/date.js';

export const I18N_LOCALES = {
    en: 'en-US',
    es: 'es-ES'
};

export const I18N = {
    en: {
        'boot.loading': 'Loading Nautilus',
        'lock.welcome': 'Welcome back',
        'lock.subtitle': 'Enter the access password to continue.',
        'lock.sessionHint': '? Session stays signed-in for 24 hours.',
        'lock.passwordLabel': 'Password',
        'lock.passwordPlaceholder': 'Enter password',
        'lock.unlock': 'Unlock',
        'auth.backToUserLogin': 'Back to User Login',
        'auth.login.title': 'Welcome to Nautilus',
        'auth.login.subtitle': 'Login to access your workspace',
        'auth.login.identifierLabel': 'Username or email',
        'auth.login.identifierPlaceholder': 'Enter username or email',
        'auth.login.pinLabel': 'PIN (4 digits)',
        'auth.admin.title': 'Admin Access',
        'auth.admin.passwordLabel': 'Admin password',
        'auth.admin.passwordRequired': 'Enter the admin password',
        'auth.admin.opening': 'Opening the admin dashboard…',
        'auth.setup.title': 'Complete Your Setup',
        'auth.setup.subtitle': 'Personalize your account',
        'auth.setup.usernameLabel': 'Username',
        'auth.setup.usernamePlaceholder': 'e.g., alex',
        'auth.setup.usernameHint': '3-20 characters, lowercase letters and numbers only',
        'auth.setup.displayNameLabel': 'Display Name',
        'auth.setup.displayNamePlaceholder': 'e.g., Alex Morgan',
        'auth.setup.emailLabel': 'Email',
        'auth.setup.emailPlaceholder': 'your.email@example.com',
        'auth.setup.emailHint': 'Required for notifications and account deletion confirmation',
        'auth.setup.newPinLabel': 'New PIN (4 digits)',
        'auth.setup.confirmPinLabel': 'Confirm PIN',
        'auth.setup.next': 'Next',
        'auth.setup.submit': 'Complete Setup',
        'admin.title': 'User Management',
        'admin.signOut': 'Sign Out',
        'admin.usersTitle': 'Users',
        'admin.createUserTitle': 'Create New User',
        'admin.usernameLabel': 'Username',
        'admin.usernamePlaceholder': 'e.g., jdoe',
        'admin.usernameHint': '3-20 characters, lowercase letters and numbers only',
        'admin.displayNameLabel': 'Display Name',
        'admin.displayNamePlaceholder': 'e.g., John Doe',
        'admin.tempPinLabel': 'Temporary PIN (4 digits)',
        'admin.createUserButton': 'Create User',
        'auth.admin.resetPinPrompt': 'Enter new temporary PIN for {userName}:',
        'auth.admin.pinMustBe4Digits': 'PIN must be exactly 4 digits',
        'auth.admin.resetPinFailed': 'Failed to reset PIN',
        'auth.admin.resetPinSuccess': 'PIN reset for {userName}. New temp PIN: {pin}',
        'auth.admin.deleteUserConfirm': 'Are you sure you want to delete user \"{userName}\"? This will also delete all their tasks and projects.',
        'auth.admin.deleteUserFailed': 'Failed to delete user',
        'auth.admin.deleteUserSuccess': 'User \"{userName}\" deleted successfully',
        'auth.login.passwordLabel': 'Password',
        'auth.login.passwordPlaceholder': 'Enter your password',
        'auth.login.forgotCredential': 'Forgot your password or PIN?',
        'auth.login.submit': 'Sign in',
        'auth.login.brandTag': 'TASK MANAGER',
        'auth.login.workspaceSubtitle': 'Sign in to your workspace',
        'auth.login.identifierExample': 'you@institute.org',
        'auth.login.pinProgress': '{count} of {total} digits entered',
        'auth.login.keypadDelete': 'Delete last digit',
        'auth.login.keypadEnter': 'Sign in',
        'auth.login.opening': 'Opening your workspace…',
        'auth.login.identifierRequired': 'Enter your username or email',
        'auth.login.passwordRequired': 'Enter your password',
        'auth.login.pinIncorrect': 'Incorrect PIN',
        'auth.login.passwordIncorrect': 'Incorrect password',
        'auth.login.tooManyAttempts': 'Too many attempts. Please try again later.',
        'auth.login.failed': 'Couldn’t sign in. Please try again.',
        'auth.login.creature.region': 'Featured sea creature',
        'auth.login.creature.counter': 'CREATURE OF THE DAY',
        'auth.login.creature.prev': 'Previous creature',
        'auth.login.creature.next': 'Next creature',
        'auth.login.creature.learnMore': 'Learn more about this species',
        'auth.login.creature.imageAlt': 'Illustration: {name}',
        'auth.login.creature.watchVideo': 'Watch video',
        'auth.login.creature.closeVideo': 'Close video',
        'auth.login.creature.videoTitle': 'Featured creature video',
        'auth.setup.authMethodLabel': 'Authentication Method',
        'auth.setup.methodPin': 'PIN',
        'auth.setup.methodPassword': 'Password',
        'auth.setup.newPasswordLabel': 'Password',
        'auth.setup.passwordPlaceholder': '8+ chars, 1 upper, 1 lower, 1 digit',
        'auth.setup.newPasswordPlaceholder': '8+ chars, 1 upper, 1 lower, 1 digit',
        'auth.setup.confirmPasswordLabel': 'Confirm Password',
        'auth.setup.confirmPasswordPlaceholder': 'Re-enter password',
        'auth.setup.passwordRequirements': '8+ characters, uppercase, lowercase, and a digit',
        'auth.forgot.title': 'Reset Your Login',
        'auth.forgot.subtitle': 'Enter your email to receive a reset link',
        'auth.forgot.emailLabel': 'Email Address',
        'auth.forgot.emailPlaceholder': 'your.email@example.com',
        'auth.forgot.submit': 'Send Reset Link',
        'auth.forgot.errorEmpty': 'Please enter your email address',
        'auth.forgot.errorInvalidEmail': 'Please enter a valid email address',
        'auth.forgot.successSentTo': 'Reset link sent to',
        'auth.forgot.successCheckInbox': 'Check your inbox (and spam folder).',
        'auth.forgot.successExpiry': 'The link expires in 1 hour.',
        'auth.forgot.backToLogin': 'Back to Login',
        'auth.forgot.sending': 'Sending…',
        'auth.forgot.errorGeneric': 'Something went wrong. Please try again.',
        'auth.backToLogin': 'Back to Login',
        'auth.reset.title': 'Set New Credential',
        'auth.reset.subtitle': 'Choose your authentication method and set a new credential',
        'auth.reset.methodPassword': 'Password',
        'auth.reset.methodPin': 'PIN',
        'auth.reset.submit': 'Reset Credential',
        'auth.reset.success': 'Credential reset successfully! Redirecting to login...',
        'auth.reset.newPasswordLabel': 'New Password',
        'auth.reset.confirmPasswordLabel': 'Confirm Password',
        'auth.setup.passwordHint': '8+ characters, 1 uppercase, 1 lowercase, 1 digit',
        'common.cancel': 'Cancel',
        'common.clear': 'Clear',
        'common.uploadingFile': 'Uploading {name}...',
        'common.uploadingFileProgress': 'Uploading {name}… {progress}%',
        'common.delete': 'Delete',
        'common.continue': 'Continue',
        'common.gotIt': 'Got it',
        'common.close': 'Close modal',
        'common.done': 'Done',
        'common.updating': 'Updating...',
        'common.nautilusLogoAlt': 'Nautilus logo',
        'common.devBanner': '⚠️ LOCAL DEV - NOT PRODUCTION ⚠️',
        'common.datePlaceholder': datePlaceholder('en'),
        'crop.title': 'Crop Image to Square',
        'crop.instructions': 'Drag to adjust the crop area. The image will be cropped to a square.',
        'crop.apply': 'Crop & Apply',
        'crop.close': 'Close crop modal',
        'dashboard.insights.productivityTitle': 'Productivity Trend',
        'dashboard.insights.productivityDesc': 'Task completion rate increased 23% compared to last month',
        'dashboard.insights.deadlineTitle': 'Deadline Alert',
        'dashboard.insights.deadlineDesc': '3 milestones due within the next 7 days',
        'projects.title': 'Projects',
        'projects.subtitle': 'Manage your projects',
        'projects.archived.title': 'Archived Projects',
        'projects.archived.subtitle': 'Archived projects are hidden from active project views until restored.',
        'projects.archived.back': '← Back to Projects',
        'projects.archived.colProgress': 'Progress',
        'projects.archived.selectAll': 'Select all archived projects',
        'projects.archived.selected': '{count} of {total} projects selected',
        'projects.archived.emptyTitle': 'No archived projects',
        'projects.archived.emptySubtitle': 'Projects you archive will appear here.',
        'projects.archived.noResults': 'No archived projects match your filters',
        'projects.archived.noResultsSubtitle': 'Try adjusting your search or filters.',
        'projects.archived.bulkRestored': '{count} projects restored',
        'projects.searchPlaceholder': '\u{1F50D} Search projects (title or description)',
        'projects.filters.status': 'Status',
        'projects.filters.statusTitle': 'Project Status',
        'projects.filters.archived': 'Archived',
        'projects.status.backlog': 'BACKLOG',
        'projects.status.planning': 'PLANNING',
        'projects.status.active': 'ACTIVE',
        'projects.status.completed': 'COMPLETED',
        'projects.status.archived': 'ARCHIVED',
        'projects.archive.title': 'Archive project',
        'projects.archive.modalTitle': 'Archive project',
        'projects.archive.modalTitleWithName': 'Archive project: {{name}}',
        'projects.archive.intro': 'Archiving hides this project from active views. Nothing is deleted, and you can restore it later.',
        'projects.archive.summary.open': 'Open',
        'projects.archive.summary.completed': 'Completed',
        'projects.archive.summary.archived': 'Already archived',
        'projects.archive.option.projectOnly': 'Archive project only',
        'projects.archive.option.projectOnlyHint': 'Tasks stay visible in active views.',
        'projects.archive.option.completedChildren': 'Archive project and completed tasks',
        'projects.archive.option.completedChildrenHint': 'Only tasks with status Done get archived. Open tasks remain active.',
        'projects.archive.option.allChildren': 'Archive project and all tasks',
        'projects.archive.option.allChildrenHint': 'Hides every non-archived task in this project. Status is never changed.',
        'projects.archive.recommended': '(recommended)',
        'projects.archive.confirm': 'Archive',
        'projects.restore.title': 'Restore project',
        'projects.restore.modalTitle': 'Restore project',
        'projects.restore.modalTitleWithName': 'Restore project: {{name}}',
        'projects.restore.hasArchived': 'This project has',
        'projects.restore.archivedTasks': 'archived tasks.',
        'projects.restore.option.projectOnly': 'Restore project only',
        'projects.restore.option.withChildren': 'Restore project and its archived tasks',
        'projects.restore.confirm': 'Restore',
        'projects.bulkRestore.modalTitle': 'Restore Projects',
        'projects.bulkRestore.intro': 'The selected projects have',
        'projects.bulkRestore.option.projectsOnly': 'Restore projects only',
        'projects.bulkRestore.option.withChildren': 'Restore projects and their archived tasks',
        'projects.filters.updated': 'Updated',
        'projects.filters.updatedTitle': 'Recently Updated',
        'projects.filters.completion': 'Progress',
        'projects.filters.completionTitle': 'Tasks Completed',
        'projects.filters.completion100': '100% complete',
        'projects.filters.completion75': '75% or more',
        'projects.filters.completion50': '50% or more',
        'projects.filters.completionUnder50': 'Under 50%',
        'projects.filters.completion0': 'Not started (0%)',
        'projects.filters.all': 'All',
        'projects.filters.last5m': 'Last 5 minutes',
        'projects.filters.last30m': 'Last 30 minutes',
        'projects.filters.last24h': 'Last 24 hours',
        'projects.filters.lastWeek': 'Last week',
        'projects.filters.lastMonth': 'Last month',
        'projects.filters.hasTasks': 'Has tasks',
        'projects.filters.noTasks': 'No tasks',
        'projects.filters.generatedChip': 'Recently generated',
        'projects.filters.reportChip': 'From report',
        'projects.viewToggle.cards': 'Card view',
        'projects.viewToggle.rows': 'List view',
        'projects.filters.tags': 'Tags',
        'projects.filters.tagsTitle': 'Project Tags',
        'projects.filters.clear': 'Clear Filters',
        'projects.noDescription': 'No description',
        'projects.card.tasksProgress': '{done} of {total} tasks',
        'projects.card.noStart': 'No start',
        'projects.card.noEnd': 'No end',
        'projects.card.overdueOne': 'Overdue by 1 day',
        'projects.card.overdueDays': 'Overdue by {count} days',
        'projects.card.dueToday': 'Due today',
        'projects.card.dueInOne': 'in 1 day',
        'projects.card.dueInDays': 'in {count} days',
        'projects.card.showTasks': 'Show tasks',
        'projects.card.hideTasks': 'Hide tasks',
        'projects.card.tasksTitle': 'Tasks',
        'projects.card.pendingDone': '{pending} pending · {done} done',
        'projects.card.col.task': 'Task',
        'projects.card.col.date': 'Date',
        'projects.card.col.priority': 'Priority',
        'projects.card.col.status': 'Status',
        'projects.tasksBreakdown': '{total} tasks · {done} done',
        'tasks.card.description': 'Description',
        'projects.card.tasksCount': '{count} tasks',
        'projects.card.percentDone': '{count}% done',
        'projects.sort.label': 'Sort: Status',
        'projects.expandAll': 'Expand all',
        'projects.collapseAll': 'Collapse all',
        'projects.expandAllTitle': 'Expand all projects',
        'projects.collapseAllTitle': 'Collapse all projects',
        'projects.sort.status': 'Status',
        'projects.sort.name': 'Name',
        'projects.sort.newest': 'Newest',
        'projects.sort.lastUpdated': 'Last Updated',
        'projects.sort.mostTasks': 'Most tasks',
        'projects.sort.percentCompleted': '% Completed',
        'projects.sort.prefix': 'Sort: {label}',
        'projects.sort.help': 'Click the same option again to switch Asc/Desc.',
        'projects.sort.statusLabel': 'Status',
        'projects.sort.nameLabel': 'Name',
        'projects.sort.newestLabel': 'Newest',
        'projects.sort.lastUpdatedLabel': 'Last Updated',
        'projects.sort.mostTasksLabel': 'Most tasks',
        'projects.sort.percentCompletedLabel': '% Completed',
        'projects.newProjectButton': '+ New Project',
        'projects.openDetailsTitle': 'View details',
        'projects.empty.title': 'No projects yet',
        'projects.empty.subtitle': 'Create your first project',
        'projects.empty.searchTitle': 'No projects found',
        'projects.empty.searchSubtitle': 'Create a new project to get started',
        'projects.empty.filteredTitle': 'No projects matched',
        'projects.modal.createTitle': 'Create New Project',
        'projects.modal.nameLabel': 'Project Name',
        'projects.modal.descriptionLabel': 'Description',
        'projects.modal.startDateLabel': 'Start Date',
        'projects.modal.endDateLabel': 'End Date',
        'projects.modal.tagsLabel': 'Tags',
        'projects.modal.addTagPlaceholder': 'Add tag',
        'projects.modal.createButton': 'Create Project',
        'projects.delete.title': 'Delete Project',
        'projects.delete.body': 'This action cannot be undone. To confirm deletion, type delete below:',
        'projects.delete.deleteTasksLabel': 'Delete all tasks in this project',
        'projects.delete.deleteTasksHint': 'If unchecked, tasks will be unassigned from the project',
        'projects.delete.inputPlaceholder': 'Type delete here',
        'projects.delete.error': 'Type \"delete\" exactly to confirm',
        'projects.duplicate.title': 'Duplicate Project',
        'projects.duplicate.nameLabel': 'New project name',
        'projects.duplicate.defaultName': '{name} (copy)',
        'projects.duplicate.includeTasksLabel': 'Include tasks',
        'projects.duplicate.includeTasksHint': 'Statuses reset to To Do · archived tasks are skipped',
        'projects.duplicate.startDateLabel': 'Start date',
        'projects.duplicate.startDateHint': 'Change it to move all task dates by the same amount.',
        'projects.duplicate.prefixPlaceholder': 'e.g., Copy - ',
        'projects.duplicate.suffixPlaceholder': 'e.g., (Copy)',
        'projects.duplicate.confirm': 'Duplicate Project',
        'projects.duplicate.success': 'Project "{name}" duplicated successfully',
        'projects.duplicate.successWithTasks': 'Project "{name}" and its tasks duplicated successfully',
        'projects.template.saveAsTemplate': 'Save as Template',
        'projects.template.modalTitle': 'Save as Template',
        'projects.template.nameLabel': 'Template name',
        'projects.template.includeTasksLabel': 'Include tasks',
        'projects.template.includeTasksHint': 'Copies all tasks · statuses reset to Backlog · dates cleared',
        'projects.template.saveButton': 'Save Template',
        'projects.template.savedSuccess': 'Template saved',
        'projects.template.deletedSuccess': 'Template deleted',
        'projects.template.renamedSuccess': 'Template renamed',
        'projects.template.deleteModalTitle': 'Delete Template?',
        'projects.template.renameModalTitle': 'Rename Template',
        'projects.template.newNameLabel': 'New name',
        'projects.template.renameButton': 'Rename',
        'projects.template.taskCountOne': '{count} task',
        'projects.template.taskCountMany': '{count} tasks',
        'projects.details.dragToReorder': 'Drag to reorder',
        'projects.modal.searchProjects': 'Search projects...',
        'projects.modal.templateLabel': 'Start from template',
        'projects.modal.templateNone': 'None',
        'projects.modal.taskNaming': 'Task naming',
        'projects.modal.namingOriginal': 'Original',
        'projects.modal.namingAddPrefix': '+ Prefix',
        'projects.modal.namingAddSuffix': '+ Suffix',
        'projects.statusInfo.title': 'How Project Status Works',
        'projects.statusInfo.subtitle': 'Project status is automatically determined by task progress.',
        'projects.statusInfo.backlogBadge': 'BACKLOG',
        'projects.statusInfo.backlogDesc': 'No tasks yet, or all tasks are still in \"Backlog\". Nothing has been planned.',
        'projects.statusInfo.planningBadge': 'PLANNING',
        'projects.statusInfo.planningDesc': 'At least one task is in \"To Do\", but none are in progress.',
        'projects.statusInfo.activeBadge': 'ACTIVE',
        'projects.statusInfo.activeDesc': 'At least one task is \"In Progress\" or \"In Review\".',
        'projects.statusInfo.completedBadge': 'COMPLETED',
        'projects.statusInfo.completedDesc': 'All tasks are marked as \"Done\".',
        'projects.statusInfo.archivedBadge': 'ARCHIVED',
        'projects.statusInfo.archivedDesc': 'The project has been archived. It is hidden from active views regardless of task progress, and can be restored at any time.',
        'projects.backTo.dashboard': 'Back to Dashboard',
        'projects.backTo.calendar': 'Back to Calendar',
        'projects.backTo.projects': 'Back to Projects',
        'projects.backTo.archived': 'Back to Archived Projects',
        'projects.details.tab.details': 'Details',
        'projects.details.tab.docs': 'Docs',
        'projects.details.tab.history': 'History',
        'docs.projectWiki': 'Pages',
        'docs.pageTitle': 'Documentation',
        'docs.pageSubtitle': 'A focused writing space for notes, wikis, and internal documentation.',
        'docs.projectLabel': 'Project',
        'docs.createFirstInline': 'Create a page to start writing.',
        'docs.noProjectsTitle': 'No projects yet',
        'docs.noProjectsSubtitle': 'Create a project first, then write documentation for it here.',
        'docs.pages': '📄 Pages',
        'docs.newPage': 'New page',
        'docs.createFirstPage': 'Create first page',
        'docs.untitledPage': 'Untitled page',
        'docs.slashCommandFailed': 'Could not insert that block. Please try again.',
        'docs.searchAria': 'Search pages',
        'docs.searchPlaceholder': 'Search pages...',
        'docs.searchClear': 'Clear search',
        'docs.searchNoResults': 'No pages match',
        'docs.searchResultCount': '{count} result(s)',
        'docs.searchInTitle': 'title',
        'docs.searchInContent': 'content',
        'docs.breadcrumbAria': 'Page location',
        'docs.breadcrumbRoot': 'Pages',
        'docs.titlePlaceholder': 'Untitled page',
        'docs.loading': 'Loading documentation...',
        'docs.saved': 'Saved',
        'docs.savedToast': 'Documentation saved',
        'docs.saving': 'Saving...',
        'docs.save': 'Save',
        'docs.edit': 'Edit',
        'docs.createdLabel': 'Created',
        'docs.updatedLabel': 'Updated',
        'docs.switchToReading': 'Switch to reading',

        'docs.resumeEditing': 'Resume editing',

        'docs.doneEditing': 'Done',
        'docs.doneEditingTooltip': 'Done editing — back to the reading view, where tables sort and links open. Your changes are already saved.',
        'docs.copyChip': 'Copy chip',
        'docs.saveFailed': 'Save failed',
        'docs.autosaveSavedAt': 'Saved {time}',
        'docs.replaceGuardTitle': 'Remove most of this page?',
        'docs.replaceGuardBody': 'This page is shared through a public link, and your edit deletes most of its content. Anyone with the link will see the shortened page.',
        'docs.replaceGuardConfirm': 'Save anyway',
        'docs.replaceGuardKeep': 'Keep editing',
        'docs.replaceGuardBlocked': 'Not saved — the page is shared and this edit removes most of it',
        'docs.unsavedChanges': 'Unsaved changes',
        'docs.history': 'History',
        'askKai.openButton': 'Ask Kai',
        'askKai.modalTitle': 'Ask Kai',
        'askKai.drawerSubtitle': 'Plan work, shape content, and review the result before applying.',
        'askKai.actionLabel': 'Action',
        'askKai.actionAuto': 'Auto (infer best action)',
        'askKai.actionFind': 'Query tasks or projects',
        'askKai.modeFind': 'Query',
        // Find mode (natural-language filtering)
        'askKai.find.placeholder': 'e.g. urgent open Nautilus tasks tagged franky',
        'askKai.find.matchCount': '{count} matching',
        'askKai.find.resultsTasks': 'There are {count} matching tasks:',
        'askKai.find.resultsProjects': 'There are {count} matching projects:',
        'askKai.find.showingOf': 'Showing {shown} of {total}. Click {button} to see them all.',
        'askKai.find.noMatches': 'Nothing matches these filters.',
        'askKai.find.emptyHint': 'Dropping “{dimension}” would match {count}.',
        'askKai.find.emptyQuery': 'I could not turn that into a filter. Try naming a tag, a project, a priority, a status or a due date.',
        'askKai.find.unknownValue': 'I could not find a project or tag named “{term}”.',
        'askKai.find.ambiguous': '“{term}” matches more than one thing. Which did you mean?',
        'askKai.find.contradictoryRange': 'That date range is contradictory. No date filter was applied.',
        'askKai.find.blocked': 'I could not apply this filter.',
        'askKai.find.showTasks': 'Show tasks',
        'askKai.find.showProjects': 'Show projects',
        'askKai.find.refine': 'Refine',
        'askKai.find.clear': 'Clear Kai filter',
        'askKai.find.not': 'not',
        'askKai.find.joinOr': ' or ',
        'askKai.find.joinAnd': ' and ',
        'askKai.find.value.none': 'none',
        // Dimension labels for the interpreted-filter chips
        'askKai.find.dimension.search': 'Text',
        'askKai.find.dimension.status': 'Status',
        'askKai.find.dimension.priority': 'Priority',
        'askKai.find.dimension.project': 'Project',
        'askKai.find.dimension.tag': 'Tag',
        'askKai.find.dimension.datePreset': 'Due',
        'askKai.find.dimension.dateRange': 'Date range',
        'askKai.find.dimension.linkType': 'Links',
        'askKai.find.dimension.updated': 'Updated',
        'askKai.find.dimension.scope': 'Scope',
        'askKai.find.dimension.projectStatus': 'Project status',
        'askKai.find.dimension.taskFilter': 'Tasks',
        'askKai.find.dimension.completion': 'Progress',
        'askKai.find.dimension.created': 'Created',
        'askKai.find.dimension.updatedRange': 'Updated',
        'askKai.find.dimension.completed': 'Completed',
        'askKai.find.dimension.duplicate': 'Duplicate',
        'askKai.find.value.duplicate': 'by title',
        'askKai.find.dimension.recency': 'Order',
        'askKai.find.dimension.limit': 'Limit',
        'askKai.find.recency.recent-updated': 'most recently modified',
        'askKai.find.recency.recent-created': 'most recently created',
        'askKai.find.wrongKind': 'There is no tag named “{term}”, but there is a {kind} with that name.',
        'askKai.find.unsupported.updatedWindow': 'Nautilus can only filter by these update windows: 5 minutes, 30 minutes, 24 hours, this week, this month.',
        'askKai.find.contradictoryCompletion': 'That progress range is contradictory. No progress filter was applied.',
        'askKai.find.preset.overdue': 'Overdue',
        'askKai.find.preset.no-date': 'No end date',
        'askKai.find.preset.has-date': 'Has an end date',
        'askKai.find.preset.has-start-date': 'Has a start date',
        'askKai.find.ambiguousKind': '“{term}” is both a project and a tag. Which did you mean?',
        'askKai.find.kind.tag': 'Tag',
        'askKai.find.kind.project': 'Project',
        'askKai.find.preset.end-today': 'Today',
        'askKai.find.preset.end-tomorrow': 'Tomorrow',
        'askKai.find.preset.end-week': 'This week',
        'askKai.find.preset.end-month': 'This month',
        'askKai.find.preset.no-start-date': 'No start date',
        'askKai.find.preset.already-started': 'Already started',
        'askKai.find.link.is_blocked_by': 'Blocked',
        'askKai.find.link.blocks': 'Blocking others',
        'askKai.find.link.relates_to': 'Related',
        'askKai.find.link.has_web_links': 'Has links',
        'askKai.find.link.no_links': 'No links',
        // Honest refusals — Nautilus does not model these
        'askKai.find.unsupported.generic': 'Nautilus does not track “{term}”, so I left it out.',
        'askKai.find.unsupported.assignee': 'Nautilus has no assignee field — people are tags. Name one and I will filter by that tag.',
        'askKai.find.unsupported.effort': 'Nautilus does not track estimated effort.',
        'askKai.find.unsupported.milestone': 'Nautilus has no milestones, sprints or epics.',
        'askKai.find.unsupported.workspace': 'Nautilus has no workspaces.',
        'askKai.find.unsupported.subtask': 'Nautilus has no subtasks or parent tasks.',
        'askKai.find.unsupported.category': 'Nautilus has no categories — use tags instead.',
        'askKai.find.unsupported.createdDate': 'Nautilus cannot filter by creation date.',
        'askKai.find.unsupported.noPriority': 'Every task has a priority, so “no priority” cannot match anything.',
        'askKai.find.unsupported.sort': 'Kai cannot set the sort order. Click a column header in list view.',
        'askKai.find.unsupported.groupBy': 'Nautilus cannot group results. The Kanban board groups by status.',
        'askKai.find.unsupported.limit': 'Nautilus cannot limit how many results are shown.',
        'askKai.find.unsupported.projectAggregate': 'Projects cannot be filtered by their tasks’ priority, due date or blocked state.',
        'askKai.errors.findFailed': 'Could not apply that filter.',
        'askKai.actionAutomation': 'Create recurring automation',
        'askKai.actionAutomationHint': 'Describe a task and how often it should repeat',
        'askKai.actionDraft': 'Create documentation',
        'askKai.actionRewrite': 'Rewrite or summarize text',
        'askKai.actionApplyChips': 'Turn table labels into colored status chips',
        'askKai.actionProject': 'Create project with starter tasks',
        'askKai.actionProjectOnly': 'Create project only',
        'askKai.actionTasksOnly': 'Create tasks only',
        'askKai.modeLabel': 'Mode',
        'askKai.modeHelp': 'Pick a lane or leave it on auto.',
        'askKai.modeAuto': 'Auto-detect',
        'askKai.modeAutomation': 'Automation',
        'askKai.modeProject': 'Project + Tasks',
        'askKai.modeProjectOnly': 'Projects',
        'askKai.modeTasksOnly': 'Tasks',
        'askKai.modeTemplate': 'Documentation',
        'askKai.modeRewrite': 'Rewrite',
        'askKai.modeApplyChips': 'Status chips',
        'askKai.useDocContext': 'Use current page as context',
        'askKai.useGeneratedProjectTags': 'Generate project tags',
        'askKai.useGeneratedTaskTags': 'Generate task tags',
        'askKai.useGeneratedProjectTaskTags': 'Generate project and task tags',
        'askKai.instructionLabel': 'Instruction',
        'askKai.instructionPlaceholder': 'Describe what you want Kai to generate...',
        'askKai.promptLabel': 'Prompt',
        'askKai.promptHelp': 'Describe the outcome you want. Kai will generate a reviewable proposal first.',
        'askKai.clearPrompt': 'Clear',
        'askKai.savePrompt': 'Save prompt',
        'askKai.unsavePrompt': 'Remove saved prompt',
        'askKai.savedPrompts': 'Saved prompts',
        'askKai.savedEmpty': 'No saved prompts yet. Type a prompt and tap the star to save it.',
        'askKai.searchSavedPrompts': 'Search saved prompts',
        'askKai.savedNoMatches': 'No saved prompts match your search.',
        'askKai.deleteSavedPrompt': 'Delete saved prompt',
        'askKai.deleteSavedPromptConfirm': 'Are you sure you want to delete this prompt?',
        'askKai.editPromptName': 'Rename saved query',
        'askKai.promptName': 'Saved query name',
        'askKai.savePromptName': 'Save query name',
        'askKai.promptNameUpdated': 'Query name updated',
        'askKai.promptSaved': 'Prompt saved',
        'askKai.promptRemoved': 'Prompt removed',
        'askKai.promptAlreadySaved': 'That prompt is already saved',
        'askKai.savedError': 'Could not save your prompt. Please try again.',
        'askKai.expandPrompt': 'Expand',
        'askKai.shortcutHint': 'Press {submitShortcut} to generate a proposal.',
        'askKai.openButtonTooltip': 'Ask Kai ({shortcut})',
        'askKai.previewLabel': 'Preview',
        'askKai.previewPlaceholder': 'Generate a proposal to preview it here.',
        'askKai.previewEmptyTitle': 'Generate a proposal',
        'askKai.previewEmptySummary': 'Kai will turn your prompt into a structured review before anything is applied.',
        'askKai.reviewLabel': 'Review',
        'askKai.reviewHelp': 'Review the proposal before anything is applied.',
        'askKai.generatingSummary': 'Reviewing your request and building the proposal.',
        'askKai.generatingSummaryWithSeconds': 'Reviewing your request and building the proposal.',
        'askKai.intentHint': 'Kai will infer the best action from your instruction.',
        'askKai.intentHintAuto': 'Kai will infer the best action from your instruction.',
        'askKai.intentHintManual': 'Kai will use {mode}.',
        'askKai.intentAutoResolved': 'Kai inferred {mode}.',
        'askKai.intentManualResolved': 'Using {mode}.',
        'askKai.inferredMode': 'Inferred: {mode}',
        'askKai.contextProjects': 'Projects context',
        'askKai.contextTasks': 'Tasks context',
        'askKai.contextDocs': 'Docs context',
        'askKai.contextDocSource': 'Using current page text',
        'askKai.contextGenerateTags': 'Generating tags',
        'askKai.ambiguityHeader': 'Choose how Kai should structure this request.',
        'askKai.ambiguityFooter': 'Choose an option above to enable confirm.',
        'askKai.ambiguitySelectedPrefix': 'Selected:',
        'askKai.ambiguityPreviewLabel': 'Selected option',
        'askKai.stats.projects': 'Projects',
        'askKai.stats.tasks': 'Tasks',
        'askKai.stats.pages': 'Pages',
        'askKai.stats.options': 'Options',
        'askKai.stats.sections': 'Sections',
        'askKai.stats.characters': 'Characters',
        'askKai.stats.levels': 'Levels',
        'askKai.meta.tasksCount': '{count} tasks',
        'askKai.meta.tagsCount': '{count} tags',
        'askKai.meta.project': 'Project: {name}',
        'askKai.meta.noProject': 'No project',
        'askKai.meta.levelLabel': 'Level {count}',
        'askKai.meta.levelShort': 'L{count}',
        'askKai.meta.childOf': 'Under {title}',
        'askKai.meta.rootPage': 'Root page',
        'askKai.meta.noExcerpt': 'No page excerpt available yet.',
        'askKai.seeMore': 'See more',
        'askKai.viewMore': 'View {count} more',
        'askKai.showLess': 'Show less',
        'askKai.review.blockedSummary': 'This draft is missing required parts from your prompt. Regenerate before applying.',
        'askKai.review.blockedExhausted': 'Kai already retried this prompt once and still could not satisfy the required result. Edit the prompt before generating again.',
        'askKai.review.errorTitle': 'Generation Error',
        'askKai.review.errorSummary': 'Kai could not turn this prompt into a reviewable proposal.',
        'askKai.review.fixButton': 'Regenerate Missing Parts',
        'askKai.actionIndicator.project': 'Create Project + Tasks',
        'askKai.actionIndicator.multiProject': 'Create Multiple Projects',
        'askKai.actionIndicator.projectOnly': 'Create Project',
        'askKai.actionIndicator.tasksOnly': 'Create Tasks',
        'askKai.actionIndicator.automation': 'Create Automation',
        'askKai.actionIndicator.template': 'Create Documentation',
        'askKai.actionIndicator.rewrite': 'Rewrite Text',
        'askKai.actionIndicator.applyChips': 'Status Chips',
        'askKai.actionIndicator.proposalSuffix': 'proposal',
        'askKai.actionIndicator.default': 'Waiting for instruction...',
        'askKai.actionIndicator.ready': 'Ready to generate',
        'askKai.actionIndicator.proposalReady': 'Proposal ready',
        'askKai.generateButton': 'Generate',
        'askKai.confirmButton': 'Apply',
        'askKai.generating': 'Generating proposal...',
        'askKai.generatingWithSeconds': 'Generating proposal... {seconds}s',
        'askKai.errors.instructionRequired': 'Please add an instruction for Ask Kai.',
        'askKai.errors.sourceRequired': 'Please provide source text to rewrite or summarize.',
        'askKai.errors.invalidProposal': 'Ask Kai returned an invalid proposal.',
        'askKai.errors.generateFailed': 'Could not generate Ask Kai proposal. Please try again.',
        'askKai.errors.generateTimedOut': 'Kai is taking longer than expected. Please try again.',
        'askKai.errors.confirmFailed': 'Could not apply Ask Kai proposal. Please try again.',
        'askKai.errors.choiceRequired': 'Choose one structure before confirming.',
        'askKai.errors.reviewBlocked': 'This Ask Kai proposal still has blocking issues. Regenerate it before applying.',
        'askKai.errors.repairExhausted': 'Kai already retried this prompt once. Edit the prompt before generating again.',
        'askKai.errors.generatingKeepOpen': 'Kai is still generating. Keep the panel open until the proposal appears.',
        'askKai.errors.rateLimited': 'Ask Kai is busy right now. Please try again shortly.',
        'askKai.errors.inputTooLong': 'Your Ask Kai input is too long. Please shorten it and try again.',
        'askKai.errors.rewriteDocsOnly': 'Open a docs page to rewrite or summarize existing text.',
        'askKai.errors.rewriteRequiresDocPage': 'Rewrite mode can only be applied on an active documentation page.',
        'askKai.errors.currentPageEmpty': 'Kai cannot summarize or rewrite this page because it has no text yet.',
        'askKai.errors.chipNoTable': 'Kai needs a table with a header row on this page to create status chips.',
        'askKai.errors.chipNoMatch': 'Kai found no matching cells to convert into status chips.',
        'askKai.confirm.discardTitle': 'Discard Kai Proposal?',
        'askKai.confirm.discardBody': 'You have a Kai proposal open. Are you sure you want to close and lose it?',
        'askKai.confirm.discardButton': 'Discard Proposal',
        'askKai.confirm.regenerateTitle': 'Generate Similar Proposal?',
        'askKai.confirm.regenerateBody': 'You have not changed the prompt or options. Generating again will likely return a similar proposal.',
        'askKai.confirm.regenerateButton': 'Generate Again',
        'askKai.success.singleProject': 'Created project: {name}',
        'askKai.success.multiProject': 'Created {count} projects',
        'askKai.undoToast.label': 'Applied successfully.',
        'askKai.undoToast.subtitle': 'Your content is ready to view.',
        'askKai.undoToast.view': 'View',
        'askKai.undoToast.close': 'Close',
        'askKai.undoToast.button': 'Undo',
        'askKai.undoToast.error': 'Undo failed. Please delete manually.',
        'docs.shareLink': 'Share link',
        'docs.shareCreateLink': 'Create link',
        'docs.shareSaveExpiry': 'Save expiry',
        'docs.shareModalTitle': 'Public link ready',
        'docs.shareModalSubtitle': 'Use this public URL to share the page.',
        'docs.shareModalLabel': 'Public link',
        'docs.shareModalNote': 'Anyone with this link can view the page',
        'docs.shareModalInfoButton': 'More info',
        'docs.shareModalCopy': 'Copy link',
        'docs.shareModalCopyDone': 'Copied',
        'docs.shareModalCopied': 'Public link copied to clipboard',
        'docs.shareSettingsUpdated': 'Link settings updated',
        'docs.shareLinkCreated': 'Public link created',
        'docs.shareLinkDisabled': 'A public link is already active. Revoke it first.',
        'docs.revokeLink': 'Revoke link',
        'docs.revokeLinkSuccess': 'Access to the page has been removed or revoked.',
        'docs.shareExpiryLabel': 'Expiration',
        'docs.shareExpiryEnds': 'Expires on {date}',
        'docs.shareExpiry1d': '24h',
        'docs.shareExpiry1w': '7 days',
        'docs.shareExpiry1m': '30 days',
        'docs.shareExpiryNever': 'Never',
        'docs.shareExpiryCustom': 'Custom...',
        'docs.shareExpiryDate': 'Date',
        'docs.shareExpiryTime': 'Time',
        'docs.shareExpiryMinutes': 'Min',
        'docs.shareExpiryInvalid': 'Please enter a valid future date and time.',
        'docs.linkExpiredTitle': 'Link expired',
        'docs.linkExpired': 'This share link has expired and is no longer available.',
        'docs.linkUnavailableTitle': 'Link not available',
        'docs.linkUnavailable': 'This share link has been revoked or is no longer available.',
        'docs.shareLinkExpiredNotice': 'This link has expired. Choose a new expiration and renew it.',
        'docs.shareRenewLink': 'Renew link',
        'docs.shareLinkError': 'Could not update the public link. Please try again.',
        'docs.revokeLinkError': 'Could not revoke the link. Please try again.',
        'docs.shareSessionExpired': 'Your session has expired. Please log in and try again.',
        'docs.shareChipActive': 'Active',
        'docs.shareChipExpired': 'Expired',
        'docs.shareCreateSubtitle': 'Anyone with the link can view this page.',
        'docs.shareChangeExpiry': 'Change expiration',
        'docs.shareReactivateHeading': 'Reactivate link',
        'docs.shareExpiryInstantHint': 'Applied instantly — no save button.',
        'docs.shareExpiryApply': 'Apply date',
        'docs.shareCreatePreview': 'Will expire on {date}',
        'docs.shareCreatePreviewNever': 'The link will not expire.',
        'docs.shareExpiryChooseFuture': 'Choose a future date and time.',
        'docs.shareStatusExpiresOn': 'Expires on {date}',
        'docs.shareStatusNoExpiry': 'No expiration — the link does not expire.',
        'docs.shareStatusExpired': 'Expired on {date} — this link is inactive.',
        'docs.shareLinkReactivated': 'Link reactivated',
        'docs.width': 'Width',
        'docs.width.narrow': 'Narrow',
        'docs.width.wide': 'Wide',
        'docs.pageOptions': 'Page options',
        'docs.pageIcon': 'Page icon',
        'docs.removeIcon': 'Remove icon',
        'docs.expandPage': 'Expand page',
        'docs.collapsePage': 'Collapse page',
        'docs.expandAllPages': 'Expand all pages',
        'docs.collapseAllPages': 'Collapse all pages',
        'docs.deletePage': 'Delete page',
        'docs.duplicate.title': 'Duplicate page',
        'docs.removeNesting': 'Remove nesting',
        'docs.duplicate.newTitleLabel': 'New title',
        'docs.duplicate.includeChildren': 'Include child pages',
        'docs.duplicate.confirm': 'Duplicate',
        'docs.duplicate.success': 'Page duplicated',
        'docs.duplicate.errorEmptyTitle': 'Title cannot be empty',
        'docs.duplicate.errorDuplicateTitle': 'A page with this title already exists at the same level',
        'docs.deleteConfirm': 'Delete this documentation page? This cannot be undone.',
        'docs.deleteConfirmBody': 'will be permanently deleted. This action cannot be undone.',
        'docs.trash': 'Trash',
        'docs.trashBack': 'Back',
        'docs.trashTitle': 'Docs Trash',
        'docs.trashSubtitle': 'Deleted pages stay here until restored.',
        'docs.trashPage': 'Move page to trash?',
        'docs.trashConfirmBody': 'will be moved to trash. You can restore it later.',
        'docs.trashEmpty': 'Trash is empty.',
        'docs.trashEmptyTitle': 'Trash is empty',
        'docs.trashEmptySubtitle': 'Deleted documentation pages will appear here so you can restore them.',
        'docs.trashNoResultsTitle': 'No pages found',
        'docs.trashNoResultsSubtitle': 'Try a different search term.',
        'docs.trashSearchPlaceholder': 'Search pages...',
        'docs.trashSearchAria': 'Search trashed pages',
        'docs.trashSortNewest': 'Recently deleted',
        'docs.trashSortOldest': 'Oldest first',
        'docs.trashSortAria': 'Sort trashed pages',
        'docs.trashCount': '{count} page(s)',
        'docs.trashTablePage': 'Page',
        'docs.trashTableLocation': 'Location',
        'docs.trashTableCreated': 'Created',
        'docs.trashTableDeleted': 'Deleted',
        'docs.trashTableActions': 'Actions',
        'docs.trashLocationLabel': 'Original location',
        'docs.trashDeletedLabel': 'Deleted',
        'docs.trashRootLocation': 'Root',
        'docs.trashLocationUnknown': 'Location unavailable',
        'docs.trashPreviewTitle': 'Page Preview',
        'docs.trashPreviewSubtitle': 'Review the page before restoring it.',
        'docs.trashPreviewLabel': 'Trash preview',
        'docs.trashPreviewButton': 'Preview',
        'docs.trashRestore': 'Restore',
        'docs.trashRestored': 'Page restored from trash',
        'docs.trashDeletePermanent': 'Delete permanently',
        'docs.trashDeletedPermanent': 'Page permanently deleted',
        'docs.trashEmptyButton': 'Empty trash',
        'docs.trashEmptyConfirmTitle': 'Empty trash?',
        'docs.trashEmptyConfirmBody': 'You are about to permanently delete {count} page(s) from trash.',
        'docs.trashEmptyAffected': 'Pages that will be permanently deleted: {count}',
        'docs.trashEmptied': 'Trash emptied ({count} page(s))',
        'docs.trashPermanentDeleteTitle': 'Delete page permanently?',
        'docs.trashPermanentDeleteBody': '"{title}" will be permanently deleted from trash. This action cannot be undone.',
        'docs.trashedLabel': 'Trashed {date}',
        'docs.movedToTrash': 'Page moved to trash.',
        'docs.bulkMovedToTrash': 'Moved {count} page(s) to trash.',
        'docs.bulkSelect': 'Select',
        'docs.bulkSelectPage': 'Select page',
        'docs.bulkSelectCancel': 'Cancel',
        'docs.bulkSelectCount': '{count} selected',
        'docs.bulkDeleteTitle': 'Delete Pages?',
        'docs.bulkDeleteBody': 'You are about to permanently delete {count} documentation page(s).',
        'docs.bulkDeleteRoots': 'Selected roots: {count}',
        'docs.bulkDeleteDescendants': 'Selected parent pages will delete their descendants too.',
        'docs.bulkDeleteAffected': 'Affected pages: {count}',
        'docs.bulkDeleteMore': '+{count} more page(s)',
        'docs.bulkTrashTitle': 'Move pages to trash?',
        'docs.bulkTrashBody': 'You are about to move {count} documentation page(s) to trash.',
        'docs.bulkTrashRoots': 'Selected roots: {count}',
        'docs.bulkTrashDescendants': 'Selected parent pages will move their descendants to trash too.',
        'docs.bulkTrashAffected': 'Affected pages: {count}',
        'docs.bulkTrashMore': '+{count} more page(s)',
        'docs.bulkDeleteTypeInstruction': 'Type delete to confirm.',
        'docs.bulkDeleteTypePlaceholder': 'Type delete here',
        'docs.bulkDeleteTypeError': 'Type "delete" exactly to confirm',
        'docs.bulkDeletePartialFailed': 'Moving to trash stopped because "{title}" could not be updated.',
        'docs.bulkDeleteSuccess': 'Deleted {count} page(s).',
        'docs.historyTitle': 'Page history',
        'docs.historyEmpty': 'No saved revisions yet.',
        'docs.historyRestore': 'Restore',
        'docs.historyCurrent': 'Current',
        'docs.historyRestored': 'Page restored from history',
        'docs.historySelectVersion': 'Review a saved version before deciding whether to restore it.',
        'docs.historyReadyToRestore': 'Restore this version?',
        'docs.historyRestoreHint': 'This will replace the current page content with the selected version.',
        'docs.historyCurrentHint': 'You are previewing the latest saved version of this page.',
        'docs.historySnapshotFallback': 'Saved page snapshot',
        'docs.historyVersionLabel': 'Version {number}',
        'docs.contentCorrupted': 'This page contains invalid saved content. Review it before saving again.',
        'docs.emptyTitle': 'No documentation yet',
        'docs.emptySubtitle': 'Create a lightweight wiki for this project with notes, code blocks, tables, and callouts.',
        'docs.noSummary': 'No summary yet',
        'docs.editorPlaceholder': 'Write documentation for this project...',
        'docs.tocTitle': 'Table of contents',
        'docs.tocEmpty': 'Add headings to build the table of contents.',
        'docs.childListTitle': 'Child pages',
        'docs.noChildPages': 'No child pages yet.',
        'docs.imagesNotSupported': 'Paste images as files elsewhere; inline images are disabled for docs.',
        'docs.imageNoPage': 'Create or load a page before adding images.',
        'docs.imageInvalidType': 'Only image files can be added to docs.',
        'docs.imageTooLarge': 'Images must be 25MB or smaller.',
        'docs.fileNoPage': 'Create or load a page before adding files.',
        'docs.fileInvalidType': 'This file type is not supported in docs.',
        'docs.fileBlockedType': 'This file type is blocked for safety reasons.',
        'docs.fileTooLarge': 'Files must be {maxMB}MB or smaller.',
        'docs.fileUploading': 'Uploading {name}... {progress}%',
        'docs.fileUploaded': '{name} added to the page.',
        'docs.fileOpen': 'Open',
        'docs.fileDownload': 'Download',
        'docs.fileConvertToCard': 'Convert to card',
        'docs.fileConvertToChip': 'Convert to chip',
        'docs.fileRemove': 'Remove file',
        'docs.fileUntitled': 'Untitled file',
        'docs.fileActions': 'File actions',
        'docs.imageSize.small': 'Small',
        'docs.imageSize.medium': 'Medium',
        'docs.imageSize.large': 'Large',
        'docs.imageSize.full': 'Full width',
        'docs.imageResize': 'Resize image',
        'docs.copyCode': 'Copy to clipboard',
        'docs.copyCodeDone': 'Copied',
        'docs.copyCodeFailed': 'Failed',
        'docs.codeWrap': 'Wrap text',
        'docs.codeOneLine': 'One line',
        'docs.deleteBlock': 'Delete',
        'docs.toolbar.bullets': 'Bullets',
        'docs.toolbar.numbers': 'Numbers',
        'docs.toolbar.divider': 'Divider',
        'docs.toolbar.code': 'Code',
        'docs.toolbar.bold': 'Bold',
        'docs.toolbar.italic': 'Italic',
        'docs.toolbar.underline': 'Underline',
        'docs.toolbar.textStyle': 'Text style',
        'docs.toolbar.paragraph': 'Paragraph',
        'docs.toolbar.heading1': 'Heading 1',
        'docs.toolbar.heading2': 'Heading 2',
        'docs.toolbar.heading3': 'Heading 3',
        'docs.toolbar.alignment': 'Text alignment',
        'docs.toolbar.alignLeft': 'Align left',
        'docs.toolbar.alignCenter': 'Align center',
        'docs.toolbar.alignRight': 'Align right',
        'docs.toolbar.alignJustify': 'Justify',
        'docs.toolbar.checklist': 'Checklist',
        'docs.toolbar.emoji': 'Insert emoji',
        'docs.toolbar.undo': 'Undo',
        'docs.toolbar.redo': 'Redo',
        'docs.findReplace.title': 'Find & Replace',
        'docs.findReplace.findPlaceholder': 'Find...',
        'docs.findReplace.replacePlaceholder': 'Replace with...',
        'docs.findReplace.replace': 'Replace',
        'docs.findReplace.replaceAll': 'Replace all',
        'docs.findReplace.noMatch': 'No matches',
        'docs.findReplace.prev': 'Previous match',
        'docs.findReplace.next': 'Next match',
        'docs.findReplace.replacedCount': 'Replaced {count} occurrence(s)',
        'docs.findReplace.close': 'Close',
        'docs.toolbar.image': 'Image',
        'docs.toolbar.file': 'File',
        'docs.toolbar.video': 'YouTube video',
        'docs.toolbar.status': 'Status chip',
        'docs.toolbar.dateChip': 'Date',
        'docs.toolbar.table': 'Table',
        'docs.toolbar.info': 'Info',
        'docs.toolbar.warning': 'Warning',
        'docs.toolbar.success': 'Success',
        'docs.toolbar.columns': 'Columns',
        'docs.toolbar.macros': 'Macros',
        'docs.toolbar.toc': 'Table of contents',
        'docs.toolbar.childList': 'Child pages',
        'docs.toolbar.programTimeline': 'Program timeline',
        'roadmap.zoom.weeks': 'Weeks',
        'roadmap.zoom.weeksShort': 'W',
        'roadmap.zoom.weeksAbout': 'about 5 months',
        'roadmap.zoom.months': 'Months',
        'roadmap.zoom.monthsShort': 'M',
        'roadmap.zoom.monthsAbout': 'about 9 months',
        'roadmap.zoom.quarters': 'Quarters',
        'roadmap.zoom.quartersShort': 'Q',
        'roadmap.zoom.quartersAbout': 'about 18 months',
        'roadmap.time.justNow': 'just now',
        'roadmap.time.minutesAgo': '{count} min ago',
        'roadmap.time.hoursAgo.one': '{count} hr ago',
        'roadmap.time.hoursAgo.other': '{count} hrs ago',
        'roadmap.time.daysAgo.one': '{count} day ago',
        'roadmap.time.daysAgo.other': '{count} days ago',
        'roadmap.ruler.label': 'Calendar ruler',
        'roadmap.ruler.week': 'Week {week}',
        'roadmap.ruler.weekShort': 'W{week}',
        'roadmap.ruler.quarter': 'Q{quarter} {year}',
        'roadmap.ruler.weekdays': 'S,M,T,W,T,F,S',
        'roadmap.search.placeholder': 'Search roadmap',
        'roadmap.search.noMatches': 'No matches for "{query}"',
        'roadmap.search.milestone': 'Milestone',
        'roadmap.search.item': 'Item',
        'roadmap.count.lanes.one': '{count} lane',
        'roadmap.count.lanes.other': '{count} lanes',
        'roadmap.count.items.one': '{count} item',
        'roadmap.count.items.other': '{count} items',
        'roadmap.count.milestones.one': '{count} milestone',
        'roadmap.count.milestones.other': '{count} milestones',
        'roadmap.toolbar.height': 'Macro height',
        'roadmap.toolbar.height.compact': 'Compact height',
        'roadmap.toolbar.height.comfortable': 'Comfortable height',
        'roadmap.toolbar.height.tall': 'Tall height',
        'roadmap.toolbar.editHistory': 'Edit history',
        'roadmap.toolbar.undo': 'Undo',
        'roadmap.toolbar.undoTitle': 'Undo (Ctrl/Cmd+Z)',
        'roadmap.toolbar.redo': 'Redo',
        'roadmap.toolbar.redoTitle': 'Redo (Ctrl/Cmd+Shift+Z)',
        'roadmap.toolbar.export': 'Export as PNG image',
        'roadmap.toolbar.today': 'Today',
        'roadmap.toolbar.scale': 'Timeline scale',
        'roadmap.toolbar.add': 'Add',
        'roadmap.toolbar.panel': 'Feedback, what’s new & about',
        'roadmap.toast.dismiss': 'Dismiss notification',
        'roadmap.empty.eyebrow': 'YOUR ROADMAP STARTS HERE',
        'roadmap.empty.title': 'Create your first workstream',
        'roadmap.empty.body': 'Add a lane for a team, theme, or delivery stream. Then double-click its timeline to create work, or double-click the milestone band above.',
        'roadmap.empty.createLane': 'Create first lane',
        'roadmap.empty.setTitle': 'Set title & dates',
        'roadmap.sidebar.workstreams': 'WORKSTREAMS',
        'roadmap.sidebar.addLane': 'Add lane',
        'roadmap.sidebar.collapseAll': 'Collapse all lanes',
        'roadmap.sidebar.expandAll': 'Expand all lanes',
        'roadmap.sidebar.widen': 'Widen workstream panel',
        'roadmap.sidebar.narrow': 'Narrow workstream panel',
        'roadmap.sidebar.resize': 'Resize workstream panel',
        'roadmap.sidebar.dragResize': 'Drag to resize workstream panel',
        'roadmap.sidebar.milestones': 'Milestones',
        'roadmap.sidebar.dragToMove': 'Drag to move',
        'roadmap.sidebar.toggleLane': 'Toggle {title}',
        'roadmap.sidebar.hideLane': 'Hide {title}',
        'roadmap.sidebar.showLane': 'Show {title}',
        'roadmap.sidebar.editLane': 'Edit {title}',
        'roadmap.sidebar.hidden': 'HIDDEN',
        'roadmap.span.weeks.one': '{count} wk',
        'roadmap.span.weeks.other': '{count} wks',
        'roadmap.span.days.one': '{count} day',
        'roadmap.span.days.other': '{count} days',
        'roadmap.canvas.label': 'Editable roadmap',
        'roadmap.canvas.itemLabel': '{title}, {start} to {end}',
        'roadmap.canvas.dashed': 'dashed',
        'roadmap.canvas.milestoneLabel': '{title}, {date}, row {row}',
        'roadmap.export.title': 'Export as image',
        'roadmap.export.subtitle.weeks': 'A PNG of the weeks timeline',
        'roadmap.export.subtitle.months': 'A PNG of the months timeline',
        'roadmap.export.subtitle.quarters': 'A PNG of the quarters timeline',
        'roadmap.export.range': 'Date range',
        'roadmap.export.rangeFrom': 'From',
        'roadmap.export.rangeTo': 'To',
        'roadmap.export.rangeAll': 'Whole timeline',
        'roadmap.export.resolution': 'Resolution',
        'roadmap.export.standard': 'Standard',
        'roadmap.export.standardUse': 'Chat & email',
        'roadmap.export.high': 'High',
        'roadmap.export.highUse': 'Slides & docs',
        'roadmap.export.maximum': 'Maximum',
        'roadmap.export.maximumUse': 'Print & big screens',
        'roadmap.export.recommended': 'Recommended',
        'roadmap.export.reducedTitle': 'Reduced to fit browser limits',
        'roadmap.export.whatsInImage': 'What’s in the image',
        'roadmap.export.tipWhole': 'The date range you pick (the whole timeline by default), not only the part you can see on screen.',
        'roadmap.export.tipLight': 'Always in light colours, even if you use dark mode.',
        'roadmap.export.tipHidden': 'Hidden lanes are not included.',
        'roadmap.export.tipCollapsed': 'Collapsed lanes stay collapsed. Expand them first to show their items.',
        'roadmap.export.copy': 'Copy',
        'roadmap.export.download': 'Download PNG',
        'roadmap.export.rendering': 'Rendering…',
        'roadmap.export.view': '{scale} view',
        'roadmap.export.exportedOn': 'Exported {date}',
        'roadmap.export.copied': 'Copied a {width} × {height} px PNG',
        'roadmap.export.exported': 'Exported a {width} × {height} px PNG',
        'roadmap.export.reduced': '(reduced to fit browser limits)',
        'roadmap.export.copyNotAllowed': 'Copying images isn’t allowed here. Use Download PNG instead.',
        'roadmap.export.errorDraw': 'The roadmap image could not be drawn.',
        'roadmap.export.errorTooLarge': 'This roadmap is too large to export as an image.',
        'roadmap.export.errorGeneric': 'The image could not be exported.',
        'roadmap.color.violet': 'Violet',
        'roadmap.color.blue': 'Blue',
        'roadmap.color.teal': 'Teal',
        'roadmap.color.amber': 'Amber',
        'roadmap.color.coral': 'Coral',
        'roadmap.color.pink': 'Pink',
        'roadmap.color.slate': 'Slate',
        'roadmap.dialog.select': 'Select…',
        'roadmap.dialog.closeMenu': 'Close menu',
        'roadmap.dialog.close': 'Close',
        'roadmap.dialog.delete': 'Delete',
        'roadmap.dialog.cancel': 'Cancel',
        'roadmap.dialog.confirm': 'Confirm',
        'roadmap.dialog.saveChanges': 'Save changes',
        'roadmap.dialog.title': 'Title',
        'roadmap.dialog.color': 'Color',
        'roadmap.dialog.style': 'Style',
        'roadmap.dialog.barStyle': 'Bar style',
        'roadmap.dialog.solid': 'Solid',
        'roadmap.dialog.dashed': 'Dashed',
        'roadmap.dialog.dashedTitle': 'Dashed — e.g. tentative or unconfirmed work',
        'roadmap.lock.restricted': 'Restricted',
        'roadmap.lock.locked': 'Locked',
        'roadmap.lock.chipOwnerTitle': 'This roadmap is restricted — you can edit it',
        'roadmap.lock.chipTitle': 'Editing is restricted to {creator}',
        'roadmap.lock.theCreator': 'the creator',
        'roadmap.lock.theRoadmapCreator': 'the roadmap creator',
        'roadmap.lock.editingRestricted': 'Editing restricted',
        'roadmap.lock.onlyCreatorCanChange': 'Only {creator} can change this roadmap.',
        'roadmap.lock.you': 'you',
        'roadmap.lock.onlyWho': 'Only {who} can edit this roadmap.',
        'roadmap.lock.onlyWhoAndOwner': 'Only {who} and the app owner can edit this roadmap.',
        'roadmap.lock.anyone': 'Anyone with page access can edit this roadmap.',
        'roadmap.lock.restrictEditing': 'Restrict editing',
        'roadmap.lock.restrict': 'Restrict',
        'roadmap.lock.unrestrict': 'Unrestrict',
        'roadmap.lock.denied': 'This roadmap is locked. Only its creator can edit it.',
        'roadmap.add.eyebrow': 'CREATE',
        'roadmap.add.title': 'What do you want to add?',
        'roadmap.add.item': 'Item',
        'roadmap.add.itemHint': 'A bar of work inside a lane.',
        'roadmap.add.itemNeedsLane': 'Create a lane first — items live inside lanes.',
        'roadmap.add.lane': 'Lane',
        'roadmap.add.laneHint': 'A workstream row: a team, theme, or delivery stream.',
        'roadmap.add.milestone': 'Milestone',
        'roadmap.add.milestoneHint': 'A dated diamond in the milestone band.',
        'roadmap.add.tip': 'Tip: double-click a lane or the milestone band to add something at that date.',
        'roadmap.item.create': 'Create an item',
        'roadmap.item.details': 'Item details',
        'roadmap.item.createButton': 'Create item',
        'roadmap.item.lane': 'Lane',
        'roadmap.item.start': 'Start',
        'roadmap.item.end': 'End',
        'roadmap.item.notes': 'Notes',
        'roadmap.group.extendedByChildren': 'The group\'s own dates are saved. Its bar still spans {start} – {end} to include its subitems.',
        'roadmap.group.name': 'Grupo',
        'roadmap.group.delete': 'Delete this group?',
        'roadmap.group.deleteWithChildren': 'Delete this group and its {count} subitems?',
        'roadmap.group.subitems.one': '{count} subitem',
        'roadmap.group.subitems.other': '{count} subitems',
        'roadmap.group.addSubitem': 'Add subitem',
        'roadmap.group.convertToItem': 'Convert to item',
        'roadmap.group.convertToGroup': 'Convert to group',
        'roadmap.group.noGroup': 'No group',
        'roadmap.group.help': 'Keeps its own dates. Subitems may extend its visual range, but never alter its planned dates.',
        'roadmap.item.status': 'Status',
        'roadmap.item.statusFromTask': 'from Nautilus task',
        'roadmap.item.statusFromChildren': 'from its subitems',
        'roadmap.item.titleAria': 'Title',
        'roadmap.item.colorMode': 'Item colours',
        'roadmap.item.barStyleAria': 'Bar style',
        'roadmap.item.exportInvalidRange': 'Choose valid From and To dates within the timeline.',
        'roadmap.item.invalidDate': 'Enter a complete date.',
        'roadmap.item.taskSyncAfterLoad': 'Date imported from task',
        'roadmap.board.autoSyncTitle': 'Linked tasks',
        'roadmap.board.autoSyncLabel': 'Automatically sync titles and dates',
        'roadmap.board.autoSyncHelp': 'Statuses always update. When this is off, each differing bar shows a warning so you can review changes individually.',
        'roadmap.board.autoSyncConfirm': 'Titles and dates for {count} linked items will be replaced with Nautilus values. Future changes will synchronize automatically.',
        'roadmap.board.autoSyncConfirmButton': 'Enable automatic sync',
        'roadmap.board.source': 'Shared roadmap from {title}. Changes apply to every page using this roadmap. Refresh to see changes made elsewhere.',
        'roadmap.toolbar.sync': 'Refresh linked task data',
        'roadmap.toolbar.syncing': 'Refreshing linked tasks…',
        'roadmap.toolbar.syncDone': 'Linked tasks refreshed',
        'roadmap.toolbar.syncError': 'Could not refresh linked tasks',
        'roadmap.toolbar.tallHelp': 'Tall: shows the entire roadmap',
        'roadmap.hover.due': 'Due {date}',
        'roadmap.shift.title': 'Shift dates',
        'roadmap.shift.buttonTitle': 'Shift dates: move several items by a number of days',
        'roadmap.shift.selected': '{count} selected',
        'roadmap.shift.nothing': 'Nothing selected',
        'roadmap.shift.dayLess': 'One day less',
        'roadmap.shift.dayMore': 'One day more',
        'roadmap.shift.daysInput': 'Days to move',
        'roadmap.shift.days': 'days',
        'roadmap.shift.selectAll': 'Select all',
        'roadmap.shift.clear': 'Clear',
        'roadmap.shift.move.one': 'Move {count} item',
        'roadmap.shift.move.other': 'Move {count} items',
        'roadmap.shift.moveItems': 'Move items',
        'roadmap.shift.invalid': 'Enter a whole number of days up to {max}. Use a minus sign to move earlier.',
        'roadmap.shift.hint': 'Click items to select them, or a lane name to select its items, then drag one of them or enter the days. A group moves with its subitems.',
        'roadmap.shift.noteLater.one': '{count} item will move {days} later.',
        'roadmap.shift.noteLater.other': '{count} items will move {days} later.',
        'roadmap.shift.noteEarlier.one': '{count} item will move {days} earlier.',
        'roadmap.shift.noteEarlier.other': '{count} items will move {days} earlier.',
        'roadmap.shift.outside.one': '{count} will reach past the timeline; widen it in Roadmap settings to see it.',
        'roadmap.shift.outside.other': '{count} will reach past the timeline; widen it in Roadmap settings to see them.',
        'roadmap.shift.tasksUnchanged': 'Linked task dates are not changed.',
        'roadmap.shift.autoSynced.one': '{count} linked item follows its task’s dates and will not move.',
        'roadmap.shift.autoSynced.other': '{count} linked items follow their tasks’ dates and will not move.',
        'roadmap.shift.dragging': 'Dragging: {days}',
        'roadmap.shift.noChange': 'No change',
        'roadmap.shift.history': 'Dates shifted {days}: {items}',
        'roadmap.shift.toastLater.one': 'Moved {count} item {days} later',
        'roadmap.shift.toastLater.other': 'Moved {count} items {days} later',
        'roadmap.shift.toastEarlier.one': 'Moved {count} item {days} earlier',
        'roadmap.shift.toastEarlier.other': 'Moved {count} items {days} earlier',
        'roadmap.sidebar.collapseGroups': 'Collapse all groups',
        'roadmap.sidebar.expandGroups': 'Expand all groups',
        'roadmap.sidebar.hideMilestones': 'Hide milestones',
        'roadmap.sidebar.showMilestones': 'Show milestones',
        'roadmap.sidebar.groupCount.one': '{count} subitem',
        'roadmap.sidebar.groupCount.other': '{count} subitems',
        'roadmap.picker.outside': 'Outside the timeline',
        'roadmap.picker.partly': 'Partly outside the timeline',
        'roadmap.picker.notImported': 'Not imported',
        'roadmap.picker.selectAll': 'Select all',
        'roadmap.picker.selectNone': 'Select none',
        'roadmap.picker.selected': '{count} of {total} selected',
        'roadmap.picker.import': 'Import',
        'roadmap.picker.importSelected': 'Import {count}',
        'roadmap.picker.selectedOutside.one': '{count} selected item is entirely outside the timeline and won\'t appear',
        'roadmap.picker.selectedOutside.other': '{count} selected items are entirely outside the timeline and won\'t appear',
        'roadmap.picker.selectedPartly.one': '{count} selected item is partially outside the timeline',
        'roadmap.picker.selectedPartly.other': '{count} selected items are partially outside the timeline',
        'roadmap.picker.changeRange': 'The timeline runs from {start} to {end}. Change the range in Roadmap settings to see them.',
        'roadmap.dialog.openCalendar': 'Open {label} calendar',
        'roadmap.dialog.removeScreenshot': 'Remove screenshot',
        'roadmap.dialog.closeDialog': 'Close',
        'roadmap.dialog.confirmDelete': 'Delete',
        'roadmap.export.selectedSubtitle': 'A PNG of your selected date range',
        'roadmap.export.resetDates': 'Reset From and To to the whole timeline',
        'roadmap.lane.eyebrow': 'WORKSTREAM',
        'roadmap.lane.create': 'Create a lane',
        'roadmap.lane.settings': 'Lane settings',
        'roadmap.lane.createButton': 'Create lane',
        'roadmap.lane.name': 'Lane name',
        'roadmap.lane.accentColor': 'Accent color',
        'roadmap.lane.position': 'Position',
        'roadmap.lane.moveUp': 'Move up',
        'roadmap.lane.moveDown': 'Move down',
        'roadmap.milestone.eyebrow': 'MILESTONE',
        'roadmap.milestone.create': 'Create a milestone',
        'roadmap.milestone.details': 'Milestone details',
        'roadmap.milestone.createButton': 'Create milestone',
        'roadmap.milestone.date': 'Date',
        'roadmap.milestone.row': 'Row',
        'roadmap.milestone.firstRow': 'First row',
        'roadmap.milestone.secondRow': 'Second row',
        'roadmap.board.eyebrow': 'ROADMAP',
        'roadmap.board.settings': 'Roadmap settings',
        'roadmap.board.title': 'Roadmap title',
        'roadmap.board.starts': 'Timeline starts',
        'roadmap.board.ends': 'Timeline ends',
        'roadmap.board.untitled': 'Untitled roadmap',
        'roadmap.board.help': 'Set the calendar range here. Switching between weeks, months, and quarters keeps this range and your item dates unchanged.',
        'roadmap.confirm.deleteItem': 'Delete this roadmap item?',
        'roadmap.confirm.deleteLane': 'Delete this lane?',
        'roadmap.confirm.deleteLaneWithItems.one': 'Delete this lane and its {count} item?',
        'roadmap.confirm.deleteLaneWithItems.other': 'Delete this lane and its {count} items?',
        'roadmap.confirm.deleteMilestone': 'Delete this milestone?',
        'roadmap.confirm.deleteFeedback': 'Delete this feedback?',
        'roadmap.change.undid': 'Undid: {message}',
        'roadmap.change.redid': 'Redid: {message}',
        'roadmap.change.untitled': 'Untitled',
        'roadmap.change.laneCreated': 'Lane created: {title}',
        'roadmap.change.laneUpdated': 'Lane updated: {title}',
        'roadmap.change.laneDeleted': 'Lane deleted: {title}',
        'roadmap.change.laneReordered': 'Lane reordered: {title}',
        'roadmap.change.laneExpanded': 'Lane expanded: {title}',
        'roadmap.change.laneCollapsed': 'Lane collapsed: {title}',
        'roadmap.change.laneShown': 'Lane shown: {title}',
        'roadmap.change.laneHidden': 'Lane hidden: {title}',
        'roadmap.change.allCollapsed': 'All lanes collapsed',
        'roadmap.change.allExpanded': 'All lanes expanded',
        'roadmap.change.itemCreated': 'Item created: {title}',
        'roadmap.change.itemUpdated': 'Item updated: {title}',
        'roadmap.change.itemDeleted': 'Item deleted: {title}',
        'roadmap.change.itemMoved': 'Item moved: {title}',
        'roadmap.change.itemDates': 'Item dates changed: {title}',
        'roadmap.change.milestoneCreated': 'Milestone created: {title}',
        'roadmap.change.milestoneUpdated': 'Milestone updated: {title}',
        'roadmap.change.milestoneDeleted': 'Milestone deleted: {title}',
        'roadmap.change.milestoneMoved': 'Milestone moved: {title}',
        'roadmap.change.restricted': 'Editing restricted',
        'roadmap.change.unrestricted': 'Editing unrestricted',
        'roadmap.change.height': 'Macro height changed',
        'roadmap.change.panelResized': 'Workstream panel resized',
        'roadmap.change.scale': 'Timeline scale: {scale}',
        'roadmap.change.settings': 'Roadmap settings updated',
        'roadmap.feedback.kindBug': 'Something’s broken',
        'roadmap.feedback.kindIdea': 'Idea / improvement',
        'roadmap.feedback.sent': 'Thank you — it’s been sent',
        'roadmap.feedback.back': 'Back to feedback',
        'roadmap.feedback.lead': 'Add a bug report or improvement, then check the shared list below to avoid duplicates.',
        'roadmap.feedback.type': 'Type of feedback',
        'roadmap.feedback.message': 'Your message',
        'roadmap.feedback.placeholder': 'What happened, or what would you like to see?',
        'roadmap.feedback.attach': 'Attach screenshot',
        'roadmap.feedback.attached': 'Attached screenshot',
        'roadmap.feedback.removeImage': 'Remove screenshot',
        'roadmap.feedback.hint': 'Paste an image to attach it · Ctrl/Cmd+Enter sends',
        'roadmap.feedback.send': 'Send feedback',
        'roadmap.feedback.sending': 'Sending…',
        'roadmap.feedback.notImage': 'That file is not an image.',
        'roadmap.feedback.unreadable': 'That image could not be read.',
        'roadmap.feedback.tooLarge': 'That screenshot is too large — crop it and try again.',
        'roadmap.feedback.attachFailed': 'That image could not be attached.',
        'roadmap.feedback.viewImage': 'View screenshot',
        'roadmap.feedback.markDone': 'Mark as done',
        'roadmap.feedback.reopen': 'Reopen',
        'roadmap.feedback.delete': 'Delete feedback',
        'roadmap.feedback.someone': 'Someone',
        'roadmap.feedback.doneBy': 'Done by {who}',
        'roadmap.feedback.shared': 'Shared feedback',
        'roadmap.feedback.sharedNote': 'Reports and ideas from all Program Roadmap pages.',
        'roadmap.feedback.loading': 'Loading…',
        'roadmap.feedback.recent': '{count} recent',
        'roadmap.feedback.open': 'Open',
        'roadmap.feedback.done': 'Done',
        'roadmap.feedback.search': 'Search reports and ideas',
        'roadmap.feedback.showingFirst': 'Showing the first {count} matching entries.',
        'roadmap.feedback.noMatches': 'No matching feedback.',
        'roadmap.feedback.noneDone': 'Nothing marked done yet.',
        'roadmap.feedback.noneOpen': 'No open feedback.',
        'roadmap.feedback.imageLoadFailed': 'Screenshot could not be loaded',
        'roadmap.feedback.imageGone': 'That screenshot is no longer available.',
        'roadmap.feedback.deleteFailed': 'Feedback could not be deleted',
        'roadmap.feedback.updateFailed': 'Feedback could not be updated',
        'roadmap.feedback.sendFailed': 'Feedback could not be sent',
        'roadmap.feedback.ownOnly': 'You can only delete your own feedback.',
        'roadmap.feedback.emptyMessage': 'Write a short note before sending.',
        'roadmap.feedback.unavailable': 'This action is not available here.',
        'roadmap.whatsNew.current': 'Current',
        'roadmap.history.empty': 'No changes recorded yet. Edits to lanes, items, milestones, and settings will show up here.',
        'roadmap.history.lead': 'The last {count} changes to this roadmap.',
        'roadmap.about.tagline': 'Program planning inside Nautilus Docs',
        'roadmap.about.description': 'Plan work across teams, dates, and milestones in a roadmap that lives inside your documentation.',
        'roadmap.about.details': 'Product details',
        'roadmap.about.owner': 'Owner',
        'roadmap.about.version': 'Version',
        'roadmap.about.support': 'Support',
        'roadmap.about.openFeedback': 'Open feedback',
        'roadmap.panel.title': 'Feedback & about',
        'roadmap.panel.feedback': 'Feedback',
        'roadmap.panel.whatsNew': 'What’s new',
        'roadmap.panel.history': 'History',
        'roadmap.panel.about': 'About',
        'roadmap.loading.title': 'Shaping your roadmap',
        'roadmap.loading.body': 'Preparing lanes, dates, and milestones…',
        'roadmap.error.openTitle': 'We couldn’t open this roadmap',
        'roadmap.error.tryAgain': 'Try again',
        'roadmap.error.attention': 'Roadmap needs attention.',
        'roadmap.error.reload': 'Reload',
        'roadmap.error.load': 'The roadmap could not be loaded.',
        'roadmap.error.save': 'The roadmap could not be saved.',
        'roadmap.error.conflict': 'This roadmap changed elsewhere. Reload before making more edits.',
        'roadmap.error.locked': 'This roadmap is locked. Only its creator can edit it.',
        'roadmap.error.notPersisted': 'Not saved to the server yet. Your changes are kept in this browser and saving is retried automatically.',
        'roadmap.error.readOnly': 'This roadmap is read-only here.',
        'roadmap.foot.tip': '<strong>Tip:</strong> Wheel over the timeline to move left or right; wheel over workstreams to move up or down.',
        'roadmap.foot.viewHistory': 'View change history',
        'roadmap.foot.edited': 'Edited {when}',
        'roadmap.foot.editedBy': 'Edited {when} by {who}',
        'roadmap.save.saving': 'Saving',
        'roadmap.save.error': 'Not saved',
        'roadmap.save.saved': 'Saved',
        'roadmap.refreshing': 'Refreshing…',
        'roadmap.task.field': 'Task',
        'roadmap.task.searchPlaceholder': 'Search your tasks',
        'roadmap.task.noResults': 'No matching tasks.',
        'roadmap.task.notFound': 'This task no longer exists.',
        'roadmap.task.noProject': 'No project',
        'roadmap.task.noDates': 'no dates',
        'roadmap.task.dates': 'Dates',
        'roadmap.task.open': 'Open task',
        'roadmap.task.unlink': 'Unlink',
        'roadmap.task.badgeTitle': '{title} · {status}',
        'roadmap.task.syncFailed': 'The task dates could not be updated.',
        'roadmap.task.pushDone': 'Task dates updated.',
        'roadmap.task.pushOne': 'Send to task',
        'roadmap.task.pullOne': 'Get from task',
        'roadmap.task.undo': 'Undo',
        'roadmap.task.hasDate': 'Task: {date}',
        'roadmap.task.noDate': 'no date',
        'roadmap.task.clearStart': 'Clear start',
        'roadmap.date.placeholder': datePlaceholder('en'),
        'roadmap.date.today': 'Today',
        'roadmap.task.stale': 'The task\'s dates changed. Refresh the roadmap to see them.',
        'roadmap.task.refresh': 'Refresh',
        'roadmap.task.linkPlaceholder': 'Link to a task (optional)',
        'roadmap.task.titleDiffers': 'The item title differs from the task.',
        'roadmap.task.useTitle': 'Use the task title',
        'roadmap.group.subitemConflict.before.one': 'A subitem starts before the group',
        'roadmap.group.subitemConflict.before.other': '{count} subitems start before the group',
        'roadmap.group.subitemConflict.after.one': 'A subitem ends after the group',
        'roadmap.group.subitemConflict.after.other': '{count} subitems end after the group',
        'roadmap.group.subitemConflict.range.one': 'A subitem falls outside the group dates',
        'roadmap.group.subitemConflict.range.other': '{count} subitems fall outside the group dates',
        'roadmap.group.conflictSummary.before.one': 'subitem “{title}” extends the bar back to {date}.',
        'roadmap.group.conflictSummary.before.other': 'subitem “{title}” extends the bar back to {date}.',
        'roadmap.group.conflictSummary.after.one': 'subitem “{title}” extends the bar to {date}.',
        'roadmap.group.conflictSummary.after.other': 'subitem “{title}” extends the bar to {date}.',
        'roadmap.group.conflictSummary.range': 'subitems extend the bar to Start: {start} → End: {end}.',
        'roadmap.task.pushBlocked': 'These dates cannot be sent to the linked task while this conflict exists.',
        'roadmap.item.titlePlaceholder': 'Item title',
        'roadmap.item.addNote': 'Add note',
        'roadmap.item.days.one': '{count} day',
        'roadmap.item.days.other': '{count} days',
        'roadmap.lane.project': 'Project',
        'roadmap.lane.noProject': 'No project',
        'roadmap.lane.importCount.one': '{count} task with dates to import.',
        'roadmap.lane.importCount.other': '{count} tasks with dates to import.',
        'roadmap.lane.importNone': 'All dated tasks of this project are already on the roadmap.',
        'roadmap.lane.importOnCreate': 'They are added when you create the lane.',
        'roadmap.lane.undated.one': '{count} task without dates is not included',
        'roadmap.lane.undated.other': '{count} tasks without dates are not included',
        'roadmap.lane.importButton.one': 'Import {count} task',
        'roadmap.lane.importButton.other': 'Import {count} tasks',
        'roadmap.lane.import': 'Import',
        'roadmap.confirm.importTasks.one': 'Import {count} task from {project} into this lane?',
        'roadmap.confirm.importTasks.other': 'Import {count} tasks from {project} into this lane?',
        'roadmap.change.tasksImported.one': '{count} task imported: {title}',
        'roadmap.change.tasksImported.other': '{count} tasks imported: {title}',
        'roadmap.change.laneImported.one': 'Lane created with {count} task: {title}',
        'roadmap.change.laneImported.other': 'Lane created with {count} tasks: {title}',
        'roadmap.change.synced.one': '{count} change synced from tasks',
        'roadmap.change.synced.other': '{count} changes synced from tasks',
        'roadmap.sync.button': 'Sync with tasks and projects',
        'roadmap.sync.eyebrow': 'SYNC',
        'roadmap.sync.title': 'Sync with tasks and projects',
        'roadmap.sync.lead': 'Changes in your linked tasks and projects. Only the selected ones are applied; the rest of the roadmap stays as it is.',
        'roadmap.sync.kindTitle': 'Title',
        'roadmap.sync.kindDates': 'Dates',
        'roadmap.sync.kindNew': 'New task',
        'roadmap.sync.kindMissing': 'Task deleted',
        'roadmap.sync.unlinkNote': 'the item stays, without the link',
        'roadmap.sync.apply.one': 'Apply {count} change',
        'roadmap.sync.apply.other': 'Apply {count} changes',
        'roadmap.sync.upToDate': 'The roadmap is up to date with your tasks and projects.',
        'docs.toolbar.callout': 'Callout',
        'docs.toolbar.strike': 'Strikethrough',
        'docs.toolbar.inlineCode': 'Inline code',
        'docs.mobile.insertTitle': 'Insert',
        'docs.mobile.moreTitle': 'More',
        'docs.mobile.historyLabel': 'History',
        'docs.mobile.formatLabel': 'Format',
        'docs.mobile.linkTitle': 'Add link',
        'docs.mobile.linkPlaceholder': 'https://',
        'docs.mobile.linkApply': 'Apply',
        'docs.mobile.linkCancel': 'Cancel',
        'docs.table.numberColumn': 'Numbered rows',
        'docs.table.distributeEvenly': 'Distribute columns evenly',
        'docs.table.options': 'Options',
        'docs.table.rows': 'Rows',
        'docs.table.columns': 'Columns',
        'docs.table.actions': 'Table actions',
        'docs.table.headerRow': 'Header row',
        'docs.table.headerColumn': 'Header column',
        'docs.table.mobileColumnLabel': 'Column {number}',
        'docs.table.addRowAbove': 'Add row above',
        'docs.table.addRowBelow': 'Add row below',
        'docs.table.deleteRow': 'Delete row',
        'docs.table.addColLeft': 'Add column left',
        'docs.table.addColRight': 'Add column right',
        'docs.table.deleteColumn': 'Delete column',
        'docs.table.copy': 'Copy table',
        'docs.table.copied': 'Table copied to clipboard',
        'docs.table.delete': 'Delete table',
        'docs.videoModalTitle': 'Insert YouTube video',
        'docs.videoModalHint': 'Paste a YouTube link to embed it in the page.',
        'docs.videoUrlPlaceholder': 'https://www.youtube.com/watch?v=...',
        'docs.videoInsert': 'Insert video',
        'docs.videoRequired': 'Paste a YouTube URL first.',
        'docs.videoInvalid': 'Only YouTube links can be embedded in docs.',
        'docs.statusModalTitle': 'Insert status chip',
        'docs.statusModalHint': 'Add a short status label and choose a color.',
        'docs.statusLabelPlaceholder': 'STATUS',
        'docs.statusDefault': 'STATUS',
        'docs.statusInsert': 'Insert status',
        'docs.templates.pickerTitle': 'Start from a template',
        'docs.templates.blank': 'Blank page',
        'docs.templates.blankDesc': 'Start with an empty document',
        'docs.templates.builtIn': 'Built-in',
        'docs.templates.myTemplates': 'My Templates',
        'docs.templates.searchPlaceholder': 'Search templates...',
        'docs.templates.saveAsTemplate': 'Save as Template',
        'docs.templates.saveDialogTitle': 'Save as Template',
        'docs.templates.templateName': 'Template name',
        'docs.templates.templateIcon': 'Icon',
        'docs.templates.templateDesc': 'Description (optional)',
        'docs.templates.saveBtn': 'Save Template',
        'docs.templates.savedToast': 'Template saved',
        'docs.templates.deleteTemplate': 'Delete template',
        'docs.templates.deleteConfirmTitle': 'Delete template {name}?',
        'docs.templates.deleteConfirmWarning': 'This action cannot be undone.',
        'docs.templates.deleteConfirmBtn': 'Delete',
        'docs.templates.noUserTemplates': 'No saved templates yet',
        'docs.templates.dismiss': 'Dismiss',
        'docs.templates.bannerTitle': 'Start from a template',
        'docs.templates.bannerSubtitle': 'Pick a layout or use one of your saved templates to jump-start this page.',
        'docs.templates.browseBtn': 'Browse templates',
        'projects.details.startDate': 'Start Date',
        'projects.details.endDate': 'End Date',
        'projects.details.duration': 'Duration',
        'projects.details.timeline': 'Timeline',
        'projects.details.durationDays': '{count} days',
        'projects.details.remainingDaysLeft': '{count} days left',
        'projects.details.remainingDueToday': 'Due today',
        'projects.details.remainingOverdueDays': '{count} days overdue',
        'projects.details.remainingCompleted': 'Completed',
        'projects.details.finishedOnTime': 'Finished on time',
        'projects.details.finishedLateDays': 'Finished {count} days late',
        'projects.details.finishedEarlyDays': 'Finished {count} days early',
        'projects.details.created': 'Created',
        'projects.details.calendarColor': 'Calendar Color',
        'projects.details.color': 'Color',
        'projects.details.customColor': 'Custom color',
        'projects.details.progressOverview': 'Progress Overview',
        'projects.details.taskProgress': 'Task Progress',
        'projects.details.tasksTitle': 'Tasks ({count})',
        'projects.details.descriptionTitle': 'Description',
        'projects.details.tasksDoneSummary': '{done} of {total} done',
        'projects.details.overdueTasks': 'Overdue tasks: {count}',
        'projects.details.viewOverdueTasks': 'See the overdue tasks',
        'projects.details.moreActions': 'More actions',
        'projects.details.changeHistory': 'Change History',
        'projects.details.noChanges': 'No changes yet for this project',
        'projects.details.noTasksFound': 'No tasks found',
        'projects.details.viewTodo': 'View To Do tasks for this project',
        'projects.details.viewProgress': 'View In Progress tasks for this project',
        'projects.details.viewReview': 'View In Review tasks for this project',
        'projects.details.viewCompleted': 'View Completed tasks for this project',
        'projects.details.viewBacklog': 'View backlog tasks in List view',
        'projects.details.viewInList': 'View all tasks in List view filtered by this project',
        'projects.details.viewInListBtn': 'View in List',
        'projects.details.resetSort': 'Auto-sort',
        'projects.details.groupBy.label': 'Group by',
        'projects.details.groupBy.none': 'None',
        'projects.details.groupBy.status': 'Status',
        'projects.details.groupBy.priority': 'Priority',
        'projects.details.resetSortTitle': 'Reset to automatic sort by priority',
        'projects.details.editDescription': 'Edit',
        'projects.details.doneEditing': 'Done',
        'projects.details.clickToEdit': 'Click to edit',
        'projects.details.descriptionPlaceholder': 'Add a description…',
        'projects.details.segmentTip': '{count} of {total} tasks',
        'projects.details.segmentHint': 'Click to see these tasks',
        'projects.details.segmentHintTouch': 'Tap to see these tasks',
        'projects.untitled': 'Untitled Project',
        'tasks.title': 'All Tasks',
        'tasks.subtitle': 'Manage tasks across all projects',
        'tasks.searchPlaceholder': '\u{1F50D} Search tasks...',
        'tasks.filters.status': 'Status',
        'tasks.filters.statusTitle': 'Status',
        'tasks.status.backlog': 'Backlog',
        'tasks.status.todo': 'To Do',
        'tasks.status.progress': 'In Progress',
        'tasks.status.review': 'In Review',
        'tasks.status.done': 'Done',
        'tasks.filters.tags': 'Tags',
        'tasks.filters.tagsTitle': 'Tags',
        'tasks.filters.searchTags': 'Search tags...',
        'tasks.filters.priority': 'Priority',
        'tasks.filters.priorityTitle': 'Priority',
        'tasks.priority.high': 'High',
        'tasks.priority.medium': 'Medium',
        'tasks.priority.low': 'Low',
        'tasks.filters.project': 'Project',
        'tasks.filters.projectTitle': 'Project',
        'tasks.filters.searchProjects': 'Search projects...',
        'tasks.filters.selectAll': 'Select / Unselect All',
        'tasks.filters.date': 'Date',
        'tasks.filters.endDate': 'End Date',
        'tasks.filters.startDate': 'Start Date',
        'tasks.filters.dateTitle': 'Quick Date Filters',
        'tasks.filters.endDateTitle': 'End Date Filters',
        'tasks.filters.noDate': 'No End Date',
        'tasks.filters.hasDate': 'Has End Date',
        'tasks.filters.hasStartDate': 'Has Start Date',
        'tasks.filters.overdue': 'Overdue',
        'tasks.filters.endToday': 'End Date Today',
        'tasks.filters.endTomorrow': 'End Date Tomorrow',
        'tasks.filters.end7Days': 'End Date in 7 Days',
        'tasks.filters.endThisWeek': 'End Date This Week',
        'tasks.filters.endThisMonth': 'End Date This Month',
        'tasks.filters.startDateTitle': 'Start Date Filters',
        'tasks.filters.noStartDate': 'No Start Date',
        'tasks.filters.alreadyStarted': 'Already Started',
        'tasks.filters.startToday': 'Start Date Today',
        'tasks.filters.startTomorrow': 'Start Date Tomorrow',
        'tasks.filters.start7Days': 'Start Date in 7 Days',
        'tasks.filters.startThisWeek': 'Start Date This Week',
        'tasks.filters.startThisMonth': 'Start Date This Month',
        'tasks.filters.updated': 'Updated',
        'tasks.filters.updatedTitle': 'Recently Updated',
        'tasks.filters.links': 'Links',
        'tasks.filters.linksTitle': 'Filter by link types',
        'tasks.filters.blocksOthers': 'Blocks others',
        'tasks.filters.isBlockedByOthers': 'Is Blocked by others',
        'tasks.filters.relatesTo': 'Relates to',
        'tasks.filters.hasWebLinks': 'Has Web Links',
        'tasks.filters.noLinks': 'No links',
        'tasks.filters.dateFrom': 'Start date',
        'tasks.filters.dateTo': 'End date',
        'tasks.filters.specificEndDate': 'Specific end date',
        'tasks.filters.specificStartDate': 'Specific start date',
        'tasks.filters.dateFromLabel': 'From',
        'tasks.filters.dateToLabel': 'To',
        'tasks.filters.clear': 'Clear Filters',
        'tasks.filters.presetActive': 'Active',
        'tasks.filters.presetNotDone': 'Not Done',
        'tasks.filters.presetInProgress': 'In Progress',
        'tasks.filters.include': 'Include',
        'tasks.filters.exclude': 'Exclude',
        'tasks.filters.excluding': 'Excluding',
        'tasks.filters.tagMatchOr': 'OR',
        'tasks.filters.tagMatchAnd': 'AND',
        'filters.noOtherProjects': 'No other projects',
        'filters.noTags': 'No Tags',
        'filters.noOtherTags': 'No other tags',
        'filters.sheet.title': 'Options',
        'filters.chip.search': 'Search',
        'filters.chip.project': 'Project',
        'filters.chip.tag': 'Tag',
        'filters.chip.date': 'Date',
        'filters.chip.startDate': 'Start date',
        'filters.chip.endDate': 'End date',
        'filters.chip.updated': 'Updated',
        'filters.chip.created': 'Created',
        'filters.chip.completed': 'Completed',
        'filters.chip.emailTasks': 'Email Tasks',
        'filters.chip.generatedTasks': 'Recently generated',
        'filters.chip.reportTasks': 'From report',
        'filters.chip.overdueTasks': 'Overdue in project',
        'filters.chip.removeAria': 'Remove {label} filter',
        'filters.dateRange.from': 'From',
        'filters.dateRange.until': 'Until',
        'filters.updated.week': 'Week',
        'filters.updated.month': 'Month',
        'tasks.kanban.tipLabel': 'Tip:',
        'tasks.kanban.tipTextBefore': 'In the Kanban Board hold ',
        'tasks.kanban.tipTextAfter': ' (Cmd on Mac) and click to select multiple tasks. Shift-click selects a range, then drag to move them together',
        'tasks.view.kanban': 'Kanban',
        'tasks.view.list': 'List',
        'tasks.view.archived': 'Archived',
        'tasks.view.calendar': 'Calendar',
        'tasks.sort.label': 'Sort: Priority',
        'tasks.sort.orderByPriority': 'Order by Priority',
        'tasks.mobile.sortBy': 'Sort by',
        'tasks.mobile.sortApply': 'Apply',
        'tasks.mobile.sortName': 'Name',
        'tasks.kanban.showBacklog': 'Show Backlog',
        'tasks.kanban.showProjects': 'Show Projects',
        'tasks.kanban.showTags': 'Show Tags',
        'tasks.kanban.showNoDate': 'Show \"No Date\"',
        'tasks.addButton': '+ Add Task',
        'tasks.noProject': 'No Project',
        'tasks.projectIndicatorNone': 'No Project - ',
        'tasks.project.selectPlaceholder': 'Select a project',
        'tasks.tags.none': 'No tags',
        'tasks.checklist.toggle': 'Toggle checkbox',
        'tasks.untitled': 'Untitled task',
        'tasks.startDatePrefix': 'Start Date: ',
        'tasks.endDatePrefix': 'End Date: ',
        'tasks.noDate': 'No date',
        'tasks.noDatesSet': 'No dates set',
        'tasks.noEndDate': 'No End Date',
        'tasks.due.yesterday': 'Yesterday',
        'tasks.due.daysOverdue': '{count} days overdue',
        'tasks.due.tomorrow': 'Tomorrow',
        'tasks.openTaskDetails': 'Open task details',
        'tasks.noTasksInProject': 'No tasks in this project',
        'tasks.attachments.open': 'Open',
        'tasks.attachments.none': 'No attachments',
        'tasks.attachments.remove': 'Remove attachment',
        'tasks.attachments.removeTitle': 'Remove',
        'tasks.attachments.removeLink': 'Remove link',
        'tasks.attachments.googleDoc': 'Google Doc',
        'tasks.attachments.googleSheet': 'Google Sheet',
        'tasks.attachments.googleSlides': 'Google Slides',
        'tasks.attachments.googleDriveFile': 'Google Drive File',
        'tasks.attachments.pdf': 'PDF Document',
        'tasks.attachments.word': 'Word Document',
        'tasks.attachments.excel': 'Excel File',
        'tasks.attachments.powerpoint': 'PowerPoint',
        'tasks.empty.epic': 'No tasks yet. Create your first task for this epic.',
        'tasks.backlogQuickTitle': 'Open Backlog tasks in List view',
        'tasks.backlogQuickLabel': 'Backlog',
        'tasks.kanban.columnBacklog': 'Backlog',
        'tasks.kanban.columnTodo': 'To Do',
        'tasks.kanban.overdue': 'Overdue',
        'tasks.kanban.addTask': '+ Add task',
        'tasks.kanban.addTaskAria': 'Add task',
        'tasks.kanban.columnProgress': 'In Progress',
        'tasks.kanban.columnReview': 'In Review',
        'tasks.kanban.columnDone': 'Done',
        'tasks.table.task': 'Task',
        'tasks.table.priority': 'Priority',
        'tasks.table.status': 'Status',
        'tasks.table.tags': 'Tags',
        'tasks.table.project': 'Project',
        'tasks.table.startDate': 'Start Date',
        'tasks.table.endDate': 'End Date',
        'tasks.table.updated': 'Updated',
        'tasks.list.count': '{count} results',
        'tasks.export.button': 'Export to Excel',
        'tasks.export.buttonAria': 'Export the tasks in this list to Excel',
        'tasks.export.sheetName': 'Tasks',
        'tasks.export.column.id': 'ID',
        'tasks.export.column.created': 'Created',
        'tasks.export.column.description': 'Description',
        'tasks.export.success': 'Exported {count} tasks to Excel',
        'tasks.export.empty': 'No tasks to export with the current filters',
        'tasks.export.failed': 'Could not create the Excel file. Please try again.',
        'tasks.massEdit.title': 'Mass Edit',
        'tasks.massEdit.selected': '{count} of {total} tasks selected',
        'tasks.massEdit.selectedHidden': '{count} of {total} selected ({hidden} hidden)',
        'tasks.massEdit.status': 'Status',
        'tasks.massEdit.priority': 'Priority',
        'tasks.massEdit.dates': 'Dates',
        'tasks.massEdit.project': 'Project',
        'tasks.massEdit.tags': 'Tags',
        'tasks.massEdit.clear': 'Clear',
        'tasks.massEdit.applyChanges': 'Apply Changes',
        'tasks.massEdit.pendingChanges': 'changes pending',
        'tasks.massEdit.changeQueued': 'Change queued',
        'tasks.massEdit.apply': 'Apply',
        'tasks.massEdit.selectFirst': 'Please select a value first',
        'tasks.massEdit.cancel': 'Cancel',
        'tasks.massEdit.selectAll': 'Select all filtered',
        'tasks.massEdit.startDate': 'Start Date',
        'tasks.massEdit.endDate': 'End Date',
        'tasks.massEdit.tags.add': 'Add tags (keep existing)',
        'tasks.massEdit.tags.replace': 'Replace all tags',
        'tasks.massEdit.tags.remove': 'Remove specific tags',
        'tasks.massEdit.tags.noExisting': 'No existing tags',
        'tasks.massEdit.success': 'Updated {count} tasks',
        'tasks.massEdit.confirm.title': 'Confirm Mass Edit',
        'tasks.massEdit.confirm.summary': 'You are about to update {count} tasks:',
        'tasks.massEdit.confirm.field.status': 'Status → {value}',
        'tasks.massEdit.confirm.field.priority': 'Priority → {value}',
        'tasks.massEdit.confirm.field.startDate': 'Start Date → {value}',
        'tasks.massEdit.confirm.field.endDate': 'End Date → {value}',
        'tasks.massEdit.confirm.field.project': 'Project → {value}',
        'tasks.massEdit.confirm.field.tagsAdd': 'Add tags: {value}',
        'tasks.massEdit.confirm.field.tagsReplace': 'Replace tags with: {value}',
        'tasks.massEdit.confirm.field.tagsRemove': 'Remove tags: {value}',
        'tasks.massEdit.confirm.proceed': 'Yes, Update {count} Tasks',
        'tasks.massEdit.confirm.confirm': 'Confirm',
        'tasks.massEdit.confirm.movingFrom': 'moving from {from} to {to}',
        'tasks.massEdit.confirm.variousTo': 'moving from various to {to}',
        'tasks.massEdit.multiple': 'Multiple',
        'tasks.created.successMobile': 'Task created',
        'tasks.created.viewAction': 'View',
        'tasks.modal.editTitle': 'Edit Task',
        'tasks.modal.createTitle': 'Create New Task',
        'tasks.modal.duplicate': '📄 Duplicate Task',
        'tasks.modal.makeRecurring': 'Make recurring…',
        'tasks.modal.copyLink': '🔗 Copy link',
        'tasks.modal.linkCopied': 'Link copied to clipboard',
        'tasks.modal.delete': '🗑️ Delete Task',
        'tasks.modal.archive': '📦 Archive Task',
        'tasks.modal.restore': '↩️ Restore Task',
        'tasks.contextMenu.open': 'Open',
        'tasks.contextMenu.duplicate': 'Duplicate',
        'tasks.contextMenu.copyLink': 'Copy link',
        'tasks.contextMenu.status': 'Status',
        'tasks.contextMenu.priority': 'Priority',
        'tasks.contextMenu.archive': 'Archive',
        'tasks.contextMenu.delete': 'Delete',
        'tasks.contextMenu.selectedCount': '{{count}} tasks selected',
        'tasks.contextMenu.updatedCount': '{{count}} tasks updated',
        'tasks.filters.showArchived': '📦 Archived',
        'tasks.archived.btnLabel': 'Archived',
        'tasks.archived.back': '← Back',
        'tasks.archived.automated': 'Automated',
        'tasks.archived.automatedFilter': '🔄 Automated only',
        'tasks.archived.btnTitle': 'View archived tasks',
        'tasks.archived.modalTitle': 'Archived Tasks',
        'tasks.archived.modalSubtitle': 'Archived tasks are hidden from all active views until restored.',
        'tasks.archived.searchPlaceholder': 'Search archived tasks...',
        'tasks.archived.searchAria': 'Search archived tasks',
        'tasks.archived.count': '{{count}} task(s)',
        'tasks.archived.emptyTitle': 'No archived tasks',
        'tasks.archived.emptySubtitle': 'Tasks you archive will appear here.',
        'tasks.archived.noResults': 'No results',
        'tasks.archived.noResultsSubtitle': 'No archived tasks match your search.',
        'tasks.archived.selectAll': 'Select all archived tasks',
        'tasks.archived.colTask': 'Task',
        'tasks.archived.colProject': 'Project',
        'tasks.archived.colStatus': 'Status',
        'tasks.archived.colArchived': 'Archived',
        'tasks.archived.colActions': 'Actions',
        'tasks.massEdit.archive': '📦 Archive',
        'tasks.massEdit.restore': '↩️ Restore',
        'toast.task.archived': 'Task archived',
        'toast.task.restored': 'Task restored',
        'toast.task.restoredParentArchived': 'Task restored. Project "{{project}}" is still archived.',
        'toast.tasks.archivedCount': '{{count}} task(s) archived',
        'toast.tasks.restoredCount': '{{count}} task(s) restored',
        'toast.project.archived.none': 'Project archived. No tasks were archived.',
        'toast.project.archived.one': 'Project archived. 1 task archived too.',
        'toast.project.archived.many': 'Project archived. {count} tasks archived too.',
        'toast.project.restored.none': 'Project restored. No tasks were restored.',
        'toast.project.restored.one': 'Project restored. 1 task restored too.',
        'toast.project.restored.many': 'Project restored. {count} tasks restored too.',
        'tasks.modal.tab.general': 'General',
        'tasks.modal.tab.details': 'Details',
        'tasks.modal.tab.history': 'History',
        'tasks.modal.titleLabel': 'Task Title',
        'tasks.modal.attachmentsLabel': 'Attachments',
        'tasks.modal.attachmentsSupported': 'Supported: Images (25MB), PDFs (750MB), Documents (25MB)',
        'tasks.modal.attachmentsDropzoneDefault': 'Drag & drop or click to attach files (up to 10)',
        'tasks.modal.attachmentsDropzoneTap': 'Tap to attach files (up to 10)',
        'tasks.modal.linksLabel': 'Links',
        'tasks.modal.attachmentNamePlaceholder': 'Name (optional)',
        'tasks.modal.attachmentUrlPlaceholder': 'URL',
        'tasks.modal.addLink': 'Add Link',
        'tasks.links.relatesTo': 'relates to',
        'tasks.links.blocks': 'blocks',
        'tasks.links.isBlockedBy': 'is blocked by',
        'tasks.links.dependsOn': 'depends on',
        'tasks.links.openTask': 'Open task',
        'tasks.links.removeDependency': 'Remove dependency',
        'tasks.links.webLink': 'Web link',
        'tasks.links.searchTasks': 'Search tasks...',
        'tasks.modal.statusLabel': 'Status',
        'tasks.modal.priorityLabel': 'Priority',
        'tasks.modal.tagsLabel': 'Tags',
        'tasks.modal.addTagPlaceholder': 'Add tag',
        'tasks.modal.projectLabel': 'Project',
        'tasks.modal.projectOpenTitle': 'Open project details',
        'tasks.modal.projectSelect': 'Select a project',
        'tasks.modal.submitCreate': 'Create Task',
        'tasks.modal.createAnother': 'Create Another',
        'tasks.modal.listBulleted': '• List',
        'tasks.modal.listNumbered': '1. List',
        'tasks.modal.insertDivider': 'Insert horizontal divider',
        'tasks.modal.insertCheckbox': 'Insert checkbox',
        'tasks.history.emptyTitle': 'No Changes Yet',
        'tasks.history.emptySubtitle': 'Changes to this task will appear here',
        'calendar.title': 'Calendar',
        'calendar.today': 'Today',
        'calendar.prevMonth': 'Previous month',
        'calendar.nextMonth': 'Next month',
        'calendar.dayItemsTitle': 'Items for {date}',
        'calendar.dayItemsProjects': 'Projects',
        'calendar.dayItemsTasks': 'Tasks',
        'calendar.dayItems.close': 'Close day items',
        'calendar.dayItems.addTask': '+ Add Task',
        'calendar.filters.all': 'All',
        'calendar.filters.projects': 'Projects',
        'calendar.filters.tasks': 'Tasks',
        'calendar.filters.searchProjects': '🔍 Search projects...',
        'calendar.filters.filtersBtn': 'Filters',
        'calendar.filters.clearAll': 'Clear',
        'calendar.filters.panelTitle': 'Filters',
        'calendar.filters.drawerTitle': 'Filters',
        'calendar.filters.sectionSuffix': 'Filters',
        'calendar.filters.stepProjects': 'Which projects?',
        'calendar.filters.stepProjectsHint': 'Their bars and their tasks show on the calendar',
        'calendar.filters.stepTasks': 'Which tasks?',
        'calendar.filters.scopeQuick': 'Project shortcuts',
        'calendar.filters.scopeAll': 'All',
        'calendar.filters.scopeActive': 'Active',
        'calendar.filters.pickProjects': 'Choose projects',
        'calendar.filters.projectsCount': 'Projects: {count} of {total}',
        'calendar.filters.visibleCount': 'Visible: {count}',
        'calendar.filters.clearAllFilters': 'Clear all',
        'feedback.title': 'Feedback & Issues',
        'feedback.subtitle': 'Report bugs and suggest features',
        'feedback.saveStatus.saved': 'Saved',
        'feedback.saveStatus.saving': 'Saving...',
        'feedback.saveStatus.error': 'Save failed',
        'feedback.saveStatus.offline': 'Offline',
        'feedback.type.bugLabel': '\u{1F41E} Bug',
        'feedback.type.title': 'Feedback Type',
        'feedback.type.bugOption': '\u{1F41E} Bug',
        'feedback.type.improvementOption': '\u{1F4A1} Improvement',
        'feedback.descriptionPlaceholder': 'Describe the issue or idea. You can paste an image directly into this field.',
        'feedback.descriptionPlaceholderShort': 'Describe the issue or idea.',
        'feedback.screenshotAttachTitle': 'Attach file from device',
        'feedback.screenshotDropzoneTap': '\u{1F4F7} Tap to attach',
        'feedback.screenshotDropzoneDefault': '\u{1F4F7} Drag & drop or click to attach',
        'feedback.attachmentPreviewTitle': 'File attached',
        'feedback.screenshotPreviewTitle': 'Screenshot attached',
        'feedback.screenshotPreviewSubtitle': 'Will be saved with this feedback',
        'feedback.screenshotRemove': 'Remove',
        'feedback.screenshotPreviewAlt': 'Feedback screenshot preview',
        'feedback.viewScreenshotTitle': 'View screenshot',
        'feedback.uploading': 'Uploading...',
        'feedback.attachment': 'Attachment',
        'feedback.pagination.first': 'First page',
        'feedback.pagination.prev': 'Previous page',
        'feedback.pagination.next': 'Next page',
        'feedback.pagination.last': 'Last page',
        'feedback.pagination.showing': 'Showing {start}-{end} of {total}',
        'feedback.pagination.pageOf': 'Page {current} of {total}',
        'feedback.addButton': 'Add',
        'feedback.pendingTitle': '? Pending',
        'feedback.doneTitle': '? Done',
        'feedback.delete.title': 'Delete Feedback',
        'feedback.delete.body': 'Are you sure you want to delete this feedback?',
        'feedback.empty.pending': 'No pending feedback',
        'feedback.empty.done': 'No completed feedback',
        'export.title': 'Export Data',
        'export.body': 'This will download a complete backup of all your tasks, projects, and settings as a JSON file. Are you sure you want to export your data?',
        'export.confirm': 'Export',
        'import.title': 'Import Data',
        'import.warningTitle': 'WARNING: This is a FULL REPLACEMENT',
        'import.warningBody': 'Importing data will <strong>permanently replace</strong> all your current:',
        'import.warningTasks': 'All tasks',
        'import.warningProjects': 'All projects',
        'import.warningSettings': 'All settings and preferences',
        'import.warningHistory': 'All history records',
        'import.warningIrreversible': 'This action cannot be undone. Your current data will be lost.',
        'import.fileLabel': 'Select backup file to import:',
        'import.dropzoneDefault': 'Drag & drop or click to select backup file',
        'import.fileHint': 'Only JSON files exported from Nautilus are supported',
        'import.previewTitle': 'Import Preview:',
        'import.confirmText': 'To confirm import and replace all your data, type <strong>import</strong> below:',
        'import.inputPlaceholder': 'Type import here',
        'import.error': 'Type "import" exactly to confirm',
        'import.button': 'Import Data',
        'import.processing': 'Importing data...',
        'import.success': 'Data imported successfully! All data has been replaced.',
        'import.errorInvalidFile': 'Invalid file type. Please select a JSON file.',
        'import.errorInvalidFormat': 'Invalid file format. The file does not appear to be a valid Nautilus backup.',
        'import.errorParseFailed': 'Failed to parse file. Please ensure the file is valid JSON.',
        'import.errorReadFailed': 'Failed to read file. Please try again.',
        'import.errorNoFile': 'Please select a file to import.',
        'import.errorFailed': 'Failed to import data',
        'confirm.deleteTask.title': 'Delete Task',
        'confirm.deleteTask.body': 'This action cannot be undone. To confirm deletion, type delete below:',
        'confirm.deleteTask.inputPlaceholder': 'Type delete here',
        'confirm.deleteTask.error': 'Type \"delete\" exactly to confirm',
        'confirm.unsaved.title': 'Unsaved Changes',
        'confirm.unsaved.body': 'You have unsaved changes. Are you sure you want to close and lose them?',
        'confirm.unsaved.discard': 'Discard Changes',
        'confirm.review.title': 'Disable \"In Review\" Status',
        'error.saveDataFailed': 'Failed to save data. Please try again.',
        'error.saveProjectsFailed': 'Failed to save projects. Please try again.',
        'error.saveTasksFailed': 'Failed to save tasks. Please try again.',
        'error.saveRetry': 'Retrying save...',
        'error.saveFeedbackFailed': 'Failed to save feedback. Please try again.',
        'error.saveProjectColorsFailed': 'Failed to save project colors.',
        'error.saveTaskFailed': 'Failed to save task. Please try again.',
        'error.saveChangesFailed': 'Failed to save changes. Please try again.',
        'error.saveTaskPositionFailed': 'Failed to save task position. Please try again.',
        'error.saveProjectFailed': 'Failed to save project. Please try again.',
        'error.notLoggedInResetPin': 'You must be logged in to reset your PIN',
        'error.resetPinFailed': 'Failed to reset PIN',
        'error.unauthorized': 'You are not authorized to perform this action',
        'error.deleteAccountFailed': 'Failed to delete account. Please try again.',
        'success.resetPin': 'PIN reset successfully',
        'error.resetPinError': 'An error occurred while resetting your PIN',
        'error.userNameEmpty': 'User name cannot be empty.',
        'error.saveDisplayNameFailed': 'Could not save display name. Please try again.',
        'error.invalidEmail': 'Please enter a valid email address.',
        'error.saveEmailFailed': 'Could not save email. Please try again.',
        'error.saveAvatarFailed': 'Could not save avatar. Please try again.',
        'success.settingsSaved': 'Settings saved successfully!',
        'error.logoSelectFile': 'Please select an image file for the workspace logo.',
        'error.logoTooLarge': 'Please use an image smaller than 2MB for the workspace logo.',
        'error.imageReadFailed': 'Could not read the selected image.',
        'error.imageLoadFailed': 'Could not load the selected image.',
        'error.logoUploadFailed': 'Error uploading logo: {message}',
        'error.cropInvalid': 'Error: Crop state is invalid.',
        'error.cropTooLarge': 'Cropped image is too large. Please select a smaller area or use a smaller source image.',
        'success.cropApplied': 'Image cropped and applied successfully!',
        'success.logoCroppedApplied': 'Workspace logo cropped and applied successfully!',
        'error.cropFailed': 'Error cropping image: {message}',
        'error.avatarSelectFile': 'Please select an image file for your avatar.',
        'error.avatarTooLarge': 'Please use an image smaller than 2MB for your avatar.',
        'error.avatarUploadFailed': 'Failed to upload avatar. Please try again.',
        'error.endDateBeforeStart': 'End date cannot be before start date',
        'error.startDateAfterEnd': 'Start date cannot be after end date',
        'error.feedbackAttachImage': 'Please attach an image file.',
        'error.feedbackReadImage': 'Could not read the image file. Please try again.',
        'error.feedbackStatusFailed': 'Failed to update feedback status. Please try again.',
        'error.attachmentSaveFailed': 'Failed to save attachment. Please try again.',
        'error.attachmentLoadFailed': 'Failed to load image: {message}',
        'success.fileDownloaded': 'File downloaded!',
        'error.fileDownloadFailed': 'Failed to download file: {message}',
        'success.attachmentDeletedFromStorage': '{name} deleted from storage',
        'error.fileDeleteFailed': 'Failed to delete file from storage',
        'success.attachmentRemoved': 'Attachment removed',
        'success.linkAdded': 'Task link added successfully!',
        'success.linkRemoved': 'Task link removed successfully!',
        'error.linkAlreadyExists': 'This link already exists',
        'error.linkNotFound': 'Link not found',
        'error.cannotLinkToSelf': 'Cannot link a task to itself',
        'error.invalidTaskIds': 'Invalid task IDs',
        'error.fileSizeTooLarge': 'File size must be less than {maxMB}MB. Please choose a smaller file.',
        'error.tooManyFiles': 'Maximum {max} files per upload. Please select {max} or fewer at a time.',
        'success.fileUploaded': 'File uploaded successfully!',
        'error.fileUploadFailed': 'Error uploading file: {message}',
        'error.selectFile': 'Please select a file',
        'error.saveTagFailed': 'Failed to save tag. Please try again.',
        'error.removeTagFailed': 'Failed to remove tag. Please try again.',
        'error.openScreenshotFailed': 'Could not open screenshot',
        'error.cfOutageBanner': 'Changes cannot be saved right now. Avoid creating new content until resolved.',
        'error.cfOutageContact': 'Contact support',
        'error.cfOutageBlocked': 'Saving is unavailable right now. Please wait and try again.',
        'history.sort.newest': '↑ Newest First',
        'history.sort.oldest': '↓ Oldest First',
        'history.field.title': 'Title',
        'history.field.name': 'Name',
        'history.field.description': 'Description',
        'history.field.status': 'Status',
        'history.field.priority': 'Priority',
        'history.field.category': 'Category',
        'history.field.startDate': 'Start Date',
        'history.field.endDate': 'End Date',
        'history.field.link': 'Link',
        'history.field.task': 'Link',
        'history.field.projectId': 'Project',
        'history.field.tags': 'Tags',
        'history.field.attachments': 'Attachments',
        'history.actor.kai': 'Kai',
        'history.actor.user': 'User',
        'history.attribution.by': 'By {name}',
        'history.action.created': 'Created',
        'history.action.deleted': 'Deleted',
        'history.link.added': 'Added',
        'history.link.removed': 'Removed',
        'history.entity.task': 'task',
        'history.entity.project': 'project',
        'history.value.empty': 'empty',
        'history.value.none': 'none',
        'history.change.beforeLabel': 'Before:',
        'history.change.afterLabel': 'After:',
        'history.change.notSet': 'Not set',
        'history.change.removed': 'Removed',
        'history.tags.none': 'No tags',
        'history.attachments.none': 'No attachments',
        'history.attachments.countSingle': '{count} file',
        'history.attachments.countPlural': '{count} files',
        'history.project.fallback': 'Project #{id}',
        'history.change.arrow': '→',
        'menu.openMenu': 'Open menu',
        'menu.language': 'Language',
        'menu.darkMode': 'Dark mode',
        'menu.lightMode': 'Light mode',
        'menu.settings': 'Settings',
        'menu.help': 'Help',
        'menu.signOut': 'Sign out',
        'nav.overview': 'Overview',
        'nav.dashboard': 'Dashboard',
        'nav.collapseSidebar': 'Collapse sidebar',
        'nav.expandSidebar': 'Expand sidebar',
        'nav.updates': 'Release notes',
        'nav.calendar': 'Calendar',
        'nav.work': 'Work',
        'nav.projects': 'Projects',
        'nav.docs': 'Docs',
        'nav.hideDocs': 'Hide docs',
        'nav.showDocs': 'Show docs',
        'nav.allTasks': 'All Tasks',
        'nav.automations': 'Automations',
        'nav.feedback': 'Feedback',
        'automations.title': 'Automations',
        'automations.subtitle': 'Recurring rules and future workflow automations',
        'automations.newRule': '+ New rule',
        'automations.emptyTitle': 'No automations yet',
        'automations.emptySubtitle': 'Create a recurring rule to automatically generate tasks on a schedule.',
        'automations.emptyCta': 'Create recurring',
        'automations.action.deleteConfirm': 'Delete this rule?',
        'automations.action.deleteConfirmYes': 'Delete',
        'automations.status.active': 'Active',
        'automations.status.paused': 'Paused',
        'automations.action.pause': 'Pause',
        'automations.action.resume': 'Resume',
        'automations.action.edit': 'Edit',
        'automations.action.delete': 'Delete',
        'automations.action.audit': 'View generated tasks',
        'automations.action.auditArchive': 'View archived tasks',
        'automations.audit.title': 'Generated tasks',
        'automations.audit.archiveTitle': 'Archived tasks',
        'automations.audit.emptyTitle': 'No tasks generated yet',
        'automations.audit.emptyBody': 'This rule hasn\'t created any tasks yet, or the tasks it created have since been deleted.',
        'automations.audit.archiveEmptyTitle': 'No tasks archived yet',
        'automations.audit.archiveEmptyBody': 'This rule has not archived any tasks yet, or the tasks it archived have since been restored or deleted.',
        'automations.audit.searchAria': 'Search tasks in this history',
        'automations.audit.searchPlaceholder': 'Search tasks or projects...',
        'automations.audit.sortAria': 'Sort history',
        'automations.audit.sortNewest': 'Newest first',
        'automations.audit.sortOldest': 'Oldest first',
        'automations.audit.sortTitle': 'Task title A–Z',
        'automations.audit.sortProject': 'Project A–Z',
        'automations.audit.count': '{count} tasks',
        'automations.audit.noResultsTitle': 'No matching tasks',
        'automations.audit.noResultsBody': 'Try a different task or project name.',
        'automations.audit.task': 'Task',
        'automations.audit.project': 'Project',
        'automations.audit.archivedDate': 'Archived',
        'automations.audit.createdDate': 'Created',
        'automations.audit.status': 'Status',
        'automations.summary.nextRun': 'Next: {date}',
        'automations.recurrence.generatedBy': 'Generated by automation',
        'automations.group.recurringTask': 'Recurring tasks',
        'automations.type.recurringTask': 'Recurring task',
        'automations.type.recurringTaskHint': 'Generate a task on a repeating schedule.',
        'automations.typePicker.title': 'Choose an automation type',
        'automations.typePicker.comingSoon': 'More coming soon',
        'automations.typePicker.close': 'Close',
        'automations.type.dueSoon': 'Due-soon reminder',
        'automations.type.autoArchive': 'Auto-archive done tasks',
        'automations.type.autoArchiveHint': 'Archive Done tasks automatically.',
        'automations.group.autoArchive': 'Auto-archive',
        'autoArchive.unit.day': 'day',
        'autoArchive.unit.days': 'days',
        'autoArchive.unit.task': 'task',
        'autoArchive.unit.tasks': 'tasks',
        'autoArchive.createTitle': 'Create auto-archive rule',
        'autoArchive.editTitle': 'Edit auto-archive rule',
        'autoArchive.modalSubtitle': 'Move finished tasks out of the way automatically',
        'autoArchive.trigger': 'When to archive',
        'autoArchive.mode.doneAge': 'After time in Done',
        'autoArchive.mode.onDate': 'On a specific date',
        'autoArchive.mode.periodic': 'Recurring cleanup',
        'autoArchive.doneAge.label': 'Archive a task once it has been Done for',
        'autoArchive.doneAge.hint': 'Checked every time Nautilus loads.',
        'autoArchive.onDate.label': 'Archive every Done task on this date',
        'autoArchive.onDate.hint': 'Runs the next time Nautilus opens on or after that date, then turns itself off.',
        'autoArchive.periodic.label': 'Clean up Done tasks',
        'autoArchive.periodic.daily': 'Daily',
        'autoArchive.periodic.weekly': 'Weekly',
        'autoArchive.periodic.monthly': 'Monthly',
        'autoArchive.periodic.weeklyOn': 'On',
        'autoArchive.periodic.monthlyOnDay': 'On day',
        'autoArchive.periodic.monthlyLastDay': 'Last day of the month',
        'autoArchive.periodic.hint': 'Archives every Done task on each run.',
        'autoArchive.scope.includeProjectTasks': 'Include tasks in projects',
        'autoArchive.scope.hint': 'By default, only tasks without a project are archived.',
        'autoArchive.preview.empty': 'Choose a trigger above to preview this rule.',
        'autoArchive.preview.doneAge': 'Archives Done tasks {count} {unit} after they are completed.',
        'autoArchive.preview.onDate': 'When Nautilus opens on or after {date}, this archives every Done task, then switches off.',
        'autoArchive.preview.periodicDaily': 'Archives every Done task once a day.',
        'autoArchive.preview.periodicWeekly': 'Archives every Done task every {days}.',
        'autoArchive.preview.periodicWeeklyNoDays': 'Archives every Done task once a week.',
        'autoArchive.preview.periodicMonthly': 'Archives every Done task on day {day} of each month.',
        'autoArchive.preview.periodicMonthlyNoDay': 'Archives every Done task once a month.',
        'autoArchive.preview.matchNow': 'Matches {count} {unit} right now.',
        'autoArchive.preview.matchNone': 'No Done tasks match right now.',
        'autoArchive.save': 'Save rule',
        'autoArchive.summary.doneAge': 'Archive Done tasks after {count} {unit}',
        'autoArchive.summary.onDate': 'Archive Done tasks on {date}',
        'autoArchive.summary.periodicDaily': 'Daily cleanup of Done tasks',
        'autoArchive.summary.periodicWeekly': 'Weekly · {days}',
        'autoArchive.summary.periodicWeeklyShort': 'Weekly cleanup of Done tasks',
        'autoArchive.summary.periodicMonthly': 'Monthly · day {day}',
        'autoArchive.summary.periodicMonthlyShort': 'Monthly cleanup of Done tasks',
        'autoArchive.meta.continuous': 'Runs on every app load',
        'autoArchive.meta.scheduled': 'Scheduled for {date}',
        'autoArchive.meta.completed': 'Completed',
        'autoArchive.meta.nextRun': 'Next run {date}',
        'autoArchive.meta.archivedTotal': '{count} archived so far',
        'autoArchive.validation.mode': 'Choose when tasks should be archived.',
        'autoArchive.validation.threshold': 'Enter a number of days between 1 and 3650.',
        'autoArchive.validation.date': 'Choose a valid archive date.',
        'autoArchive.validation.pattern': 'Choose a cleanup frequency.',
        'toast.autoArchive.created': 'Auto-archive rule created',
        'toast.autoArchive.updated': 'Auto-archive rule updated',
        'toast.autoArchive.archivedCount': '{count} {unit} auto-archived',
        'automations.deleted': 'Automation deleted',
        'recurrence.save': 'Save',
        'recurrence.modalSubtitle': 'Schedule a task to repeat automatically',
        'recurrence.section.source': 'Source',
        'recurrence.source.createNewTask': '+ Create a new task as template',
        'recurrence.source.searchPlaceholder': 'Search tasks...',
        'recurrence.source.noMatches': 'No matching tasks',
        'recurrence.summary.label': 'Summary',
        'recurrence.summary.empty': 'Complete the fields above to preview the rule.',
        'recurrence.summary.daily': 'Creates "{title}" every day',
        'recurrence.summary.weekly': 'Creates "{title}" every {days}',
        'recurrence.summary.monthly': 'Creates "{title}" every month',
        'recurrence.summary.everyX': 'Creates "{title}" every {count} {unit}',
        'recurrence.summary.firstOccurrence': 'first occurrence {date}',
        'recurrence.summary.until': 'until {date}',
        'recurrence.createTitle': 'Create recurring Task',
        'recurrence.editTitle': 'Edit recurring rule',
        'recurrence.sourceTask': 'Source task',
        'recurrence.ruleName': 'Rule name',
        'recurrence.ruleNamePlaceholder': 'Defaults to the source task\'s title',
        'recurrence.sourceTaskHint': 'Pick the task template this rule should copy.',
        'recurrence.sourceSummary': 'The original source task no longer exists.',
        'recurrence.sourceTaskMissingOption': 'Select a replacement task',
        'recurrence.sourceTaskMissingHelp': 'The original source task no longer exists. Pick a replacement task before saving.',
        'recurrence.repeat': 'Repeat',
        'recurrence.pattern.daily': 'Daily',
        'recurrence.pattern.weekly': 'Weekly',
        'recurrence.pattern.monthly': 'Monthly',
        'recurrence.pattern.everyXDays': 'Custom',
        'recurrence.pattern.everyXDaysLabel': 'Every {count} {unit}',
        'recurrence.pattern.everyXDaysPrefix': 'Every',
        'recurrence.pattern.everyXDaysSuffix': 'days',
        'recurrence.intervalUnitAria': 'Interval unit',
        'recurrence.intervalUnit.days': 'Days',
        'recurrence.intervalUnit.months': 'Months',
        'recurrence.intervalUnit.years': 'Years',
        'recurrence.section.dates': 'Task dates',
        'recurrence.taskDates.startLabel': 'Start date',
        'recurrence.taskDates.selectOption': 'Select option...',
        'recurrence.taskDates.startSameDay': 'Same day as creation',
        'recurrence.taskDates.startNone': 'No start date',
        'recurrence.taskDates.endLabel': 'End date',
        'recurrence.taskDates.endNone': 'No end date',
        'recurrence.taskDates.endSameDay': 'Same day as creation',
        'recurrence.taskDates.endOffset': 'Days after start',
        'recurrence.taskDates.endOffsetCreation': 'Days after creation',
        'recurrence.taskDates.days': 'days',
        'recurrence.automationEnd.label': 'Stop on',
        'recurrence.automationEnd.never': 'Never',
        'recurrence.automationEnd.onDate': 'On date',
        'recurrence.summary.taskDates': '· starts {start}',
        'recurrence.summary.taskDuration': '· {n}d duration',
        'recurrence.weekday.sun': 'Sun',
        'recurrence.weekday.mon': 'Mon',
        'recurrence.weekday.tue': 'Tue',
        'recurrence.weekday.wed': 'Wed',
        'recurrence.weekday.thu': 'Thu',
        'recurrence.weekday.fri': 'Fri',
        'recurrence.weekday.sat': 'Sat',
        'recurrence.endsOptional': 'Ends (optional)',
        'recurrence.validation.noTask': 'Create at least one task before adding an automation.',
        'recurrence.validation.invalidRule': 'Complete the recurrence fields before saving.',
        'recurrence.validation.sourceTaskMissing': 'Pick a replacement task for this automation before saving.',
        'recurrence.validation.taskStartDateRequired': 'Choose a start date option.',
        'recurrence.validation.taskStartDateRequiredForOffset': 'Choose Same day as creation for the start date when using days after start.',
        'recurrence.validation.taskEndDateRequired': 'Choose an end date option.',
        'recurrence.validation.intervalDaysRequired': 'Enter a valid repeat interval.',
        'recurrence.validation.endDateRequired': 'Choose an end date.',
        'recurrence.validation.endDateOffsetRequired': 'Enter how many days after the start the task should end.',
        'recurrence.saved.created': 'Recurring rule created',
        'recurrence.saved.updated': 'Recurring rule updated',
        'recurrence.saved.deleted': 'Recurring rule deleted',
        'recurrence.modalSubtitle.redesign': 'Schedule a task to repeat automatically',
        'recurrence.searchExisting': 'Search existing',
        'recurrence.createNew': 'Create new',
        'recurrence.searchPlaceholder': 'Search tasks…',
        'recurrence.newTaskPlaceholder': 'New task title…',
        'recurrence.repeats': 'Repeats',
        'recurrence.freq.daily': 'Daily',
        'recurrence.freq.weekly': 'Weekly',
        'recurrence.freq.monthly': 'Monthly',
        'recurrence.freq.custom': 'Custom',
        'recurrence.every': 'Every',
        'recurrence.on': 'On',
        'recurrence.after': 'After',
        'recurrence.occurrences': 'occurrences',
        'recurrence.unit.days': 'days',
        'recurrence.unit.weeks': 'weeks',
        'recurrence.unit.months': 'months',
        'recurrence.monthly.sameDate': 'On the same date each month',
        'recurrence.monthly.sameWeekday': 'On the same weekday each month',
        'recurrence.day.mo': 'Mo',
        'recurrence.day.tu': 'Tu',
        'recurrence.day.we': 'We',
        'recurrence.day.th': 'Th',
        'recurrence.day.fr': 'Fr',
        'recurrence.day.sa': 'Sa',
        'recurrence.day.su': 'Su',
        'recurrence.taskDates': 'Task Dates',
        'recurrence.firstOccurrence': 'First occurrence',
        'recurrence.duration': 'Duration (days)',
        'recurrence.ends': 'Ends',
        'recurrence.end.never': 'Never',
        'recurrence.end.onDate': 'On date',
        'recurrence.end.afterN': 'After N times',
        'recurrence.saveRule': 'Save Rule',
        'recurrence.errorNoDays': 'Please select at least one day',
        'recurrence.summary.pickDay': 'Pick at least one day of the week',
        'recurrence.preview.repeats': 'Repeats {repeat}',
        'recurrence.preview.everyDay': 'every day',
        'recurrence.preview.everyNDays': 'every {count} days',
        'recurrence.preview.everyWeekOn': 'every {days}',
        'recurrence.preview.everyNWeeksOn': 'every {count} weeks on {days}',
        'recurrence.preview.monthlyDate': 'on the same date each month',
        'recurrence.preview.monthlyWeekday': 'on the same weekday each month',
        'recurrence.preview.everyNUnit': 'every {count} {unit}',
        'recurrence.preview.firstOccurrence': 'starting {date}',
        'recurrence.preview.until': 'until {date}',
        'recurrence.preview.forNOccurrences': 'for {count} occurrences',
        'recurrence.preview.forever': 'forever',
        'recurrence.validation.firstOccurrenceRequired': 'Choose a first occurrence date.',
        'recurrence.validation.firstOccurrencePast': 'First occurrence cannot be in the past.',
        'updates.title': 'Release notes',
        'updates.subtitle': 'Latest change log for Nautilus',
        'updates.latestLabel': 'Latest release',
        'updates.historyLabel': 'Release log',
        'updates.sections.new': 'New',
        'updates.sections.improvements': 'Improvements',
        'updates.sections.fixes': 'Fixes',
        'updates.empty': 'No release notes yet.',
        'updates.sectionEmpty': 'Nothing listed yet.',
        'updates.historyEmpty': 'No previous releases yet.',
        'notifications.title': 'Notifications',
        'notifications.toggle': 'Notifications',
        'notifications.today': 'Today',
        'notifications.yesterday': 'Yesterday',
        'notifications.clearAll': 'Clear all',
        'notifications.releaseTitle': 'New release',
        'notifications.releaseCta': 'View updates',
        'notifications.releaseMeta': 'Released {date}',
        'notifications.dueTodayTitle': 'Due today',
        'notifications.dueTodayCta': 'View tasks',
        'notifications.dueTodayMetaOne': '{count} task due today',
        'notifications.dueTodayMetaMany': '{count} tasks due today',
        'notifications.dueTodayMore': 'and {count} more tasks',
        'notifications.sectionStarting': 'STARTING',
        'notifications.sectionDue': 'DUE',
        'notifications.moreStarting': '+{count} more starting',
        'notifications.moreDue': '+{count} more due',
        'notifications.viewStarting': 'View {count} starting',
        'notifications.viewDue': 'View {count} due',
        'notifications.dismiss': 'Dismiss',
        'notifications.empty': 'You are all caught up.',
        'settings.title': 'Settings',
        'settings.subtitle': 'Manage your preferences and application settings',
        'settings.tabs.ariaLabel': 'Settings sections',
        'settings.tabs.account': 'Account',
        'settings.tabs.preferences': 'Preferences',
        'settings.tabs.notifications': 'Notifications',
        'settings.tabs.security': 'Security',
        'settings.tabs.data': 'Data',
        'settings.tabs.advanced': 'Advanced',
        'settings.panel.account.subtitle': 'Your name, photo and email',
        'settings.panel.preferences.subtitle': 'Language and how tasks behave',
        'settings.panel.notifications.subtitle': 'When and how Nautilus reminds you',
        'settings.panel.security.subtitle': 'How you sign in',
        'settings.panel.data.subtitle': 'Back up, restore or delete your data',
        'settings.panel.advanced.subtitle': 'Troubleshooting tools',
        'settings.advancedNote': 'Only needed when troubleshooting with support.',
        'settings.tabs.general': 'General',
        'settings.mobileBack': 'All settings',
        'settings.group.signInSecurity': 'Sign-in & security',
        'settings.group.languageWorkspace': 'Language & workspace',
        'settings.group.tasks': 'Tasks',
        'settings.group.reminderOptions': 'Reminder options',
        'settings.group.backup': 'Backup',
        'settings.group.docs': 'Docs',
        'settings.unsavedChanges': 'Unsaved changes',
        'settings.deleteAccount.backupHint': 'Might you need your work later? Download a backup first from Data.',
        'settings.deleteAccount.keep': 'Keep my account',
        'settings.section.profile': 'Profile',
        'settings.displayName': 'Display Name',
        'settings.displayNameHint': 'Shown across the app',
        'settings.placeholder.displayName': 'Enter your display name',
        'settings.email': 'Email',
        'settings.emailHint': 'For your account and reminders',
        'settings.placeholder.email': 'Enter your email',
        'settings.avatar': 'Avatar',
        'settings.avatarHint': 'Max 2 MB, shown as a circle',
        'settings.avatarRemoveTitle': 'Remove avatar',
        'settings.workspaceLogo': 'Logo',
        'settings.workspaceLogoHint': 'Square image, max 2 MB',
        'settings.workspaceLogoRemoveTitle': 'Remove custom logo',
        'settings.section.application': 'Application',
        'settings.enableReviewStatus': 'Enable In Review status',
        'settings.enableReviewStatusHintPrefix': 'Adds the',
        'settings.enableReviewStatusHintSuffix': 'column and filter',
        'settings.calendarIncludeBacklog': 'Show backlog in calendar',
        'settings.autoStartDate': 'Auto-set start date',
        'settings.autoStartDateHint': 'When moved to "In Progress", if empty',
        'settings.autoEndDate': 'Auto-set end date',
        'settings.autoEndDateHint': 'When moved to "Done", if empty',
        'settings.debugLogs': 'Enable debug logs',
        'settings.debugLogsHint': 'Diagnostic logs in the browser console',
        'settings.diagOverlay': 'On-screen diagnostics',
        'settings.diagOverlayHint': 'Debug panel on this device only. Applies instantly.',
        'settings.historySortOrder': 'History Sort Order',
        'settings.historySortOrderHint': 'For tasks and projects',
        'settings.historySortNewest': 'Newest First',
        'settings.historySortOldest': 'Oldest First',
        'settings.language': 'Language',
        'settings.section.notifications': 'Notifications',
        'settings.emailNotifications': 'Email notifications',
        'settings.emailNotificationsHint': 'Deadline reminder emails',
        'settings.weekdaysOnly': 'Weekdays only',
        'settings.weekdaysOnlyHint': 'Skip emails on Saturday and Sunday',
        'settings.includeWeekAhead': 'Notify a week before due',
        'settings.includeWeekAheadHint': 'For tasks due in 7 days',
        'settings.includeStartDates': 'Notify when tasks start',
        'settings.includeStartDatesHint': 'Reminder on the start date',
        'settings.includeBacklog': 'Include backlog tasks',
        'settings.includeBacklogHint': 'In emails and in-app notifications',
        'settings.sendTime': 'Send time',
        'settings.sendTimeHint': '08:00–18:00, every 30 min',
        'settings.timeZone': 'Time zone',
        'settings.timeZoneHint': 'Adjusts for daylight saving time',
        'settings.timeZoneSearch': 'Search time zones',
        'settings.timeZoneNoResults': 'No results',
        'settings.timeZone.option.newYork': 'New York, USA',
        'settings.timeZone.option.chicago': 'Chicago, USA',
        'settings.timeZone.option.denver': 'Denver, USA',
        'settings.timeZone.option.phoenix': 'Phoenix, USA',
        'settings.timeZone.option.losAngeles': 'Los Angeles, USA',
        'settings.timeZone.option.anchorage': 'Anchorage, USA',
        'settings.timeZone.option.honolulu': 'Honolulu, USA',
        'settings.timeZone.option.mexicoCity': 'Mexico City, Mexico',
        'settings.timeZone.option.saoPaulo': 'Sao Paulo, Brazil',
        'settings.timeZone.option.argentina': 'Buenos Aires, Argentina',
        'settings.timeZone.option.canary': 'Canary Islands',
        'settings.timeZone.option.london': 'London, UK',
        'settings.timeZone.option.spain': 'Madrid, Spain',
        'settings.timeZone.option.paris': 'Paris, France',
        'settings.timeZone.option.utc': 'UTC',
        'settings.section.security': 'Security',
        'settings.authMethod': 'Authentication Method',
        'settings.authMethodHintPin': 'Currently using PIN',
        'settings.authMethodHintPassword': 'Currently using Password',
        'settings.switchToPassword': 'Switch to Password',
        'settings.switchToPin': 'Switch to PIN',
        'settings.pinManagement': 'PIN Management',
        'settings.pinManagementHint': 'Set a new 4-digit PIN',
        'settings.passwordManagement': 'Password Management',
        'settings.passwordManagementHint': 'Change your password',
        'settings.resetPinButton': 'Reset PIN',
        'settings.changePasswordButton': 'Change Password',
        'settings.switchAuth.title': 'Switch Authentication Method',
        'settings.switchAuth.verifyCurrentPin': 'Verify your current PIN',
        'settings.switchAuth.verifyCurrentPassword': 'Verify your current password',
        'settings.switchAuth.enterNewPassword': 'Enter your new password',
        'settings.switchAuth.confirmNewPassword': 'Confirm your new password',
        'settings.switchAuth.enterNewPin': 'Enter your new PIN',
        'settings.switchAuth.confirmNewPin': 'Confirm your new PIN',
        'settings.switchAuth.success': 'Authentication method changed successfully!',
        'settings.switchAuth.successPassword': 'Switched to password authentication',
        'settings.switchAuth.successPin': 'Switched to PIN authentication',
        'settings.changePassword.title': 'Change Password',
        'settings.changePassword.currentLabel': 'Current Password',
        'settings.changePassword.newLabel': 'New Password',
        'settings.changePassword.confirmLabel': 'Confirm New Password',
        'settings.changePassword.success': 'Password changed successfully',
        'settings.confirmIdentity.title': 'Confirm it\'s you',
        'settings.confirmIdentity.emailHint': 'Changing your email requires your current PIN or password.',
        'settings.confirmIdentity.deleteHint': 'Deleting your account requires your current PIN or password.',
        'settings.confirmIdentity.pinLabel': 'Current PIN',
        'settings.confirmIdentity.passwordLabel': 'Current password',
        'settings.confirmIdentity.confirm': 'Confirm',
        'settings.confirmIdentity.required': 'Enter your current PIN or password.',
        'settings.confirmIdentity.incorrect': 'Incorrect PIN or password.',
        'settings.confirmIdentity.tooManyAttempts': 'Too many attempts. Please try again later.',
        'settings.section.dataManagement': 'Data Management',
        'settings.exportData': 'Export Data',
        'settings.exportDataHint': 'Tasks, projects and settings as JSON',
        'settings.exportButton': 'Export',
        'settings.importData': 'Import Data',
        'settings.importDataHint': 'Replaces all your current data. Can\'t be undone.',
        'settings.importButton': 'Import Data',
        'settings.docsTrash': 'Docs Trash',
        'settings.docsTrashHint': 'Restore deleted docs pages',
        'settings.docsTrashButton': 'Open Trash',
        'settings.section.dangerZone': 'Danger Zone',
        'settings.deleteAccount': 'Delete Account',
        'settings.deleteAccountHint': 'Deletes your account and all its data. Can\'t be undone.',
        'settings.deleteAccountButton': 'Delete Account',
        'settings.deleteAccount.title': 'Delete Account',
        'settings.deleteAccount.warning': 'This action <strong>cannot be undone</strong>. You will permanently lose:',
        'settings.deleteAccount.loseTasks': 'All your tasks',
        'settings.deleteAccount.loseProjects': 'All your projects',
        'settings.deleteAccount.loseData': 'All your account data and settings',
        'settings.deleteAccount.confirmText': 'To confirm account deletion, type <strong>delete</strong> below:',
        'settings.deleteAccount.inputPlaceholder': 'Type delete here',
        'settings.deleteAccount.error': 'Type "delete" exactly to confirm',
        'settings.deleteAccount.button': 'Delete Account',
        'settings.deleteAccount.success': 'Account deleted successfully. Redirecting to login...',
        'settings.cancelButton': 'Cancel',
        'settings.saveButton': 'Save Settings',
        'settings.avatarUploadDefault': 'Drag & drop or click to upload avatar',
        'settings.avatarUploadChange': 'Change avatar',
        'settings.avatarUploadAriaUpload': 'Upload avatar',
        'settings.avatarUploadAriaChange': 'Change avatar',
        'settings.logoUploadDefault': 'Drag & drop or click to upload logo',
        'settings.logoUploadChange': 'Change logo',
        'settings.logoUploadAriaUpload': 'Upload logo',
        'settings.logoUploadAriaChange': 'Change logo',
        'dashboard.title': 'Dashboard',
        'dashboard.greeting.morning': 'Good morning',
        'dashboard.greeting.afternoon': 'Good afternoon',
        'dashboard.greeting.evening': 'Good evening',
        'dashboard.greeting.morningName': 'Good morning, {name}',
        'dashboard.greeting.afternoonName': 'Good afternoon, {name}',
        'dashboard.greeting.eveningName': 'Good evening, {name}',
        'dashboard.greeting.summary': '{overdue} overdue · {today} due today · {high} high priority',
        'dashboard.greeting.summaryOne': '{count} task needs attention before it slips further.',
        'dashboard.greeting.summaryMany': '{count} tasks need attention before they slip further.',
        'dashboard.greeting.allClear': "You're all caught up for today.",
        'dashboard.metrics.aria': 'Dashboard priority metrics',
        'dashboard.summary.overdue': 'Overdue',
        'dashboard.summary.dueToday': 'Due today',
        'dashboard.summary.highPriority': 'High priority',
        'dashboard.summary.inProgress': 'In progress',
        'dashboard.summary.completed': 'Completed',
        'dashboard.summary.sub.open': 'of {count} open',
        'dashboard.summary.sub.thisMonth': 'this month',
        'dashboard.summary.sub.highOverdue': '{count} overdue',
        'dashboard.kai.title': 'Ask Kai',
        'dashboard.kai.launch': 'Launch Kai',
        'dashboard.kai.subtitle': 'Plan work, create tasks, draft docs',
        'dashboard.kai.placeholder': 'Plan my day, draft a doc, or summarize a project...',
        'dashboard.kai.submit': 'Ask Kai',
        'dashboard.kai.chipPlanWeek': 'Plan my day',
        'dashboard.kai.chipSummarize': 'Summarize projects',
        'dashboard.kai.chipAddTasks': 'Add tasks',
        'dashboard.kai.promptPlanWeek': 'Plan my week: review my overdue, due-today and high-priority tasks and suggest a focused plan for the next 7 days.',
        'dashboard.kai.promptSummarize': 'Summarize the current status of my active projects, highlighting progress, blockers and what needs attention.',
        'dashboard.kai.promptAddTasks': 'Add tasks: ',
        'dashboard.cols.due': 'Due',
        'dashboard.cols.status': 'Status',
        'dashboard.cols.priority': 'Priority',
        'dashboard.attention.title': 'Needs attention',
        'dashboard.attention.viewAll': 'View all in Tasks',
        'dashboard.attention.reason.overdue': 'Overdue',
        'dashboard.attention.reason.overdueDays': 'Overdue {count}d',
        'dashboard.attention.reason.today': 'Due today',
        'dashboard.attention.reason.high': 'High priority',
        'dashboard.attention.emptyTitle': 'All clear',
        'dashboard.attention.emptySubtitle': 'Nothing overdue, due today, or high priority. Nice work!',
        'dashboard.attention.completeAria': 'Mark complete',
        'dashboard.attention.completeAction': 'Mark done',
        'dashboard.attention.completedToast': 'Marked "{title}" done.',
        'dashboard.attention.openTask': 'Open task',
        'dashboard.attention.openAction': 'Open',
        'dashboard.glance.title': 'At a glance',
        'dashboard.today.title': 'Today',
        'dashboard.today.openCalendar': 'Open calendar',
        'dashboard.today.dueToday': 'Ends today',
        'dashboard.today.endsToday': 'Ends today',
        'dashboard.today.startsToday': 'Starts today',
        'dashboard.today.startsEndsToday': 'Starts & ends today',
        'dashboard.today.emptyTitle': 'No dated tasks today',
        'dashboard.today.emptySubtitle': 'Your calendar is clear for this date.',
        'dashboard.today.moreUpcoming': '+ {count} more upcoming this week',
        'dashboard.dueOverdue.title': 'Needs attention',
        'dashboard.dueOverdue.overdue': 'Overdue',
        'dashboard.dueOverdue.emptyTitle': 'All caught up',
        'dashboard.dueOverdue.emptySubtitle': 'Nothing overdue. Nice work!',
        'dashboard.upcoming.title': 'Coming up',
        'dashboard.upcoming.tomorrow': 'Tomorrow',
        'dashboard.upcoming.emptyTitle': 'No upcoming deadlines',
        'dashboard.upcoming.emptySubtitle': 'Future dated tasks will appear here.',
        'dashboard.hero.activeProjectsLabel': 'Active Projects',
        'dashboard.hero.completionRateLabel': 'Completion Rate',
        'dashboard.hero.tasksCompletionRateLabel': 'Tasks Completion',
        'dashboard.hero.projectsCompletionRateLabel': 'Projects Completion',
        'dashboard.hero.projectsTrend': '📈 +2 this month',
        'dashboard.hero.completionTrend': '🎯 Target: 80%',
        'dashboard.projectAnalytics': '📊 Project Analytics',
        'dashboard.period.week': 'Week',
        'dashboard.period.month': 'Month',
        'dashboard.period.quarter': 'Quarter',
        'dashboard.stat.pendingTasks': 'Pending Tasks',
        'dashboard.stat.inProgress': 'In Progress',
        'dashboard.stat.highPriority': 'High Priority',
        'dashboard.stat.overdue': 'Overdue',
        'dashboard.stat.completed': 'Completed',
        'dashboard.stat.projects': 'Projects',
        'dashboard.highPriorityHint': 'High priority tasks due within 7 days (or overdue)',
        'dashboard.projectProgress': '🌊 Project Progress',
        'dashboard.legend.todo': 'To Do',
        'dashboard.legend.progress': 'In Progress',
        'dashboard.legend.review': 'In Review',
        'dashboard.legend.complete': 'Complete',
        'dashboard.viewAll': 'View All',
        'dashboard.quickActions': '⚡ Quick Actions',
        'dashboard.action.generateReport': 'Generate report',
        'dashboard.action.generateReportTitle': 'Open a status report of your projects and tasks, ready to download as PDF or save to Docs',
        'report.summary.toast.generating': 'Generating report…',
        'report.summary.toast.created': 'Report created in Docs',
        'report.summary.toast.failed': 'Could not create the report: {error}',
        'report.summary.toast.noPage': 'Docs did not return the new page',
        'report.summary.docTitle': 'Status report · {date}',
        'report.summary.meta': 'Data as of {date} · {projects} projects · {tasks} tasks',
        'report.summary.common.none': '—',
        'report.summary.common.andMore': '{names} and {count} more',
        'report.summary.kpis.heading': 'Key figures',
        'report.summary.kpis.colMetric': 'Metric',
        'report.summary.kpis.colValue': 'Value',
        'report.summary.kpis.colDetail': 'Detail',
        'report.summary.kpis.tasks': 'Tasks',
        'report.summary.kpis.tasksDetail': '{count} created this month',
        'report.summary.kpis.completed': 'Completed',
        'report.summary.kpis.countPct': '{count} ({pct}%)',
        'report.summary.kpis.completedDetail': '{count} completed this month',
        'report.summary.kpis.inProgress': 'In progress',
        'report.summary.kpis.inProgressDetail': 'Includes tasks in review · across {count} projects',
        'report.summary.kpis.overdue': 'Overdue tasks',
        'report.summary.kpis.overdueDetail': '{pct}% of open tasks',
        'report.summary.kpis.lateProjects': 'Late projects',
        'report.summary.kpis.lateProjectsDetail': 'End date passed with tasks still open',
        'report.summary.kpis.nextMilestone': 'Next milestone',
        'report.summary.kpis.daysLeft': '{days} days',
        'report.summary.kpis.noMilestone': 'No project has an upcoming end date',
        'report.summary.highlights.heading': 'Highlights',
        'report.summary.highlights.lateOne': '{name} passed its end date {days} days ago and still has {open} open tasks.',
        'report.summary.highlights.lateMany': '{count} projects passed their end date and still have {open} open tasks: {names}.',
        'report.summary.highlights.behindOne': '{name} has used {time}% of its time but completed only {done}% of its tasks.',
        'report.summary.highlights.behindMany': '{count} projects are behind schedule: {names}.',
        'report.summary.highlights.overdue': '{count} tasks are overdue; the oldest is {days} days late.',
        'report.summary.highlights.startOne': '{name} starts on {date} with {tasks} tasks.',
        'report.summary.highlights.startMany': '{count} projects start in the next {days} days, with {tasks} tasks in total.',
        'report.summary.highlights.milestone': 'Next milestone: {name} on {date} ({days} days left).',
        'report.summary.highlights.allClear': 'No overdue tasks and no project behind schedule.',
        'report.summary.state.late': 'Late',
        'report.summary.state.behind': 'Behind',
        'report.summary.state.onTrack': 'On track',
        'report.summary.state.ahead': 'Ahead',
        'report.summary.state.notStarted': 'Not started',
        'report.summary.state.noDates': 'No dates',
        'report.summary.state.noTasks': 'No tasks',
        'report.summary.state.done': 'Completed',
        'report.summary.projects.heading': 'Projects',
        'report.summary.projects.intro': 'Projects that need attention come first. Time is the share of the project’s duration that has already passed.',
        'report.summary.projects.empty': 'There are no active projects.',
        'report.summary.projects.colProject': 'Project',
        'report.summary.projects.colState': 'State',
        'report.summary.projects.colCompleted': 'Completed',
        'report.summary.projects.colProgress': 'Progress',
        'report.summary.projects.colTime': 'Time',
        'report.summary.projects.colEnd': 'End',
        'report.summary.projects.doneOf': '{done} of {total}',
        'report.summary.projects.lateBy': '{days} days late',
        'report.summary.projects.unassigned': 'No project',
        'report.summary.projects.archived': 'Archived projects',
        'report.summary.throughput.heading': 'Work pace',
        'report.summary.throughput.intro': 'Tasks created and completed each month. {month} counts up to {date}.',
        'report.summary.throughput.colMonth': 'Month',
        'report.summary.throughput.colCreated': 'Created',
        'report.summary.throughput.colCompleted': 'Completed',
        'report.summary.throughput.colOpen': 'Not done at month end',
        'report.summary.throughput.pace': 'Over the last 3 full months, an average of {avg} tasks were completed per month. At that pace, the {remaining} unfinished tasks would take about {months} months.',
        'report.summary.throughput.noPace': 'No tasks were completed in the last 3 full months, so there is no pace to project.',
        'report.summary.deadlines.heading': 'Deadlines',
        'report.summary.deadlines.agingHeading': 'Overdue tasks by delay',
        'report.summary.deadlines.noOverdue': 'No overdue tasks.',
        'report.summary.deadlines.colDelay': 'Delay',
        'report.summary.deadlines.colTasks': 'Tasks',
        'report.summary.deadlines.bucket.1-7': '1–7 days',
        'report.summary.deadlines.bucket.8-14': '8–14 days',
        'report.summary.deadlines.bucket.15-30': '15–30 days',
        'report.summary.deadlines.bucket.31+': 'More than 30 days',
        'report.summary.deadlines.upcomingHeading': 'Next {days} days',
        'report.summary.deadlines.upcomingEmpty': 'Nothing scheduled.',
        'report.summary.deadlines.projectStart': '{name} starts · {count} tasks',
        'report.summary.deadlines.projectEnd': '{name} ends · {count} unfinished tasks',
        'report.summary.deadlines.more': '{count} more not shown.',
        'report.summary.attention.heading': 'Needs attention',
        'report.summary.attention.intro': 'Open tasks past their end date, most delayed first. Tasks with the same title in the same project are grouped.',
        'report.summary.attention.empty': 'Nothing is overdue.',
        'report.summary.attention.colTask': 'Task',
        'report.summary.attention.colProject': 'Project',
        'report.summary.attention.colStatus': 'Status',
        'report.summary.attention.colPriority': 'Priority',
        'report.summary.attention.colOverdue': 'Overdue',
        'report.summary.attention.days': '{days} days',
        'report.summary.attention.more': 'Showing the {shown} most delayed of {total}.',
        'report.summary.method.heading': 'How this report is calculated',
        'report.summary.method.body': 'Data as of {date}. Progress = completed tasks / project tasks. Time = days elapsed / project duration. A project is behind when its progress is more than {tolerance} points below its time, and late when its end date has passed with tasks still open. A task is overdue when it is open (not done and not in backlog) and its end date has passed. Archived tasks and recurring templates are not counted.',
        'report.summary.quality.heading': 'Data quality',
        'report.summary.quality.none': 'No gaps found.',
        'report.summary.quality.projectsWithoutTasks': 'Projects without tasks',
        'report.summary.quality.projectsWithoutDates': 'Projects without a start or end date',
        'report.summary.quality.tasksWithoutProject': 'Tasks without a project',
        'report.summary.quality.tasksInArchivedProjects': 'Tasks in archived projects',
        'report.summary.quality.doneWithoutCompletedDate': 'Completed tasks without a completion date (left out of the work pace)',
        'report.summary.quality.throughputHidden': 'Work pace is not shown because there is less than 2 months of activity.',
        'report.view.toolbarTitle': 'Status report',
        'report.view.back': 'Back',
        'report.view.refresh': 'Refresh',
        'report.view.link.tasks': 'Open in Tasks',
        'report.view.link.projects': 'Open in Projects',
        'report.view.refreshing': 'Refreshing…',
        'report.view.refreshHint': 'Get the latest data from the server and recalculate the report',
        'report.view.toast.updated': 'Report updated with the latest data',
        'report.view.toast.upToDate': 'The report was already up to date',
        'report.view.toast.offline': 'Couldn’t get new data. The report uses the data already loaded.',
        'report.view.meta.dateTime': '{date}, {time}',
        'report.view.saveDocs': 'Save to Docs',
        'report.view.downloadPdf': 'Download PDF',
        'report.view.pdfHint': 'Opens the print dialog. Choose “Save as PDF” as the destination.',
        'report.view.eyebrow': 'Status report · Nautilus',
        'report.view.defaultTitle': 'All projects',
        'report.view.titleHint': 'Click to rename this report',
        'report.view.meta.asOf': 'Data as of',
        'report.view.meta.period': 'Period',
        'report.view.meta.counts': '{projects} projects · {tasks} tasks',
        'report.view.kpi.createdMonth': '+{count} this month',
        'report.view.kpi.completedMonth': '{count} this month',
        'report.view.kpi.inProjects': 'in {count} projects',
        'report.view.kpi.lateProjectsNote': 'end date passed, tasks open',
        'report.view.kpi.days': 'days',
        'report.view.highlights.source': 'Calculated from your data',
        'report.view.portfolio.heading': 'Status by project',
        'report.view.portfolio.sub': 'Tasks by status. Same scale on every row.',
        'report.view.portfolio.ends': 'ends {date}',
        'report.view.portfolio.tip': '{status}: {count} of {total}',
        'report.view.portfolio.clickHint': 'Click to see these tasks',
        'report.view.portfolio.activeOnly': 'Only projects in progress are shown.',
        'report.view.portfolio.hidden': '{count} projects in planning or backlog are hidden: all their tasks are to do or in backlog, or they have no tasks.',
        'report.view.portfolio.hiddenOne': '1 project in planning or backlog is hidden: all its tasks are to do or in backlog, or it has no tasks.',
        'report.view.portfolio.noneActive': 'No project is in progress yet.',
        'report.view.pace.heading': 'Progress vs. time',
        'report.view.pace.sub': 'Each dot is a project. Below the diagonal means slower than its schedule requires.',
        'report.view.pace.empty': 'No project has both a start and an end date, so progress can’t be compared with time.',
        'report.view.pace.diagonal': 'on pace',
        'report.view.pace.zone': 'slower than schedule',
        'report.view.pace.axisX': 'Time elapsed',
        'report.view.pace.axisY': 'Tasks completed',
        'report.view.pace.joinTwo': '{a} and {b}',
        'report.view.pace.many': '{count} projects',
        'report.view.pace.lateTag': 'late · {days} d',
        'report.view.pace.tip': '{done}% completed · {time}% of the time',
        'report.view.pace.ahead': '{name} is ahead of schedule ({done}% done, {time}% of the time).',
        'report.view.pace.outsideNote': 'Only projects that have started, with a start date, an end date and at least one task, are plotted.',
        'report.view.pace.excluded': '{count} projects are excluded: {parts}.',
        'report.view.pace.excludedOne': '1 project is excluded: {parts}.',
        'report.view.pace.outsideCount.notStarted': '{count} not started yet',
        'report.view.pace.outsideCount.noDates': '{count} missing a date',
        'report.view.pace.outsideCount.noTasks': '{count} without tasks',
        'report.view.timeline.heading': 'Timeline',
        'report.view.timeline.sub': 'Fill = % completed. Vertical line = cut date.',
        'report.view.timeline.empty': 'No project has both a start and an end date.',
        'report.view.timeline.noEnd': 'no end date',
        'report.view.timeline.late': '{pct}% · {days} d late',
        'report.view.timeline.endsLater': '{pct} · ends {date} →',
        'report.view.timeline.tipDone': '{pct}% completed ({done} of {total})',
        'report.view.timeline.more': 'Showing {shown} of {total} projects.',
        'report.view.timeline.datedOnly': 'Only projects with a start and an end date are shown.',
        'report.view.timeline.excluded': '{count} projects missing at least one of these dates are excluded.',
        'report.view.timeline.excludedOne': '1 project missing at least one of these dates is excluded.',
        'report.view.throughput.sub': 'Cumulative tasks: created and completed per month.',
        'report.view.throughput.gapLabel': 'unfinished',
        'report.view.throughput.tipCum': 'Cumulative: {created} created, {completed} completed',
        'report.view.throughput.tipMonth': 'This month: {created} created, {completed} completed',
        'report.view.throughput.partial': '* {month} up to day {day}.',
        'report.view.throughput.peak': '{count} tasks were created in {month}. The gap between the two lines is the pending work.',
        'report.view.throughput.gap': 'The gap between the two lines is the pending work.',
        'report.view.throughput.avgBig': '≈{avg} / month',
        'report.view.throughput.avgNote': 'completed on average over the last 3 full months',
        'report.view.throughput.remainingBig': '{count} unfinished',
        'report.view.throughput.remainingNote': 'about {months} months of work at the current pace',
        'report.view.throughput.remainingNoPace': 'no recent pace to project',
        'report.view.deadlines.agingTip': '{count} overdue tasks',
        'report.view.deadlines.none': 'none',
        'dashboard.action.addTask': 'Add Task',
        'dashboard.action.viewCalendar': 'View Calendar',
        'dashboard.action.newProject': 'New Project',
        'dashboard.quickActions.openTasks': 'Open Tasks',
        'dashboard.quickActions.openProjects': 'Open Projects',
        'dashboard.recentActivity': 'Recent activity',
        'dashboard.researchInsights': '🧠 Insights',
        'dashboard.activity.emptyTitle': 'Welcome to your Dashboard!',
        'dashboard.activity.emptySubtitle': 'Start creating projects and tasks to see activity',
        'dashboard.activity.completed': 'Completed "{title}" {projectPart}',
        'dashboard.activity.createdProject': 'Created new project "{project}"',
        'dashboard.activity.addedTask': 'Added new task "{title}" {projectPart}',
        'dashboard.activity.completedAction': 'Completed',
        'dashboard.activity.createdProjectAction': 'Created project',
        'dashboard.activity.addedTaskAction': 'Added task',
        'dashboard.activity.updatedDocAction': 'Updated doc',
        'dashboard.activity.inLabel': 'in',
        'dashboard.activity.toLabel': 'to',
        'dashboard.activity.inProject': 'in {project}',
        'dashboard.activity.toProject': 'to {project}',
        'dashboard.activity.recently': 'Recently',
        'dashboard.activity.justNow': 'Just now',
        'dashboard.activity.today': 'Today',
        'dashboard.activity.yesterday': 'Yesterday',
        'dashboard.activity.daysAgoShort': '{count}d ago',
        'dashboard.activity.minuteAgo': '{count} minute ago',
        'dashboard.activity.minutesAgo': '{count} minutes ago',
        'dashboard.activity.hourAgo': '{count} hour ago',
        'dashboard.activity.hoursAgo': '{count} hours ago',
        'dashboard.activity.dayAgo': '{count} day ago',
        'dashboard.activity.daysAgo': '{count} days ago',
        'dashboard.trend.thisWeek': 'this week',
        'dashboard.trend.thisMonth': 'this month',
        'dashboard.trend.thisQuarter': 'this quarter',
        'dashboard.trend.dueTodayOne': '{count} due today',
        'dashboard.trend.dueTodayMany': '{count} due today',
        'dashboard.trend.onTrack': 'On track',
        'dashboard.trend.needsAttention': 'Needs attention',
        'dashboard.trend.allOnTrack': 'All on track',
        'dashboard.trend.criticalOne': '{count} critical',
        'dashboard.trend.criticalMany': '{count} critical',
        'dashboard.trend.completedOne': '{count} completed',
        'dashboard.trend.completedMany': '{count} completed',
        'dashboard.trend.inProgress': 'In progress',
        'dashboard.emptyProjects.title': 'No projects yet',
        'dashboard.emptyProjects.subtitle': 'Create your first project to see progress visualization',
        'dashboard.tasks': 'tasks',
        'dashboard.activity.allTitle': 'All Recent Activity',
        'dashboard.activity.allSubtitle': 'Full history of your recent work',
        'dashboard.activity.backToDashboard': '← Back to Dashboard',
        'dashboard.insights.excellentTitle': 'Excellent Progress',
        'dashboard.insights.excellentDesc': '{percent}% completion rate exceeds target. Great momentum!',
        'dashboard.insights.goodTitle': 'Good Progress',
        'dashboard.insights.goodDesc': '{percent}% completion rate is solid. Consider pushing to reach 80% target.',
        'dashboard.insights.opportunityTitle': 'Progress Opportunity',
        'dashboard.insights.opportunityDesc': '{percent}% completion rate. Focus on completing current tasks to build momentum.',
        'dashboard.insights.actionTitle': 'Action Needed',
        'dashboard.insights.actionDesc': '{percent}% completion rate is low. Break down large tasks and tackle smaller ones first.',
        'dashboard.insights.todayTitle': 'Today\'s Focus',
        'dashboard.insights.todayDescOne': '{count} task is due today. Prioritize it for maximum impact.',
        'dashboard.insights.todayDescMany': '{count} tasks are due today. Prioritize them for maximum impact.',
        'dashboard.insights.overdueTitle': 'Overdue Items',
        'dashboard.insights.overdueDescOne': '{count} overdue task needs attention. Address it to prevent delays.',
        'dashboard.insights.overdueDescMany': '{count} overdue tasks need attention. Address these to prevent delays.',
        'dashboard.insights.highPriorityTitle': 'High Priority Focus',
        'dashboard.insights.highPriorityDescOne': '{count} high-priority task needs immediate attention.',
        'dashboard.insights.highPriorityDescMany': '{count} high-priority tasks need immediate attention.',
        'dashboard.insights.emptyProjectsTitle': 'Empty Projects',
        'dashboard.insights.emptyProjectsDescOne': '{count} project has no tasks yet. Add tasks to track progress.',
        'dashboard.insights.emptyProjectsDescMany': '{count} projects have no tasks yet. Add tasks to track progress.',
        'dashboard.insights.momentumTitle': 'Strong Momentum',
        'dashboard.insights.momentumDescOne': '{count} task completed this week. You\'re in a productive flow!',
        'dashboard.insights.momentumDescMany': '{count} tasks completed this week. You\'re in a productive flow!',
        'dashboard.insights.readyTitle': 'Ready to Start',
        'dashboard.insights.readyDesc': 'Create your first project and add some tasks to begin tracking your progress.',
        'dashboard.insights.caughtUpTitle': 'All Caught Up',
        'dashboard.insights.caughtUpDesc': 'Great work! No urgent items detected. Consider planning your next milestones.'
    },
    es: {
        'boot.loading': 'Cargando Nautilus',
        'lock.welcome': 'Bienvenido de nuevo',
        'lock.subtitle': 'Ingresa la contraseña de acceso para continuar.',
        'lock.sessionHint': '? La sesión permanece iniciada por 24 horas.',
        'lock.passwordLabel': 'Contraseña',
        'lock.passwordPlaceholder': 'Ingresa la contraseña',
        'lock.unlock': 'Desbloquear',
        'auth.backToUserLogin': 'Volver al inicio de sesión',
        'auth.login.title': 'Bienvenido a Nautilus',
        'auth.login.subtitle': 'Inicia sesión para acceder a tu espacio',
        'auth.login.identifierLabel': 'Usuario o email',
        'auth.login.identifierPlaceholder': 'Ingresa usuario o correo',
        'auth.login.pinLabel': 'PIN (4 dígitos)',
        'auth.admin.title': 'Acceso de administración',
        'auth.admin.passwordLabel': 'Contraseña de administrador',
        'auth.admin.passwordRequired': 'Introduce la contraseña de administrador',
        'auth.admin.opening': 'Abriendo el panel de administración…',
        'auth.setup.title': 'Completa tu configuración',
        'auth.setup.subtitle': 'Personaliza tu cuenta',
        'auth.setup.usernameLabel': 'Usuario',
        'auth.setup.usernamePlaceholder': 'p. ej., alex',
        'auth.setup.usernameHint': '3-20 caracteres, solo letras minúsculas y números',
        'auth.setup.displayNameLabel': 'Nombre visible',
        'auth.setup.displayNamePlaceholder': 'p. ej., Alex Morgan',
        'auth.setup.emailLabel': 'Correo electrónico',
        'auth.setup.emailPlaceholder': 'tu.correo@ejemplo.com',
        'auth.setup.emailHint': 'Necesario para notificaciones y confirmación de eliminación de cuenta',
        'auth.setup.newPinLabel': 'Nuevo PIN (4 dígitos)',
        'auth.setup.confirmPinLabel': 'Confirmar PIN',
        'auth.setup.next': 'Siguiente',
        'auth.setup.submit': 'Completar configuración',
        'admin.title': 'Gestión de usuarios',
        'admin.signOut': 'Cerrar sesión',
        'admin.usersTitle': 'Usuarios',
        'admin.createUserTitle': 'Crear nuevo usuario',
        'admin.usernameLabel': 'Usuario',
        'admin.usernamePlaceholder': 'p. ej., jdoe',
        'admin.usernameHint': '3-20 caracteres, solo letras minúsculas y números',
        'admin.displayNameLabel': 'Nombre visible',
        'admin.displayNamePlaceholder': 'p. ej., John Doe',
        'admin.tempPinLabel': 'PIN temporal (4 dígitos)',
        'admin.createUserButton': 'Crear usuario',
        'auth.admin.resetPinPrompt': 'Ingresa un nuevo PIN temporal para {userName}:',
        'auth.admin.pinMustBe4Digits': 'El PIN debe tener exactamente 4 dígitos',
        'auth.admin.resetPinFailed': 'No se pudo restablecer el PIN',
        'auth.admin.resetPinSuccess': 'PIN restablecido para {userName}. Nuevo PIN temporal: {pin}',
        'auth.admin.deleteUserConfirm': '¿Seguro que deseas eliminar al usuario \"{userName}\"? Esto también eliminará todas sus tareas y proyectos.',
        'auth.admin.deleteUserFailed': 'No se pudo eliminar el usuario',
        'auth.admin.deleteUserSuccess': 'Usuario \"{userName}\" eliminado correctamente',
        'auth.login.passwordLabel': 'Contraseña',
        'auth.login.passwordPlaceholder': 'Ingresa tu contraseña',
        'auth.login.forgotCredential': '¿Olvidaste tu contraseña o PIN?',
        'auth.login.submit': 'Entrar',
        'auth.login.brandTag': 'GESTOR DE TAREAS',
        'auth.login.workspaceSubtitle': 'Inicia sesión en tu espacio de trabajo',
        'auth.login.identifierExample': 'tu@instituto.org',
        'auth.login.pinProgress': '{count} de {total} dígitos introducidos',
        'auth.login.keypadDelete': 'Borrar último dígito',
        'auth.login.keypadEnter': 'Entrar',
        'auth.login.opening': 'Abriendo tu espacio de trabajo…',
        'auth.login.identifierRequired': 'Introduce tu usuario o email',
        'auth.login.passwordRequired': 'Introduce tu contraseña',
        'auth.login.pinIncorrect': 'PIN incorrecto',
        'auth.login.passwordIncorrect': 'Contraseña incorrecta',
        'auth.login.tooManyAttempts': 'Demasiados intentos. Inténtalo más tarde.',
        'auth.login.failed': 'No se pudo iniciar sesión. Inténtalo de nuevo.',
        'auth.login.creature.region': 'Criatura marina destacada',
        'auth.login.creature.counter': 'CRIATURA DEL DÍA',
        'auth.login.creature.prev': 'Criatura anterior',
        'auth.login.creature.next': 'Criatura siguiente',
        'auth.login.creature.learnMore': 'Saber más sobre esta especie',
        'auth.login.creature.imageAlt': 'Ilustración: {name}',
        'auth.login.creature.watchVideo': 'Ver vídeo',
        'auth.login.creature.closeVideo': 'Cerrar vídeo',
        'auth.login.creature.videoTitle': 'Vídeo de la criatura destacada',
        'auth.setup.authMethodLabel': 'Método de autenticación',
        'auth.setup.methodPin': 'PIN',
        'auth.setup.methodPassword': 'Contraseña',
        'auth.setup.newPasswordLabel': 'Contraseña',
        'auth.setup.passwordPlaceholder': '8+ caract., 1 mayús., 1 minús., 1 dígito',
        'auth.setup.newPasswordPlaceholder': '8+ caract., 1 mayús., 1 minús., 1 dígito',
        'auth.setup.confirmPasswordLabel': 'Confirmar contraseña',
        'auth.setup.confirmPasswordPlaceholder': 'Repetir contraseña',
        'auth.setup.passwordRequirements': '8+ caracteres, mayúscula, minúscula y un dígito',
        'auth.forgot.title': 'Restablecer inicio de sesión',
        'auth.forgot.subtitle': 'Ingresa tu correo para recibir un enlace de restablecimiento',
        'auth.forgot.emailLabel': 'Correo electrónico',
        'auth.forgot.emailPlaceholder': 'tu.correo@ejemplo.com',
        'auth.forgot.submit': 'Enviar enlace',
        'auth.forgot.errorEmpty': 'Ingresa tu correo electrónico',
        'auth.forgot.errorInvalidEmail': 'Ingresa un correo electrónico válido',
        'auth.forgot.successSentTo': 'Enlace enviado a',
        'auth.forgot.successCheckInbox': 'Revisa tu bandeja de entrada (y carpeta de spam).',
        'auth.forgot.successExpiry': 'El enlace expira en 1 hora.',
        'auth.forgot.backToLogin': 'Volver al inicio de sesión',
        'auth.forgot.sending': 'Enviando…',
        'auth.forgot.errorGeneric': 'Algo salió mal. Inténtalo de nuevo.',
        'auth.backToLogin': 'Volver al inicio de sesión',
        'auth.reset.title': 'Nueva credencial',
        'auth.reset.subtitle': 'Elige tu método de autenticación y establece una nueva credencial',
        'auth.reset.methodPassword': 'Contraseña',
        'auth.reset.methodPin': 'PIN',
        'auth.reset.submit': 'Restablecer credencial',
        'auth.reset.success': '¡Credencial restablecida! Redirigiendo al inicio de sesión...',
        'auth.reset.newPasswordLabel': 'Nueva contraseña',
        'auth.reset.confirmPasswordLabel': 'Confirmar contraseña',
        'auth.setup.passwordHint': '8+ caracteres, 1 mayúscula, 1 minúscula, 1 número',
        'common.cancel': 'Cancelar',
        'common.clear': 'Borrar',
        'common.uploadingFile': 'Subiendo {name}...',
        'common.uploadingFileProgress': 'Subiendo {name}… {progress}%',
        'common.delete': 'Eliminar',
        'common.continue': 'Continuar',
        'common.gotIt': 'Entendido',
        'common.close': 'Cerrar modal',
        'common.done': 'Listo',
        'common.updating': 'Actualizando...',
        'common.nautilusLogoAlt': 'Logo de Nautilus',
        'common.devBanner': '⚠️ DESARROLLO LOCAL - NO PRODUCCIÓN ⚠️',
        'common.datePlaceholder': datePlaceholder('es'),
        'crop.title': 'Recortar imagen a cuadrado',
        'crop.instructions': 'Arrastra para ajustar el área de recorte. La imagen se recortará a un cuadrado.',
        'crop.apply': 'Recortar y aplicar',
        'crop.close': 'Cerrar recorte',
        'dashboard.insights.productivityTitle': 'Tendencia de productividad',
        'dashboard.insights.productivityDesc': 'La tasa de finalización de tareas aumentó un 23% respecto al mes pasado',
        'dashboard.insights.deadlineTitle': 'Alerta de plazos',
        'dashboard.insights.deadlineDesc': '3 hitos vencen en los próximos 7 días',
        'projects.title': 'Proyectos',
        'projects.subtitle': 'Gestiona tus proyectos',
        'projects.archived.title': 'Proyectos archivados',
        'projects.archived.subtitle': 'Los proyectos archivados se ocultan de las vistas activas hasta que se restauran.',
        'projects.archived.back': '← Volver a proyectos',
        'projects.archived.colProgress': 'Progreso',
        'projects.archived.selectAll': 'Seleccionar todos los proyectos archivados',
        'projects.archived.selected': '{count} de {total} proyectos seleccionados',
        'projects.archived.emptyTitle': 'No hay proyectos archivados',
        'projects.archived.emptySubtitle': 'Los proyectos que archives aparecerán aquí.',
        'projects.archived.noResults': 'Ningún proyecto archivado coincide con tus filtros',
        'projects.archived.noResultsSubtitle': 'Prueba a ajustar la búsqueda o los filtros.',
        'projects.archived.bulkRestored': '{count} proyectos restaurados',
        'projects.searchPlaceholder': '\u{1F50D} Buscar proyectos (título o descripción)',
        'projects.filters.status': 'Estado',
        'projects.filters.statusTitle': 'Estado del proyecto',
        'projects.filters.archived': 'Archivados',
        'projects.status.backlog': 'BACKLOG',
        'projects.status.planning': 'PLANIFICACIÓN',
        'projects.status.active': 'ACTIVO',
        'projects.status.completed': 'COMPLETADO',
        'projects.status.archived': 'ARCHIVADO',
        'projects.archive.title': 'Archivar proyecto',
        'projects.archive.modalTitle': 'Archivar proyecto',
        'projects.archive.modalTitleWithName': 'Archivar proyecto: {{name}}',
        'projects.archive.intro': 'Archivar oculta este proyecto de las vistas activas. No se elimina nada y podrás restaurarlo más tarde.',
        'projects.archive.summary.open': 'Abiertas',
        'projects.archive.summary.completed': 'Completadas',
        'projects.archive.summary.archived': 'Ya archivadas',
        'projects.archive.option.projectOnly': 'Archivar solo el proyecto',
        'projects.archive.option.projectOnlyHint': 'Las tareas seguirán visibles en las vistas activas.',
        'projects.archive.option.completedChildren': 'Archivar el proyecto y las tareas completadas',
        'projects.archive.option.completedChildrenHint': 'Solo se archivan tareas en estado Hecho. Las tareas abiertas permanecen activas.',
        'projects.archive.option.allChildren': 'Archivar el proyecto y todas las tareas',
        'projects.archive.option.allChildrenHint': 'Oculta todas las tareas no archivadas de este proyecto. El estado nunca cambia.',
        'projects.archive.recommended': '(recomendado)',
        'projects.archive.confirm': 'Archivar',
        'projects.restore.title': 'Restaurar proyecto',
        'projects.restore.modalTitle': 'Restaurar proyecto',
        'projects.restore.modalTitleWithName': 'Restaurar proyecto: {{name}}',
        'projects.restore.hasArchived': 'Este proyecto tiene',
        'projects.restore.archivedTasks': 'tareas archivadas.',
        'projects.restore.option.projectOnly': 'Restaurar solo el proyecto',
        'projects.restore.option.withChildren': 'Restaurar el proyecto y sus tareas archivadas',
        'projects.restore.confirm': 'Restaurar',
        'projects.bulkRestore.modalTitle': 'Restaurar proyectos',
        'projects.bulkRestore.intro': 'Los proyectos seleccionados tienen',
        'projects.bulkRestore.option.projectsOnly': 'Restaurar solo los proyectos',
        'projects.bulkRestore.option.withChildren': 'Restaurar los proyectos y sus tareas archivadas',
        'projects.filters.updated': 'Actualizado',
        'projects.filters.updatedTitle': 'Actualizados recientemente',
        'projects.filters.completion': 'Progreso',
        'projects.filters.completionTitle': 'Tareas completadas',
        'projects.filters.completion100': '100% completado',
        'projects.filters.completion75': '75% o más',
        'projects.filters.completion50': '50% o más',
        'projects.filters.completionUnder50': 'Menos del 50%',
        'projects.filters.completion0': 'Sin empezar (0%)',
        'projects.filters.all': 'Todos',
        'projects.filters.last5m': 'Últimos 5 minutos',
        'projects.filters.last30m': 'Últimos 30 minutos',
        'projects.filters.last24h': 'Últimas 24 horas',
        'projects.filters.lastWeek': 'Última semana',
        'projects.filters.lastMonth': 'Último mes',
        'projects.filters.hasTasks': 'Con tareas',
        'projects.filters.noTasks': 'Sin tareas',
        'projects.filters.generatedChip': 'Recién generados',
        'projects.filters.reportChip': 'Del reporte',
        'projects.viewToggle.cards': 'Vista de tarjetas',
        'projects.viewToggle.rows': 'Vista de lista',
        'projects.filters.tags': 'Etiquetas',
        'projects.filters.tagsTitle': 'Etiquetas de proyectos',
        'projects.filters.clear': 'Limpiar filtros',
        'projects.noDescription': 'Sin descripcion',
        'projects.card.tasksProgress': '{done} de {total} tareas',
        'projects.card.noStart': 'Sin inicio',
        'projects.card.noEnd': 'Sin fin',
        'projects.card.overdueOne': 'Vencido hace 1 día',
        'projects.card.overdueDays': 'Vencido hace {count} días',
        'projects.card.dueToday': 'Vence hoy',
        'projects.card.dueInOne': 'en 1 día',
        'projects.card.dueInDays': 'en {count} días',
        'projects.card.showTasks': 'Ver tareas',
        'projects.card.hideTasks': 'Ocultar tareas',
        'projects.card.tasksTitle': 'Tareas',
        'projects.card.pendingDone': '{pending} pendientes · {done} hechas',
        'projects.card.col.task': 'Tarea',
        'projects.card.col.date': 'Fecha',
        'projects.card.col.priority': 'Prioridad',
        'projects.card.col.status': 'Estado',
        'projects.tasksBreakdown': '{total} tareas · {done} hechas',
        'tasks.card.description': 'Descripcion',
        'projects.card.tasksCount': '{count} tareas',
        'projects.card.percentDone': '{count}% hecho',
        'projects.sort.label': 'Ordenar: Estado',
        'projects.expandAll': 'Expandir todo',
        'projects.collapseAll': 'Contraer todo',
        'projects.expandAllTitle': 'Expandir todos los proyectos',
        'projects.collapseAllTitle': 'Contraer todos los proyectos',
        'projects.sort.status': 'Estado',
        'projects.sort.name': 'Nombre',
        'projects.sort.newest': 'Más reciente',
        'projects.sort.lastUpdated': 'Última actualización',
        'projects.sort.mostTasks': 'Más tareas',
        'projects.sort.percentCompleted': '% completado',
        'projects.sort.prefix': 'Ordenar: {label}',
        'projects.sort.help': 'Repite la misma opción para alternar Asc/Desc.',
        'projects.sort.statusLabel': 'Estado',
        'projects.sort.nameLabel': 'Nombre',
        'projects.sort.newestLabel': 'Más reciente',
        'projects.sort.lastUpdatedLabel': 'Última actualización',
        'projects.sort.mostTasksLabel': 'Más tareas',
        'projects.sort.percentCompletedLabel': '% completado',
        'projects.newProjectButton': '+ Nuevo proyecto',
        'projects.openDetailsTitle': 'Ver detalles',
        'projects.empty.title': 'Aún no hay proyectos',
        'projects.empty.subtitle': 'Crea tu primer proyecto',
        'projects.empty.searchTitle': 'No se encontraron proyectos',
        'projects.empty.searchSubtitle': 'Crea un nuevo proyecto para empezar',
        'projects.empty.filteredTitle': 'No hubo coincidencias de proyectos',
        'projects.modal.createTitle': 'Crear nuevo proyecto',
        'projects.modal.nameLabel': 'Nombre del proyecto',
        'projects.modal.descriptionLabel': 'Descripción',
        'projects.modal.startDateLabel': 'Fecha de inicio',
        'projects.modal.endDateLabel': 'Fecha de fin',
        'projects.modal.tagsLabel': 'Etiquetas',
        'projects.modal.addTagPlaceholder': 'Añadir etiqueta',
        'projects.modal.createButton': 'Crear proyecto',
        'projects.delete.title': 'Eliminar proyecto',
        'projects.delete.body': 'Esta acción no se puede deshacer. Para confirmar, escribe delete abajo:',
        'projects.delete.deleteTasksLabel': 'Eliminar todas las tareas de este proyecto',
        'projects.delete.deleteTasksHint': 'Si no se marca, las tareas se desasignarán del proyecto',
        'projects.delete.inputPlaceholder': 'Escribe delete aquí',
        'projects.delete.error': 'Escribe \"delete\" exactamente para confirmar',
        'projects.duplicate.title': 'Duplicar proyecto',
        'projects.duplicate.nameLabel': 'Nombre del nuevo proyecto',
        'projects.duplicate.defaultName': '{name} (copia)',
        'projects.duplicate.includeTasksLabel': 'Incluir tareas',
        'projects.duplicate.includeTasksHint': 'Los estados vuelven a Por hacer · las tareas archivadas se omiten',
        'projects.duplicate.startDateLabel': 'Fecha de inicio',
        'projects.duplicate.startDateHint': 'Cámbiala para mover todas las fechas de las tareas por igual.',
        'projects.duplicate.prefixPlaceholder': 'ej., Copia - ',
        'projects.duplicate.suffixPlaceholder': 'ej., (Copia)',
        'projects.duplicate.confirm': 'Duplicar proyecto',
        'projects.duplicate.success': 'Proyecto "{name}" duplicado exitosamente',
        'projects.duplicate.successWithTasks': 'Proyecto "{name}" y sus tareas duplicados exitosamente',
        'projects.template.saveAsTemplate': 'Guardar como plantilla',
        'projects.template.modalTitle': 'Guardar como plantilla',
        'projects.template.nameLabel': 'Nombre de plantilla',
        'projects.template.includeTasksLabel': 'Incluir tareas',
        'projects.template.includeTasksHint': 'Copia todas las tareas · estados reiniciados a Pendiente · fechas eliminadas',
        'projects.template.saveButton': 'Guardar plantilla',
        'projects.template.savedSuccess': 'Plantilla guardada',
        'projects.template.deletedSuccess': 'Plantilla eliminada',
        'projects.template.renamedSuccess': 'Plantilla renombrada',
        'projects.template.deleteModalTitle': '¿Eliminar plantilla?',
        'projects.template.renameModalTitle': 'Renombrar plantilla',
        'projects.template.newNameLabel': 'Nuevo nombre',
        'projects.template.renameButton': 'Renombrar',
        'projects.template.taskCountOne': '{count} tarea',
        'projects.template.taskCountMany': '{count} tareas',
        'projects.details.dragToReorder': 'Arrastrar para reordenar',
        'projects.modal.searchProjects': 'Buscar proyectos...',
        'projects.modal.templateLabel': 'Empezar desde plantilla',
        'projects.modal.templateNone': 'Ninguna',
        'projects.modal.taskNaming': 'Nomenclatura',
        'projects.modal.namingOriginal': 'Original',
        'projects.modal.namingAddPrefix': '+ Prefijo',
        'projects.modal.namingAddSuffix': '+ Sufijo',
        'projects.statusInfo.title': 'Cómo funciona el estado del proyecto',
        'projects.statusInfo.subtitle': 'El estado del proyecto se determina en base al progreso de las tareas.',
        'projects.statusInfo.backlogBadge': 'BACKLOG',
        'projects.statusInfo.backlogDesc': 'No hay tareas aún, o todas están en \"Backlog\". Nada ha sido planificado.',
        'projects.statusInfo.planningBadge': 'PLANIFICACIÓN',
        'projects.statusInfo.planningDesc': 'Al menos una tarea está en \"Por hacer\", pero ninguna en progreso.',
        'projects.statusInfo.activeBadge': 'ACTIVO',
        'projects.statusInfo.activeDesc': 'Al menos una tarea está \"En progreso\" o \"En revisión\".',
        'projects.statusInfo.completedBadge': 'COMPLETADO',
        'projects.statusInfo.completedDesc': 'Todas las tareas están marcadas como \"Hecho\".',
        'projects.statusInfo.archivedBadge': 'ARCHIVADO',
        'projects.statusInfo.archivedDesc': 'El proyecto ha sido archivado. Se oculta de las vistas activas sin importar el progreso de las tareas, y puede restaurarse en cualquier momento.',
        'projects.backTo.dashboard': 'Volver al panel',
        'projects.backTo.calendar': 'Volver al calendario',
        'projects.backTo.projects': 'Volver a proyectos',
        'projects.backTo.archived': 'Volver a proyectos archivados',
        'projects.details.tab.details': 'Detalles',
        'projects.details.tab.docs': 'Docs',
        'projects.details.tab.history': 'Historial',
        'docs.projectWiki': 'Páginas',
        'docs.pageTitle': 'Documentación',
        'docs.pageSubtitle': 'Un espacio de escritura para notas, wikis y documentación interna.',
        'docs.projectLabel': 'Proyecto',
        'docs.createFirstInline': 'Crea una página para empezar a escribir.',
        'docs.noProjectsTitle': 'Todavía no hay proyectos',
        'docs.noProjectsSubtitle': 'Crea primero un proyecto y luego escribe su documentación aquí.',
        'docs.pages': '📄 Páginas',
        'docs.newPage': 'Nueva página',
        'docs.createFirstPage': 'Crear primera página',
        'docs.untitledPage': 'Página sin título',
        'docs.slashCommandFailed': 'No se pudo insertar ese bloque. Inténtalo de nuevo.',
        'docs.searchAria': 'Buscar páginas',
        'docs.searchPlaceholder': 'Buscar páginas...',
        'docs.searchClear': 'Borrar búsqueda',
        'docs.searchNoResults': 'Ninguna página coincide',
        'docs.searchResultCount': '{count} resultado(s)',
        'docs.searchInTitle': 'título',
        'docs.searchInContent': 'contenido',
        'docs.breadcrumbAria': 'Ubicación de la página',
        'docs.breadcrumbRoot': 'Páginas',
        'docs.titlePlaceholder': 'Página sin título',
        'docs.loading': 'Cargando documentación...',
        'docs.saved': 'Guardado',
        'docs.savedToast': 'Documentación guardada',
        'docs.saving': 'Guardando...',
        'docs.save': 'Guardar',
        'docs.edit': 'Editar',
        'docs.createdLabel': 'Creado',
        'docs.updatedLabel': 'Actualizado',
        'docs.switchToReading': 'Cambiar a lectura',

        'docs.resumeEditing': 'Reanudar edición',

        'docs.doneEditing': 'Hecho',
        'docs.doneEditingTooltip': 'Terminar de editar — vuelve a la vista de lectura, donde las tablas se ordenan y los enlaces abren. Tus cambios ya están guardados.',
        'docs.copyChip': 'Copiar chip',
        'docs.saveFailed': 'Error al guardar',
        'docs.autosaveSavedAt': 'Guardado {time}',
        'docs.replaceGuardTitle': '¿Eliminar casi toda la página?',
        'docs.replaceGuardBody': 'Esta página está compartida mediante un enlace público y tu edición borra la mayor parte de su contenido. Quien tenga el enlace verá la página recortada.',
        'docs.replaceGuardConfirm': 'Guardar igualmente',
        'docs.replaceGuardKeep': 'Seguir editando',
        'docs.replaceGuardBlocked': 'Sin guardar — la página está compartida y esta edición elimina casi todo',
        'docs.unsavedChanges': 'Cambios sin guardar',
        'docs.history': 'Historial',
        'askKai.openButton': 'Preguntar a Kai',
        'askKai.modalTitle': 'Preguntar a Kai',
        'askKai.drawerSubtitle': 'Planifica trabajo, moldea contenido y revisa el resultado antes de aplicarlo.',
        'askKai.actionLabel': 'Acción',
        'askKai.actionAuto': 'Automático (inferir mejor acción)',
        'askKai.actionFind': 'Consultar tareas o proyectos',
        'askKai.modeFind': 'Consulta',
        // Modo Buscar (filtrado en lenguaje natural)
        'askKai.find.placeholder': 'p. ej. tareas urgentes abiertas de Nautilus etiquetadas franky',
        'askKai.find.matchCount': '{count} coincidencias',
        'askKai.find.resultsTasks': 'Hay {count} tareas que coinciden:',
        'askKai.find.resultsProjects': 'Hay {count} proyectos que coinciden:',
        'askKai.find.showingOf': 'Mostrando {shown} de {total}. Pulsa {button} para verlas todas.',
        'askKai.find.noMatches': 'Nada coincide con estos filtros.',
        'askKai.find.emptyHint': 'Quitar «{dimension}» daría {count}.',
        'askKai.find.emptyQuery': 'No pude convertir eso en un filtro. Prueba a nombrar una etiqueta, un proyecto, una prioridad, un estado o una fecha de vencimiento.',
        'askKai.find.unknownValue': 'No encontré ningún proyecto ni etiqueta con el nombre «{term}».',
        'askKai.find.ambiguous': '«{term}» coincide con más de una cosa. ¿A cuál te referías?',
        'askKai.find.contradictoryRange': 'Ese rango de fechas es contradictorio. No se aplicó ningún filtro de fecha.',
        'askKai.find.blocked': 'No pude aplicar este filtro.',
        'askKai.find.showTasks': 'Ver tareas',
        'askKai.find.showProjects': 'Ver proyectos',
        'askKai.find.refine': 'Afinar',
        'askKai.find.clear': 'Quitar el filtro de Kai',
        'askKai.find.not': 'no',
        'askKai.find.joinOr': ' o ',
        'askKai.find.joinAnd': ' y ',
        'askKai.find.value.none': 'ninguno',
        // Etiquetas de dimensión para los chips de filtro interpretado
        'askKai.find.dimension.search': 'Texto',
        'askKai.find.dimension.status': 'Estado',
        'askKai.find.dimension.priority': 'Prioridad',
        'askKai.find.dimension.project': 'Proyecto',
        'askKai.find.dimension.tag': 'Etiqueta',
        'askKai.find.dimension.datePreset': 'Vencimiento',
        'askKai.find.dimension.dateRange': 'Rango de fechas',
        'askKai.find.dimension.linkType': 'Enlaces',
        'askKai.find.dimension.updated': 'Actualizado',
        'askKai.find.dimension.scope': 'Ámbito',
        'askKai.find.dimension.projectStatus': 'Estado del proyecto',
        'askKai.find.dimension.taskFilter': 'Tareas',
        'askKai.find.dimension.completion': 'Progreso',
        'askKai.find.dimension.created': 'Creado',
        'askKai.find.dimension.updatedRange': 'Actualizado',
        'askKai.find.dimension.completed': 'Completado',
        'askKai.find.dimension.duplicate': 'Duplicadas',
        'askKai.find.value.duplicate': 'por título',
        'askKai.find.dimension.recency': 'Orden',
        'askKai.find.dimension.limit': 'Límite',
        'askKai.find.recency.recent-updated': 'más recientes (modificadas)',
        'askKai.find.recency.recent-created': 'más recientes (creadas)',
        'askKai.find.wrongKind': 'No existe ninguna etiqueta llamada «{term}», pero sí un {kind} con ese nombre.',
        'askKai.find.unsupported.updatedWindow': 'Nautilus solo puede filtrar por estas ventanas de actualización: 5 minutos, 30 minutos, 24 horas, esta semana, este mes.',
        'askKai.find.contradictoryCompletion': 'Ese rango de progreso es contradictorio. No se aplicó ningún filtro de progreso.',
        'askKai.find.preset.overdue': 'Vencidas',
        'askKai.find.preset.no-date': 'Sin fecha de fin',
        'askKai.find.preset.has-date': 'Con fecha de fin',
        'askKai.find.preset.has-start-date': 'Con fecha de inicio',
        'askKai.find.ambiguousKind': '«{term}» es a la vez un proyecto y una etiqueta. ¿A cuál te referías?',
        'askKai.find.kind.tag': 'Etiqueta',
        'askKai.find.kind.project': 'Proyecto',
        'askKai.find.preset.end-today': 'Hoy',
        'askKai.find.preset.end-tomorrow': 'Mañana',
        'askKai.find.preset.end-week': 'Esta semana',
        'askKai.find.preset.end-month': 'Este mes',
        'askKai.find.preset.no-start-date': 'Sin fecha de inicio',
        'askKai.find.preset.already-started': 'Ya iniciadas',
        'askKai.find.link.is_blocked_by': 'Bloqueadas',
        'askKai.find.link.blocks': 'Bloquean a otras',
        'askKai.find.link.relates_to': 'Relacionadas',
        'askKai.find.link.has_web_links': 'Con enlaces',
        'askKai.find.link.no_links': 'Sin enlaces',
        // Negativas honestas — Nautilus no modela esto
        'askKai.find.unsupported.generic': 'Nautilus no registra «{term}», así que lo omití.',
        'askKai.find.unsupported.assignee': 'Nautilus no tiene campo de responsable: las personas son etiquetas. Nombra una y filtraré por esa etiqueta.',
        'askKai.find.unsupported.effort': 'Nautilus no registra el esfuerzo estimado.',
        'askKai.find.unsupported.milestone': 'Nautilus no tiene hitos, sprints ni épicas.',
        'askKai.find.unsupported.workspace': 'Nautilus no tiene espacios de trabajo.',
        'askKai.find.unsupported.subtask': 'Nautilus no tiene subtareas ni tareas padre.',
        'askKai.find.unsupported.category': 'Nautilus no tiene categorías: usa etiquetas.',
        'askKai.find.unsupported.createdDate': 'Nautilus no puede filtrar por fecha de creación.',
        'askKai.find.unsupported.noPriority': 'Toda tarea tiene prioridad, así que «sin prioridad» no puede coincidir con nada.',
        'askKai.find.unsupported.sort': 'Kai no puede cambiar el orden. Haz clic en una cabecera de columna en la vista de lista.',
        'askKai.find.unsupported.groupBy': 'Nautilus no puede agrupar resultados. El tablero Kanban agrupa por estado.',
        'askKai.find.unsupported.limit': 'Nautilus no puede limitar cuántos resultados se muestran.',
        'askKai.find.unsupported.projectAggregate': 'Los proyectos no se pueden filtrar por la prioridad, el vencimiento o el bloqueo de sus tareas.',
        'askKai.errors.findFailed': 'No se pudo aplicar ese filtro.',
        'askKai.actionAutomation': 'Crear automatización recurrente',
        'askKai.actionAutomationHint': 'Describe una tarea y la frecuencia con la que debe repetirse',
        'askKai.actionDraft': 'Crear documentación',
        'askKai.actionRewrite': 'Reescribir o resumir texto',
        'askKai.actionApplyChips': 'Convertir etiquetas de la tabla en chips de estado con color',
        'askKai.actionProject': 'Crear proyecto con tareas iniciales',
        'askKai.actionProjectOnly': 'Crear solo el proyecto',
        'askKai.actionTasksOnly': 'Crear solo tareas',
        'askKai.modeLabel': 'Modo',
        'askKai.modeHelp': 'Elige una ruta o déjalo en automático.',
        'askKai.modeAuto': 'Autodetectar',
        'askKai.modeAutomation': 'Automatización',
        'askKai.modeProject': 'Proyecto + tareas',
        'askKai.modeProjectOnly': 'Proyectos',
        'askKai.modeTasksOnly': 'Tareas',
        'askKai.modeTemplate': 'Documentación',
        'askKai.modeRewrite': 'Reescribir',
        'askKai.modeApplyChips': 'Chips de estado',
        'askKai.useDocContext': 'Usar la página actual como contexto',
        'askKai.useGeneratedProjectTags': 'Generar etiquetas de proyecto',
        'askKai.useGeneratedTaskTags': 'Generar etiquetas de tareas',
        'askKai.useGeneratedProjectTaskTags': 'Generar etiquetas de proyecto y tarea',
        'askKai.instructionLabel': 'Instrucción',
        'askKai.instructionPlaceholder': 'Describe lo que quieres que Kai genere...',
        'askKai.promptLabel': 'Prompt',
        'askKai.promptHelp': 'Describe el resultado que quieres. Kai generará primero una propuesta revisable.',
        'askKai.clearPrompt': 'Limpiar',
        'askKai.savePrompt': 'Guardar prompt',
        'askKai.unsavePrompt': 'Quitar prompt guardado',
        'askKai.savedPrompts': 'Prompts guardados',
        'askKai.savedEmpty': 'Aún no hay prompts guardados. Escribe un prompt y toca la estrella para guardarlo.',
        'askKai.searchSavedPrompts': 'Buscar prompts guardados',
        'askKai.savedNoMatches': 'Ningún prompt guardado coincide con tu búsqueda.',
        'askKai.deleteSavedPrompt': 'Eliminar prompt guardado',
        'askKai.deleteSavedPromptConfirm': '¿Seguro que quieres eliminar este prompt?',
        'askKai.editPromptName': 'Cambiar nombre de la consulta guardada',
        'askKai.promptName': 'Nombre de la consulta guardada',
        'askKai.savePromptName': 'Guardar nombre de la consulta',
        'askKai.promptNameUpdated': 'Nombre de la consulta actualizado',
        'askKai.promptSaved': 'Prompt guardado',
        'askKai.promptRemoved': 'Prompt eliminado',
        'askKai.promptAlreadySaved': 'Ese prompt ya está guardado',
        'askKai.savedError': 'No se pudo guardar tu prompt. Inténtalo de nuevo.',
        'askKai.expandPrompt': 'Expandir',
        'askKai.shortcutHint': 'Pulsa {submitShortcut} para generar una propuesta.',
        'askKai.openButtonTooltip': 'Preguntar a Kai ({shortcut})',
        'askKai.previewLabel': 'Vista previa',
        'askKai.previewPlaceholder': 'Genera una propuesta para verla aquí.',
        'askKai.previewEmptyTitle': 'Genera una propuesta',
        'askKai.previewEmptySummary': 'Kai convertirá tu prompt en una revisión estructurada antes de aplicar nada.',
        'askKai.reviewLabel': 'Revisión',
        'askKai.reviewHelp': 'Revisa la propuesta antes de aplicar cualquier cambio.',
        'askKai.generatingSummary': 'Analizando tu solicitud y construyendo la propuesta.',
        'askKai.generatingSummaryWithSeconds': 'Analizando tu solicitud y construyendo la propuesta.',
        'askKai.intentHint': 'Kai inferirá la mejor acción a partir de tu instrucción.',
        'askKai.intentHintAuto': 'Kai inferirá la mejor acción a partir de tu instrucción.',
        'askKai.intentHintManual': 'Kai usará {mode}.',
        'askKai.intentAutoResolved': 'Kai infirió {mode}.',
        'askKai.intentManualResolved': 'Usando {mode}.',
        'askKai.inferredMode': 'Inferido: {mode}',
        'askKai.contextProjects': 'Contexto de proyectos',
        'askKai.contextTasks': 'Contexto de tareas',
        'askKai.contextDocs': 'Contexto de documentos',
        'askKai.contextDocSource': 'Usando el texto de la página actual',
        'askKai.contextGenerateTags': 'Generando etiquetas',
        'askKai.ambiguityHeader': 'Elige cómo debe estructurar Kai esta solicitud.',
        'askKai.ambiguityFooter': 'Elige una opción para habilitar la confirmación.',
        'askKai.ambiguitySelectedPrefix': 'Seleccionado:',
        'askKai.ambiguityPreviewLabel': 'Opción elegida',
        'askKai.stats.projects': 'Proyectos',
        'askKai.stats.tasks': 'Tareas',
        'askKai.stats.pages': 'Páginas',
        'askKai.stats.options': 'Opciones',
        'askKai.stats.sections': 'Secciones',
        'askKai.stats.characters': 'Caracteres',
        'askKai.stats.levels': 'Niveles',
        'askKai.meta.tasksCount': '{count} tareas',
        'askKai.meta.tagsCount': '{count} etiquetas',
        'askKai.meta.project': 'Proyecto: {name}',
        'askKai.meta.noProject': 'Sin proyecto',
        'askKai.meta.levelLabel': 'Nivel {count}',
        'askKai.meta.levelShort': 'N{count}',
        'askKai.meta.childOf': 'Bajo {title}',
        'askKai.meta.rootPage': 'Página raíz',
        'askKai.meta.noExcerpt': 'Aún no hay un extracto de la página.',
        'askKai.seeMore': 'Ver más',
        'askKai.viewMore': 'Ver {count} más',
        'askKai.showLess': 'Ver menos',
        'askKai.review.blockedSummary': 'A este borrador le faltan partes obligatorias de tu prompt. Regénéralo antes de aplicarlo.',
        'askKai.review.blockedExhausted': 'Kai ya reintentó este prompt una vez y todavía no pudo cumplir el resultado obligatorio. Edita el prompt antes de generar de nuevo.',
        'askKai.review.errorTitle': 'Error de generación',
        'askKai.review.errorSummary': 'Kai no pudo convertir este prompt en una propuesta revisable.',
        'askKai.review.fixButton': 'Regenerar Lo Que Falta',
        'askKai.actionIndicator.project': 'Crear Proyecto + Tareas',
        'askKai.actionIndicator.multiProject': 'Crear Múltiples Proyectos',
        'askKai.actionIndicator.projectOnly': 'Crear Proyecto',
        'askKai.actionIndicator.tasksOnly': 'Crear Tareas',
        'askKai.actionIndicator.automation': 'Crear Automatización',
        'askKai.actionIndicator.template': 'Crear Documentación',
        'askKai.actionIndicator.rewrite': 'Reescribir Texto',
        'askKai.actionIndicator.applyChips': 'Chips de Estado',
        'askKai.actionIndicator.proposalSuffix': 'propuesta',
        'askKai.actionIndicator.default': 'Esperando instrucción...',
        'askKai.actionIndicator.ready': 'Listo para generar',
        'askKai.actionIndicator.proposalReady': 'Propuesta lista',
        'askKai.generateButton': 'Generar',
        'askKai.confirmButton': 'Aplicar',
        'askKai.generating': 'Generando propuesta...',
        'askKai.generatingWithSeconds': 'Generando propuesta... {seconds}s',
        'askKai.errors.instructionRequired': 'Agrega una instrucción para Ask Kai.',
        'askKai.errors.sourceRequired': 'Proporciona texto fuente para reescribir o resumir.',
        'askKai.errors.invalidProposal': 'Ask Kai devolvió una propuesta inválida.',
        'askKai.errors.generateFailed': 'No se pudo generar la propuesta de Ask Kai. Inténtalo de nuevo.',
        'askKai.errors.generateTimedOut': 'Kai está tardando más de lo esperado. Inténtalo de nuevo.',
        'askKai.errors.confirmFailed': 'No se pudo aplicar la propuesta de Ask Kai. Inténtalo de nuevo.',
        'askKai.errors.choiceRequired': 'Elige una estructura antes de confirmar.',
        'askKai.errors.reviewBlocked': 'Esta propuesta de Ask Kai todavía tiene problemas bloqueantes. Regénérala antes de aplicarla.',
        'askKai.errors.repairExhausted': 'Kai ya reintentó este prompt una vez. Edita el prompt antes de generar de nuevo.',
        'askKai.errors.generatingKeepOpen': 'Kai sigue generando. Mantén el panel abierto hasta que aparezca la propuesta.',
        'askKai.errors.rateLimited': 'Ask Kai está ocupado ahora mismo. Inténtalo de nuevo en breve.',
        'askKai.errors.inputTooLong': 'La entrada para Ask Kai es demasiado larga. Acórtala e inténtalo de nuevo.',
        'askKai.errors.rewriteDocsOnly': 'Abre una página de documentación para reescribir o resumir texto existente.',
        'askKai.errors.rewriteRequiresDocPage': 'El modo de reescritura solo se puede aplicar en una página de documentación activa.',
        'askKai.errors.currentPageEmpty': 'Kai no puede resumir ni reescribir esta página porque todavía no tiene texto.',
        'askKai.errors.chipNoTable': 'Kai necesita una tabla con fila de encabezado en esta página para crear chips de estado.',
        'askKai.errors.chipNoMatch': 'Kai no encontró celdas coincidentes para convertir en chips de estado.',
        'askKai.confirm.discardTitle': '¿Descartar propuesta de Kai?',
        'askKai.confirm.discardBody': 'Tienes una propuesta de Kai abierta. ¿Seguro que quieres cerrar y perderla?',
        'askKai.confirm.discardButton': 'Descartar propuesta',
        'askKai.confirm.regenerateTitle': '¿Generar una propuesta similar?',
        'askKai.confirm.regenerateBody': 'No has cambiado el prompt ni las opciones. Si generas de nuevo, probablemente obtendrás una propuesta similar.',
        'askKai.confirm.regenerateButton': 'Generar de nuevo',
        'askKai.success.singleProject': 'Proyecto creado: {name}',
        'askKai.success.multiProject': 'Se crearon {count} proyectos',
        'askKai.undoToast.label': 'Aplicado correctamente.',
        'askKai.undoToast.subtitle': 'Tu contenido está listo para ver.',
        'askKai.undoToast.view': 'Ver',
        'askKai.undoToast.close': 'Cerrar',
        'askKai.undoToast.button': 'Deshacer',
        'askKai.undoToast.error': 'No se pudo deshacer. Elimina manualmente.',
        'docs.shareLink': 'Compartir enlace',
        'docs.shareCreateLink': 'Crear enlace',
        'docs.shareSaveExpiry': 'Guardar expiración',
        'docs.shareModalTitle': 'Enlace público listo',
        'docs.shareModalSubtitle': 'Usa esta URL pública para compartir la página.',
        'docs.shareModalLabel': 'Enlace público',
        'docs.shareModalNote': 'Cualquier persona con este enlace puede ver la página',
        'docs.shareModalInfoButton': 'Más información',
        'docs.shareModalCopy': 'Copiar enlace',
        'docs.shareModalCopyDone': 'Copiado',
        'docs.shareModalCopied': 'Enlace público copiado al portapapeles',
        'docs.shareSettingsUpdated': 'Configuración del enlace actualizada',
        'docs.shareLinkCreated': 'Enlace público creado',
        'docs.shareLinkDisabled': 'Ya hay un enlace público activo. Revócalo primero.',
        'docs.revokeLink': 'Revocar enlace',
        'docs.revokeLinkSuccess': 'El acceso a la página ha sido eliminado o revocado.',
        'docs.shareExpiryLabel': 'Expiración',
        'docs.shareExpiryEnds': 'Expira el {date}',
        'docs.shareExpiry1d': '24h',
        'docs.shareExpiry1w': '7 días',
        'docs.shareExpiry1m': '30 días',
        'docs.shareExpiryNever': 'Nunca',
        'docs.shareExpiryCustom': 'Personalizado...',
        'docs.shareExpiryDate': 'Fecha',
        'docs.shareExpiryTime': 'Hora',
        'docs.shareExpiryMinutes': 'Min',
        'docs.shareExpiryInvalid': 'Introduce una fecha y hora válidas en el futuro.',
        'docs.linkExpiredTitle': 'Enlace expirado',
        'docs.linkExpired': 'Este enlace ha expirado y ya no está disponible.',
        'docs.linkUnavailableTitle': 'Enlace no disponible',
        'docs.linkUnavailable': 'Este enlace ha sido revocado o ya no está disponible.',
        'docs.shareLinkExpiredNotice': 'Este enlace ha expirado. Elige una nueva expiración y renuévalo.',
        'docs.shareRenewLink': 'Renovar enlace',
        'docs.shareLinkError': 'No se pudo actualizar el enlace público. Inténtalo de nuevo.',
        'docs.revokeLinkError': 'No se pudo revocar el enlace. Inténtalo de nuevo.',
        'docs.shareSessionExpired': 'Tu sesión ha caducado. Inicia sesión y vuelve a intentarlo.',
        'docs.shareChipActive': 'Activo',
        'docs.shareChipExpired': 'Expirado',
        'docs.shareCreateSubtitle': 'Cualquiera con el enlace podrá ver esta página.',
        'docs.shareChangeExpiry': 'Cambiar expiración',
        'docs.shareReactivateHeading': 'Reactivar enlace',
        'docs.shareExpiryInstantHint': 'Se aplica al instante — sin botón de guardar.',
        'docs.shareExpiryApply': 'Aplicar fecha',
        'docs.shareCreatePreview': 'Expirará el {date}',
        'docs.shareCreatePreviewNever': 'El enlace no expirará.',
        'docs.shareExpiryChooseFuture': 'Elige una fecha y hora futuras.',
        'docs.shareStatusExpiresOn': 'Expira el {date}',
        'docs.shareStatusNoExpiry': 'Sin expiración — el enlace no caduca.',
        'docs.shareStatusExpired': 'Expiró el {date} — este enlace está inactivo.',
        'docs.shareLinkReactivated': 'Enlace reactivado',
        'docs.width': 'Ancho',
        'docs.width.narrow': 'Estrecho',
        'docs.width.wide': 'Amplio',
        'docs.pageOptions': 'Opciones de página',
        'docs.pageIcon': 'Icono de página',
        'docs.removeIcon': 'Quitar icono',
        'docs.expandPage': 'Expandir página',
        'docs.collapsePage': 'Contraer página',
        'docs.expandAllPages': 'Expandir todas las páginas',
        'docs.collapseAllPages': 'Contraer todas las páginas',
        'docs.deletePage': 'Eliminar página',
        'docs.duplicate.title': 'Duplicar página',
        'docs.removeNesting': 'Quitar anidación',
        'docs.duplicate.newTitleLabel': 'Nuevo título',
        'docs.duplicate.includeChildren': 'Incluir páginas hijas',
        'docs.duplicate.confirm': 'Duplicar',
        'docs.duplicate.success': 'Página duplicada',
        'docs.duplicate.errorEmptyTitle': 'El título no puede estar vacío',
        'docs.duplicate.errorDuplicateTitle': 'Ya existe una página con este título en el mismo nivel',
        'docs.deleteConfirm': '¿Eliminar esta página de documentación? Esta acción no se puede deshacer.',
        'docs.deleteConfirmBody': 'se eliminará permanentemente. Esta acción no se puede deshacer.',
        'docs.trash': 'Papelera',
        'docs.trashBack': 'Volver',
        'docs.trashTitle': 'Papelera de docs',
        'docs.trashSubtitle': 'Las páginas eliminadas se quedan aquí hasta que las restaures.',
        'docs.trashPage': '¿Mover página a la papelera?',
        'docs.trashConfirmBody': 'se moverá a la papelera. Podrás restaurarla más tarde.',
        'docs.trashEmpty': 'La papelera está vacía.',
        'docs.trashEmptyTitle': 'La papelera está vacía',
        'docs.trashEmptySubtitle': 'Las páginas de documentación eliminadas aparecerán aquí para que puedas restaurarlas.',
        'docs.trashNoResultsTitle': 'No se encontraron páginas',
        'docs.trashNoResultsSubtitle': 'Prueba con otro término de búsqueda.',
        'docs.trashSearchPlaceholder': 'Buscar páginas...',
        'docs.trashSearchAria': 'Buscar páginas en la papelera',
        'docs.trashSortNewest': 'Eliminadas recientemente',
        'docs.trashSortOldest': 'Más antiguas primero',
        'docs.trashSortAria': 'Ordenar páginas en la papelera',
        'docs.trashCount': '{count} página(s)',
        'docs.trashTablePage': 'Página',
        'docs.trashTableLocation': 'Ubicación',
        'docs.trashTableCreated': 'Creada',
        'docs.trashTableDeleted': 'Eliminada',
        'docs.trashTableActions': 'Acciones',
        'docs.trashLocationLabel': 'Ubicación original',
        'docs.trashDeletedLabel': 'Eliminada',
        'docs.trashRootLocation': 'Raíz',
        'docs.trashLocationUnknown': 'Ubicación no disponible',
        'docs.trashPreviewTitle': 'Vista previa de la página',
        'docs.trashPreviewSubtitle': 'Revisa la página antes de restaurarla.',
        'docs.trashPreviewLabel': 'Vista previa de papelera',
        'docs.trashPreviewButton': 'Vista previa',
        'docs.trashRestore': 'Restaurar',
        'docs.trashRestored': 'Página restaurada desde la papelera',
        'docs.trashDeletePermanent': 'Eliminar permanentemente',
        'docs.trashDeletedPermanent': 'Página eliminada permanentemente',
        'docs.trashEmptyButton': 'Vaciar papelera',
        'docs.trashEmptyConfirmTitle': '¿Vaciar la papelera?',
        'docs.trashEmptyConfirmBody': 'Estás a punto de eliminar permanentemente {count} página(s) de la papelera.',
        'docs.trashEmptyAffected': 'Páginas que se eliminarán permanentemente: {count}',
        'docs.trashEmptied': 'Papelera vaciada ({count} página(s))',
        'docs.trashPermanentDeleteTitle': '¿Eliminar página permanentemente?',
        'docs.trashPermanentDeleteBody': '"{title}" se eliminará permanentemente de la papelera. Esta acción no se puede deshacer.',
        'docs.trashedLabel': 'En papelera {date}',
        'docs.movedToTrash': 'Página movida a la papelera.',
        'docs.bulkMovedToTrash': 'Se movieron {count} página(s) a la papelera.',
        'docs.bulkSelect': 'Seleccionar',
        'docs.bulkSelectPage': 'Seleccionar página',
        'docs.bulkSelectCancel': 'Cancelar',
        'docs.bulkSelectCount': '{count} seleccionada(s)',
        'docs.bulkDeleteTitle': '¿Eliminar páginas?',
        'docs.bulkDeleteBody': 'Estás a punto de eliminar permanentemente {count} página(s) de documentación.',
        'docs.bulkDeleteRoots': 'Raíces seleccionadas: {count}',
        'docs.bulkDeleteDescendants': 'Las páginas padre seleccionadas también eliminarán sus descendientes.',
        'docs.bulkDeleteAffected': 'Páginas afectadas: {count}',
        'docs.bulkDeleteMore': '+{count} página(s) más',
        'docs.bulkTrashTitle': '¿Mover páginas a la papelera?',
        'docs.bulkTrashBody': 'Estás a punto de mover {count} página(s) de documentación a la papelera.',
        'docs.bulkTrashRoots': 'Raíces seleccionadas: {count}',
        'docs.bulkTrashDescendants': 'Las páginas padre seleccionadas también moverán sus descendientes a la papelera.',
        'docs.bulkTrashAffected': 'Páginas afectadas: {count}',
        'docs.bulkTrashMore': '+{count} página(s) más',
        'docs.bulkDeleteTypeInstruction': 'Escribe delete para confirmar.',
        'docs.bulkDeleteTypePlaceholder': 'Escribe delete aquí',
        'docs.bulkDeleteTypeError': 'Escribe "delete" exactamente para confirmar',
        'docs.bulkDeletePartialFailed': 'El movimiento a la papelera se detuvo porque no se pudo actualizar "{title}".',
        'docs.bulkDeleteSuccess': 'Se eliminaron {count} página(s).',
        'docs.historyTitle': 'Historial de la página',
        'docs.historyEmpty': 'Todavía no hay revisiones guardadas.',
        'docs.historyRestore': 'Restaurar',
        'docs.historyCurrent': 'Actual',
        'docs.historyRestored': 'Página restaurada desde el historial',
        'docs.historySelectVersion': 'Revisa una versión guardada antes de decidir si quieres restaurarla.',
        'docs.historyReadyToRestore': '¿Restaurar esta versión?',
        'docs.historyRestoreHint': 'Esto reemplazará el contenido actual de la página con la versión seleccionada.',
        'docs.historyCurrentHint': 'Estás viendo la versión guardada más reciente de esta página.',
        'docs.historySnapshotFallback': 'Instantánea guardada de la página',
        'docs.historyVersionLabel': 'Versión {number}',
        'docs.contentCorrupted': 'Esta página contiene contenido guardado no válido. Revísala antes de volver a guardar.',
        'docs.emptyTitle': 'Todavía no hay documentación',
        'docs.emptySubtitle': 'Crea una wiki ligera para este proyecto con notas, bloques de código, tablas y paneles informativos.',
        'docs.noSummary': 'Sin resumen todavía',
        'docs.editorPlaceholder': 'Escribe documentación para este proyecto...',
        'docs.tocTitle': 'Tabla de contenidos',
        'docs.tocEmpty': 'Agrega títulos para generar la tabla de contenidos.',
        'docs.childListTitle': 'Páginas hijas',
        'docs.noChildPages': 'Sin páginas hijas aún.',
        'docs.imagesNotSupported': 'Pega imágenes como archivos en otro flujo; las imágenes en línea están deshabilitadas en docs.',
        'docs.imageNoPage': 'Crea o carga una página antes de agregar imágenes.',
        'docs.imageInvalidType': 'Solo se pueden agregar imágenes a docs.',
        'docs.imageTooLarge': 'Las imágenes deben pesar 25MB o menos.',
        'docs.fileNoPage': 'Crea o carga una página antes de agregar archivos.',
        'docs.fileInvalidType': 'Este tipo de archivo no es compatible en docs.',
        'docs.fileBlockedType': 'Este tipo de archivo está bloqueado por seguridad.',
        'docs.fileTooLarge': 'Los archivos deben pesar {maxMB}MB o menos.',
        'docs.fileUploading': 'Subiendo {name}... {progress}%',
        'docs.fileUploaded': '{name} se añadió a la página.',
        'docs.fileOpen': 'Abrir',
        'docs.fileDownload': 'Descargar',
        'docs.fileConvertToCard': 'Convertir en tarjeta',
        'docs.fileConvertToChip': 'Convertir en chip',
        'docs.fileRemove': 'Quitar archivo',
        'docs.fileUntitled': 'Archivo sin nombre',
        'docs.fileActions': 'Acciones del archivo',
        'docs.imageSize.small': 'Pequeña',
        'docs.imageSize.medium': 'Mediana',
        'docs.imageSize.large': 'Grande',
        'docs.imageSize.full': 'Ancho completo',
        'docs.imageResize': 'Redimensionar imagen',
        'docs.copyCode': 'Copiar al portapapeles',
        'docs.copyCodeDone': 'Copiado',
        'docs.copyCodeFailed': 'Error',
        'docs.codeWrap': 'Ajustar texto',
        'docs.codeOneLine': 'Una línea',
        'docs.deleteBlock': 'Eliminar',
        'docs.toolbar.bullets': 'Viñetas',
        'docs.toolbar.numbers': 'Números',
        'docs.toolbar.divider': 'Divisor',
        'docs.toolbar.code': 'Código',
        'docs.toolbar.bold': 'Negrita',
        'docs.toolbar.italic': 'Cursiva',
        'docs.toolbar.underline': 'Subrayado',
        'docs.toolbar.textStyle': 'Estilo de texto',
        'docs.toolbar.paragraph': 'Párrafo',
        'docs.toolbar.heading1': 'Encabezado 1',
        'docs.toolbar.heading2': 'Encabezado 2',
        'docs.toolbar.heading3': 'Encabezado 3',
        'docs.toolbar.alignment': 'Alineación del texto',
        'docs.toolbar.alignLeft': 'Alinear a la izquierda',
        'docs.toolbar.alignCenter': 'Centrar',
        'docs.toolbar.alignRight': 'Alinear a la derecha',
        'docs.toolbar.alignJustify': 'Justificar',
        'docs.toolbar.checklist': 'Checklist',
        'docs.toolbar.emoji': 'Insertar emoji',
        'docs.toolbar.undo': 'Deshacer',
        'docs.toolbar.redo': 'Rehacer',
        'docs.findReplace.title': 'Buscar y reemplazar',
        'docs.findReplace.findPlaceholder': 'Buscar...',
        'docs.findReplace.replacePlaceholder': 'Reemplazar con...',
        'docs.findReplace.replace': 'Reemplazar',
        'docs.findReplace.replaceAll': 'Reemplazar todo',
        'docs.findReplace.noMatch': 'Sin coincidencias',
        'docs.findReplace.prev': 'Anterior',
        'docs.findReplace.next': 'Siguiente',
        'docs.findReplace.replacedCount': '{count} reemplazo(s) realizados',
        'docs.findReplace.close': 'Cerrar',
        'docs.toolbar.image': 'Imagen',
        'docs.toolbar.file': 'Archivo',
        'docs.toolbar.video': 'Video de YouTube',
        'docs.toolbar.status': 'Chip de estado',
        'docs.toolbar.dateChip': 'Fecha',
        'docs.toolbar.table': 'Tabla',
        'docs.toolbar.info': 'Info',
        'docs.toolbar.warning': 'Aviso',
        'docs.toolbar.success': 'Éxito',
        'docs.toolbar.columns': 'Columnas',
        'docs.toolbar.macros': 'Macros',
        'docs.toolbar.toc': 'Tabla de contenidos',
        'docs.toolbar.childList': 'Páginas hijas',
        'docs.toolbar.programTimeline': 'Cronograma de programa',
        'roadmap.zoom.weeks': 'Semanas',
        'roadmap.zoom.weeksShort': 'S',
        'roadmap.zoom.weeksAbout': 'unos 5 meses',
        'roadmap.zoom.months': 'Meses',
        'roadmap.zoom.monthsShort': 'M',
        'roadmap.zoom.monthsAbout': 'unos 9 meses',
        'roadmap.zoom.quarters': 'Trimestres',
        'roadmap.zoom.quartersShort': 'T',
        'roadmap.zoom.quartersAbout': 'unos 18 meses',
        'roadmap.time.justNow': 'hace un momento',
        'roadmap.time.minutesAgo': 'hace {count} min',
        'roadmap.time.hoursAgo.one': 'hace {count} h',
        'roadmap.time.hoursAgo.other': 'hace {count} h',
        'roadmap.time.daysAgo.one': 'hace {count} día',
        'roadmap.time.daysAgo.other': 'hace {count} días',
        'roadmap.ruler.label': 'Regla del calendario',
        'roadmap.ruler.week': 'Semana {week}',
        'roadmap.ruler.weekShort': 'S{week}',
        'roadmap.ruler.quarter': 'T{quarter} {year}',
        'roadmap.ruler.weekdays': 'D,L,M,X,J,V,S',
        'roadmap.search.placeholder': 'Buscar en el roadmap',
        'roadmap.search.noMatches': 'Sin resultados para "{query}"',
        'roadmap.search.milestone': 'Hito',
        'roadmap.search.item': 'Ítem',
        'roadmap.count.lanes.one': '{count} carril',
        'roadmap.count.lanes.other': '{count} carriles',
        'roadmap.count.items.one': '{count} ítem',
        'roadmap.count.items.other': '{count} ítems',
        'roadmap.count.milestones.one': '{count} hito',
        'roadmap.count.milestones.other': '{count} hitos',
        'roadmap.toolbar.height': 'Altura de la macro',
        'roadmap.toolbar.height.compact': 'Altura compacta',
        'roadmap.toolbar.height.comfortable': 'Altura cómoda',
        'roadmap.toolbar.height.tall': 'Altura alta',
        'roadmap.toolbar.editHistory': 'Historial de edición',
        'roadmap.toolbar.undo': 'Deshacer',
        'roadmap.toolbar.undoTitle': 'Deshacer (Ctrl/Cmd+Z)',
        'roadmap.toolbar.redo': 'Rehacer',
        'roadmap.toolbar.redoTitle': 'Rehacer (Ctrl/Cmd+Mayús+Z)',
        'roadmap.toolbar.export': 'Exportar como imagen PNG',
        'roadmap.toolbar.today': 'Hoy',
        'roadmap.toolbar.scale': 'Escala de la línea de tiempo',
        'roadmap.toolbar.add': 'Agregar',
        'roadmap.toolbar.panel': 'Feedback, novedades e información',
        'roadmap.toast.dismiss': 'Descartar notificación',
        'roadmap.empty.eyebrow': 'TU ROADMAP EMPIEZA AQUÍ',
        'roadmap.empty.title': 'Crea tu primer carril de trabajo',
        'roadmap.empty.body': 'Agrega un carril para un equipo, un tema o una línea de entrega. Después haz doble clic en su línea de tiempo para crear trabajo, o doble clic en la banda de hitos de arriba.',
        'roadmap.empty.createLane': 'Crear el primer carril',
        'roadmap.empty.setTitle': 'Definir título y fechas',
        'roadmap.sidebar.workstreams': 'CARRILES',
        'roadmap.sidebar.addLane': 'Agregar carril',
        'roadmap.sidebar.collapseAll': 'Contraer todos los carriles',
        'roadmap.sidebar.expandAll': 'Expandir todos los carriles',
        'roadmap.sidebar.widen': 'Ensanchar el panel de carriles',
        'roadmap.sidebar.narrow': 'Estrechar el panel de carriles',
        'roadmap.sidebar.resize': 'Redimensionar el panel de carriles',
        'roadmap.sidebar.dragResize': 'Arrastra para redimensionar el panel de carriles',
        'roadmap.sidebar.milestones': 'Hitos',
        'roadmap.sidebar.dragToMove': 'Arrastra para mover',
        'roadmap.sidebar.toggleLane': 'Mostrar u ocultar {title}',
        'roadmap.sidebar.hideLane': 'Ocultar {title}',
        'roadmap.sidebar.showLane': 'Mostrar {title}',
        'roadmap.sidebar.editLane': 'Editar {title}',
        'roadmap.sidebar.hidden': 'OCULTOS',
        'roadmap.span.weeks.one': '{count} sem',
        'roadmap.span.weeks.other': '{count} sem',
        'roadmap.span.days.one': '{count} día',
        'roadmap.span.days.other': '{count} días',
        'roadmap.canvas.label': 'Roadmap editable',
        'roadmap.canvas.itemLabel': '{title}, del {start} al {end}',
        'roadmap.canvas.dashed': 'discontinuo',
        'roadmap.canvas.milestoneLabel': '{title}, {date}, fila {row}',
        'roadmap.export.title': 'Exportar como imagen',
        'roadmap.export.subtitle.weeks': 'Un PNG de la línea de tiempo por semanas',
        'roadmap.export.subtitle.months': 'Un PNG de la línea de tiempo por meses',
        'roadmap.export.subtitle.quarters': 'Un PNG de la línea de tiempo por trimestres',
        'roadmap.export.range': 'Rango de fechas',
        'roadmap.export.rangeFrom': 'Desde',
        'roadmap.export.rangeTo': 'Hasta',
        'roadmap.export.rangeAll': 'Toda la línea de tiempo',
        'roadmap.export.resolution': 'Resolución',
        'roadmap.export.standard': 'Estándar',
        'roadmap.export.standardUse': 'Chat y correo',
        'roadmap.export.high': 'Alta',
        'roadmap.export.highUse': 'Presentaciones y documentos',
        'roadmap.export.maximum': 'Máxima',
        'roadmap.export.maximumUse': 'Impresión y pantallas grandes',
        'roadmap.export.recommended': 'Recomendada',
        'roadmap.export.reducedTitle': 'Reducida para ajustarse a los límites del navegador',
        'roadmap.export.whatsInImage': 'Qué incluye la imagen',
        'roadmap.export.tipWhole': 'El rango de fechas que elijas (por defecto, toda la línea de tiempo), no solo la parte visible en pantalla.',
        'roadmap.export.tipLight': 'Siempre en colores claros, aunque uses el modo oscuro.',
        'roadmap.export.tipHidden': 'Los carriles ocultos no se incluyen.',
        'roadmap.export.tipCollapsed': 'Los carriles contraídos siguen contraídos. Expándelos antes para mostrar sus ítems.',
        'roadmap.export.copy': 'Copiar',
        'roadmap.export.download': 'Descargar PNG',
        'roadmap.export.rendering': 'Generando…',
        'roadmap.export.view': 'Vista {scale}',
        'roadmap.export.exportedOn': 'Exportado el {date}',
        'roadmap.export.copied': 'PNG de {width} × {height} px copiado',
        'roadmap.export.exported': 'PNG de {width} × {height} px exportado',
        'roadmap.export.reduced': '(reducido para ajustarse a los límites del navegador)',
        'roadmap.export.copyNotAllowed': 'Aquí no se permite copiar imágenes. Usa Descargar PNG.',
        'roadmap.export.errorDraw': 'No se pudo generar la imagen del roadmap.',
        'roadmap.export.errorTooLarge': 'Este roadmap es demasiado grande para exportarlo como imagen.',
        'roadmap.export.errorGeneric': 'No se pudo exportar la imagen.',
        'roadmap.color.violet': 'Violeta',
        'roadmap.color.blue': 'Azul',
        'roadmap.color.teal': 'Verde azulado',
        'roadmap.color.amber': 'Ámbar',
        'roadmap.color.coral': 'Coral',
        'roadmap.color.pink': 'Rosa',
        'roadmap.color.slate': 'Pizarra',
        'roadmap.dialog.select': 'Seleccionar…',
        'roadmap.dialog.closeMenu': 'Cerrar menú',
        'roadmap.dialog.close': 'Cerrar',
        'roadmap.dialog.delete': 'Eliminar',
        'roadmap.dialog.cancel': 'Cancelar',
        'roadmap.dialog.confirm': 'Confirmar',
        'roadmap.dialog.saveChanges': 'Guardar cambios',
        'roadmap.dialog.title': 'Título',
        'roadmap.dialog.color': 'Color',
        'roadmap.dialog.style': 'Estilo',
        'roadmap.dialog.barStyle': 'Estilo de barra',
        'roadmap.dialog.solid': 'Sólida',
        'roadmap.dialog.dashed': 'Discontinua',
        'roadmap.dialog.dashedTitle': 'Discontinua: por ejemplo, trabajo tentativo o sin confirmar',
        'roadmap.lock.restricted': 'Restringido',
        'roadmap.lock.locked': 'Bloqueado',
        'roadmap.lock.chipOwnerTitle': 'Este roadmap está restringido, pero tú puedes editarlo',
        'roadmap.lock.chipTitle': 'Solo {creator} puede editarlo',
        'roadmap.lock.theCreator': 'su creador',
        'roadmap.lock.theRoadmapCreator': 'el creador del roadmap',
        'roadmap.lock.editingRestricted': 'Edición restringida',
        'roadmap.lock.onlyCreatorCanChange': 'Solo {creator} puede cambiar este roadmap.',
        'roadmap.lock.you': 'tú',
        'roadmap.lock.onlyWho': 'Solo {who} puede editar este roadmap.',
        'roadmap.lock.onlyWhoAndOwner': 'Solo {who} y el propietario de la app pueden editar este roadmap.',
        'roadmap.lock.anyone': 'Cualquiera con acceso a la página puede editar este roadmap.',
        'roadmap.lock.restrictEditing': 'Restringir la edición',
        'roadmap.lock.restrict': 'Restringir',
        'roadmap.lock.unrestrict': 'Quitar restricción',
        'roadmap.lock.denied': 'Este roadmap está bloqueado. Solo su creador puede editarlo.',
        'roadmap.add.eyebrow': 'CREAR',
        'roadmap.add.title': '¿Qué quieres agregar?',
        'roadmap.add.item': 'Ítem',
        'roadmap.add.itemHint': 'Una barra de trabajo dentro de un carril.',
        'roadmap.add.itemNeedsLane': 'Crea antes un carril: los ítems viven dentro de los carriles.',
        'roadmap.add.lane': 'Carril',
        'roadmap.add.laneHint': 'Una fila de trabajo: un equipo, un tema o una línea de entrega.',
        'roadmap.add.milestone': 'Hito',
        'roadmap.add.milestoneHint': 'Un rombo con fecha en la banda de hitos.',
        'roadmap.add.tip': 'Consejo: haz doble clic en un carril o en la banda de hitos para agregar algo en esa fecha.',
        'roadmap.item.create': 'Crear un ítem',
        'roadmap.item.details': 'Detalles del ítem',
        'roadmap.item.createButton': 'Crear ítem',
        'roadmap.item.lane': 'Carril',
        'roadmap.item.start': 'Inicio',
        'roadmap.item.end': 'Fin',
        'roadmap.item.notes': 'Notas',
        'roadmap.group.extendedByChildren': 'Se guardaron las fechas propias del grupo. Su barra sigue abarcando del {start} al {end} para incluir sus subelementos.',
        'roadmap.group.name': 'Grupo',
        'roadmap.group.delete': '¿Eliminar este grupo?',
        'roadmap.group.deleteWithChildren': '¿Eliminar este grupo y sus {count} subelementos?',
        'roadmap.group.subitems.one': '{count} subelemento',
        'roadmap.group.subitems.other': '{count} subelementos',
        'roadmap.group.addSubitem': 'Añadir subelemento',
        'roadmap.group.convertToItem': 'Convertir en elemento',
        'roadmap.group.convertToGroup': 'Convertir en grupo',
        'roadmap.group.noGroup': 'Sin grupo',
        'roadmap.group.help': 'Conserva sus propias fechas. Los subelementos pueden ampliar la barra, pero no cambian las fechas planificadas.',
        'roadmap.item.status': 'Estado',
        'roadmap.item.statusFromTask': 'desde la tarea de Nautilus',
        'roadmap.item.statusFromChildren': 'desde sus subelementos',
        'roadmap.item.titleAria': 'Título',
        'roadmap.item.colorMode': 'Colores de los elementos',
        'roadmap.item.barStyleAria': 'Estilo de barra',
        'roadmap.item.exportInvalidRange': 'Elegí fechas válidas de inicio y fin dentro de la línea de tiempo.',
        'roadmap.item.invalidDate': 'Introducí una fecha completa.',
        'roadmap.item.taskSyncAfterLoad': 'Fecha importada de la tarea',
        'roadmap.board.autoSyncTitle': 'Tareas vinculadas',
        'roadmap.board.autoSyncLabel': 'Sincronizar títulos y fechas automáticamente',
        'roadmap.board.autoSyncHelp': 'Los estados siempre se actualizan. Si está desactivado, cada barra con diferencias muestra un aviso para revisarlas individualmente.',
        'roadmap.board.autoSyncConfirm': 'Se sustituirán los títulos y las fechas de {count} elementos vinculados por los valores de Nautilus. Los próximos cambios se sincronizarán automáticamente.',
        'roadmap.board.autoSyncConfirmButton': 'Activar sincronización',
        'roadmap.board.source': 'Roadmap compartido desde {title}. Los cambios afectan a todas las páginas que lo utilizan. Actualizá para ver los cambios realizados en otros lugares.',
        'roadmap.toolbar.sync': 'Actualizar datos de las tareas vinculadas',
        'roadmap.toolbar.syncing': 'Actualizando tareas vinculadas…',
        'roadmap.toolbar.syncDone': 'Tareas vinculadas actualizadas',
        'roadmap.toolbar.syncError': 'No se pudieron actualizar las tareas vinculadas',
        'roadmap.toolbar.tallHelp': 'Alta: muestra todo el roadmap',
        'roadmap.hover.due': 'Vence el {date}',
        'roadmap.shift.title': 'Desplazar fechas',
        'roadmap.shift.buttonTitle': 'Desplazar fechas: mueve varios ítems un número de días',
        'roadmap.shift.selected': '{count} seleccionados',
        'roadmap.shift.nothing': 'Nada seleccionado',
        'roadmap.shift.dayLess': 'Un día menos',
        'roadmap.shift.dayMore': 'Un día más',
        'roadmap.shift.daysInput': 'Días a mover',
        'roadmap.shift.days': 'días',
        'roadmap.shift.selectAll': 'Seleccionar todo',
        'roadmap.shift.clear': 'Quitar selección',
        'roadmap.shift.move.one': 'Mover {count} ítem',
        'roadmap.shift.move.other': 'Mover {count} ítems',
        'roadmap.shift.moveItems': 'Mover ítems',
        'roadmap.shift.invalid': 'Escribe un número entero de días hasta {max}. Usa un signo menos para adelantar.',
        'roadmap.shift.hint': 'Haz clic en los ítems para seleccionarlos, o en el nombre de un carril para seleccionar sus ítems; después arrastra uno de ellos o escribe los días. Un grupo se mueve con sus subítems.',
        'roadmap.shift.noteLater.one': '{count} ítem se moverá {days} más tarde.',
        'roadmap.shift.noteLater.other': '{count} ítems se moverán {days} más tarde.',
        'roadmap.shift.noteEarlier.one': '{count} ítem se moverá {days} antes.',
        'roadmap.shift.noteEarlier.other': '{count} ítems se moverán {days} antes.',
        'roadmap.shift.outside.one': '{count} quedará fuera de la línea de tiempo; amplíala en la configuración del roadmap para verlo.',
        'roadmap.shift.outside.other': '{count} quedarán fuera de la línea de tiempo; amplíala en la configuración del roadmap para verlos.',
        'roadmap.shift.tasksUnchanged': 'Las fechas de las tareas vinculadas no cambian.',
        'roadmap.shift.autoSynced.one': '{count} ítem vinculado sigue las fechas de su tarea y no se moverá.',
        'roadmap.shift.autoSynced.other': '{count} ítems vinculados siguen las fechas de sus tareas y no se moverán.',
        'roadmap.shift.dragging': 'Arrastrando: {days}',
        'roadmap.shift.noChange': 'Sin cambios',
        'roadmap.shift.history': 'Fechas desplazadas {days}: {items}',
        'roadmap.shift.toastLater.one': '{count} ítem movido {days} más tarde',
        'roadmap.shift.toastLater.other': '{count} ítems movidos {days} más tarde',
        'roadmap.shift.toastEarlier.one': '{count} ítem movido {days} antes',
        'roadmap.shift.toastEarlier.other': '{count} ítems movidos {days} antes',
        'roadmap.sidebar.collapseGroups': 'Contraer todos los grupos',
        'roadmap.sidebar.expandGroups': 'Expandir todos los grupos',
        'roadmap.sidebar.hideMilestones': 'Ocultar hitos',
        'roadmap.sidebar.showMilestones': 'Mostrar hitos',
        'roadmap.sidebar.groupCount.one': '{count} subelemento',
        'roadmap.sidebar.groupCount.other': '{count} subelementos',
        'roadmap.picker.outside': 'Fuera de la línea de tiempo',
        'roadmap.picker.partly': 'Parcialmente fuera de la línea de tiempo',
        'roadmap.picker.notImported': 'No importados',
        'roadmap.picker.selectAll': 'Seleccionar todos',
        'roadmap.picker.selectNone': 'Deseleccionar todos',
        'roadmap.picker.selected': '{count} de {total} seleccionados',
        'roadmap.picker.import': 'Importar',
        'roadmap.picker.importSelected': 'Importar {count}',
        'roadmap.picker.selectedOutside.one': '{count} elemento seleccionado está completamente fuera de la línea de tiempo y no aparecerá',
        'roadmap.picker.selectedOutside.other': '{count} elementos seleccionados están completamente fuera de la línea de tiempo y no aparecerán',
        'roadmap.picker.selectedPartly.one': '{count} elemento seleccionado está parcialmente fuera de la línea de tiempo',
        'roadmap.picker.selectedPartly.other': '{count} elementos seleccionados están parcialmente fuera de la línea de tiempo',
        'roadmap.picker.changeRange': 'La línea de tiempo va del {start} al {end}. Cambiá el rango en los ajustes del roadmap para verlos.',
        'roadmap.dialog.openCalendar': 'Abrir calendario de {label}',
        'roadmap.dialog.removeScreenshot': 'Quitar captura',
        'roadmap.dialog.closeDialog': 'Cerrar',
        'roadmap.dialog.confirmDelete': 'Eliminar',
        'roadmap.export.selectedSubtitle': 'Un PNG del rango de fechas seleccionado',
        'roadmap.export.resetDates': 'Restablecer el inicio y el fin a toda la línea de tiempo',
        'roadmap.lane.eyebrow': 'CARRIL',
        'roadmap.lane.create': 'Crear un carril',
        'roadmap.lane.settings': 'Ajustes del carril',
        'roadmap.lane.createButton': 'Crear carril',
        'roadmap.lane.name': 'Nombre del carril',
        'roadmap.lane.accentColor': 'Color de acento',
        'roadmap.lane.position': 'Posición',
        'roadmap.lane.moveUp': 'Subir',
        'roadmap.lane.moveDown': 'Bajar',
        'roadmap.milestone.eyebrow': 'HITO',
        'roadmap.milestone.create': 'Crear un hito',
        'roadmap.milestone.details': 'Detalles del hito',
        'roadmap.milestone.createButton': 'Crear hito',
        'roadmap.milestone.date': 'Fecha',
        'roadmap.milestone.row': 'Fila',
        'roadmap.milestone.firstRow': 'Primera fila',
        'roadmap.milestone.secondRow': 'Segunda fila',
        'roadmap.board.eyebrow': 'ROADMAP',
        'roadmap.board.settings': 'Ajustes del roadmap',
        'roadmap.board.title': 'Título del roadmap',
        'roadmap.board.starts': 'Inicio de la línea de tiempo',
        'roadmap.board.ends': 'Fin de la línea de tiempo',
        'roadmap.board.untitled': 'Roadmap sin título',
        'roadmap.board.help': 'Define aquí el rango del calendario. Al cambiar entre semanas, meses y trimestres se conservan este rango y las fechas de los ítems.',
        'roadmap.confirm.deleteItem': '¿Eliminar este ítem del roadmap?',
        'roadmap.confirm.deleteLane': '¿Eliminar este carril?',
        'roadmap.confirm.deleteLaneWithItems.one': '¿Eliminar este carril y su ítem?',
        'roadmap.confirm.deleteLaneWithItems.other': '¿Eliminar este carril y sus {count} ítems?',
        'roadmap.confirm.deleteMilestone': '¿Eliminar este hito?',
        'roadmap.confirm.deleteFeedback': '¿Eliminar este feedback?',
        'roadmap.change.undid': 'Deshecho: {message}',
        'roadmap.change.redid': 'Rehecho: {message}',
        'roadmap.change.untitled': 'Sin título',
        'roadmap.change.laneCreated': 'Carril creado: {title}',
        'roadmap.change.laneUpdated': 'Carril actualizado: {title}',
        'roadmap.change.laneDeleted': 'Carril eliminado: {title}',
        'roadmap.change.laneReordered': 'Carril reordenado: {title}',
        'roadmap.change.laneExpanded': 'Carril expandido: {title}',
        'roadmap.change.laneCollapsed': 'Carril contraído: {title}',
        'roadmap.change.laneShown': 'Carril mostrado: {title}',
        'roadmap.change.laneHidden': 'Carril ocultado: {title}',
        'roadmap.change.allCollapsed': 'Todos los carriles contraídos',
        'roadmap.change.allExpanded': 'Todos los carriles expandidos',
        'roadmap.change.itemCreated': 'Ítem creado: {title}',
        'roadmap.change.itemUpdated': 'Ítem actualizado: {title}',
        'roadmap.change.itemDeleted': 'Ítem eliminado: {title}',
        'roadmap.change.itemMoved': 'Ítem movido: {title}',
        'roadmap.change.itemDates': 'Fechas del ítem cambiadas: {title}',
        'roadmap.change.milestoneCreated': 'Hito creado: {title}',
        'roadmap.change.milestoneUpdated': 'Hito actualizado: {title}',
        'roadmap.change.milestoneDeleted': 'Hito eliminado: {title}',
        'roadmap.change.milestoneMoved': 'Hito movido: {title}',
        'roadmap.change.restricted': 'Edición restringida',
        'roadmap.change.unrestricted': 'Edición sin restricción',
        'roadmap.change.height': 'Altura de la macro cambiada',
        'roadmap.change.panelResized': 'Panel de carriles redimensionado',
        'roadmap.change.scale': 'Escala de la línea de tiempo: {scale}',
        'roadmap.change.settings': 'Ajustes del roadmap actualizados',
        'roadmap.feedback.kindBug': 'Algo no funciona',
        'roadmap.feedback.kindIdea': 'Idea o mejora',
        'roadmap.feedback.sent': 'Gracias, se ha enviado',
        'roadmap.feedback.back': 'Volver al feedback',
        'roadmap.feedback.lead': 'Envía un error o una mejora y revisa la lista compartida de abajo para evitar duplicados.',
        'roadmap.feedback.type': 'Tipo de feedback',
        'roadmap.feedback.message': 'Tu mensaje',
        'roadmap.feedback.placeholder': '¿Qué pasó o qué te gustaría ver?',
        'roadmap.feedback.attach': 'Adjuntar captura',
        'roadmap.feedback.attached': 'Captura adjunta',
        'roadmap.feedback.removeImage': 'Quitar captura',
        'roadmap.feedback.hint': 'Pega una imagen para adjuntarla · Ctrl/Cmd+Enter envía',
        'roadmap.feedback.send': 'Enviar feedback',
        'roadmap.feedback.sending': 'Enviando…',
        'roadmap.feedback.notImage': 'Ese archivo no es una imagen.',
        'roadmap.feedback.unreadable': 'No se pudo leer esa imagen.',
        'roadmap.feedback.tooLarge': 'La captura es demasiado grande: recórtala e inténtalo de nuevo.',
        'roadmap.feedback.attachFailed': 'No se pudo adjuntar la imagen.',
        'roadmap.feedback.viewImage': 'Ver captura',
        'roadmap.feedback.markDone': 'Marcar como resuelto',
        'roadmap.feedback.reopen': 'Reabrir',
        'roadmap.feedback.delete': 'Eliminar feedback',
        'roadmap.feedback.someone': 'Alguien',
        'roadmap.feedback.doneBy': 'Resuelto por {who}',
        'roadmap.feedback.shared': 'Feedback compartido',
        'roadmap.feedback.sharedNote': 'Errores e ideas de todas las páginas con Program Roadmap.',
        'roadmap.feedback.loading': 'Cargando…',
        'roadmap.feedback.recent': '{count} recientes',
        'roadmap.feedback.open': 'Abiertos',
        'roadmap.feedback.done': 'Resueltos',
        'roadmap.feedback.search': 'Buscar errores e ideas',
        'roadmap.feedback.showingFirst': 'Se muestran las primeras {count} coincidencias.',
        'roadmap.feedback.noMatches': 'No hay feedback que coincida.',
        'roadmap.feedback.noneDone': 'Todavía no hay nada resuelto.',
        'roadmap.feedback.noneOpen': 'No hay feedback abierto.',
        'roadmap.feedback.imageLoadFailed': 'No se pudo cargar la captura',
        'roadmap.feedback.imageGone': 'Esa captura ya no está disponible.',
        'roadmap.feedback.deleteFailed': 'No se pudo eliminar el feedback',
        'roadmap.feedback.updateFailed': 'No se pudo actualizar el feedback',
        'roadmap.feedback.sendFailed': 'No se pudo enviar el feedback',
        'roadmap.feedback.ownOnly': 'Solo puedes eliminar tu propio feedback.',
        'roadmap.feedback.emptyMessage': 'Escribe una nota breve antes de enviar.',
        'roadmap.feedback.unavailable': 'Esta acción no está disponible aquí.',
        'roadmap.whatsNew.current': 'Actual',
        'roadmap.history.empty': 'Aún no hay cambios registrados. Aquí aparecerán las ediciones de carriles, ítems, hitos y ajustes.',
        'roadmap.history.lead': 'Los últimos {count} cambios de este roadmap.',
        'roadmap.about.tagline': 'Planificación de programas dentro de Nautilus Docs',
        'roadmap.about.description': 'Planifica el trabajo de equipos, fechas e hitos en un roadmap que vive dentro de tu documentación.',
        'roadmap.about.details': 'Detalles del producto',
        'roadmap.about.owner': 'Responsable',
        'roadmap.about.version': 'Versión',
        'roadmap.about.support': 'Soporte',
        'roadmap.about.openFeedback': 'Abrir feedback',
        'roadmap.panel.title': 'Feedback e información',
        'roadmap.panel.feedback': 'Feedback',
        'roadmap.panel.whatsNew': 'Novedades',
        'roadmap.panel.history': 'Historial',
        'roadmap.panel.about': 'Acerca de',
        'roadmap.loading.title': 'Preparando tu roadmap',
        'roadmap.loading.body': 'Preparando carriles, fechas e hitos…',
        'roadmap.error.openTitle': 'No pudimos abrir este roadmap',
        'roadmap.error.tryAgain': 'Reintentar',
        'roadmap.error.attention': 'El roadmap necesita atención.',
        'roadmap.error.reload': 'Recargar',
        'roadmap.error.load': 'No se pudo cargar el roadmap.',
        'roadmap.error.save': 'No se pudo guardar el roadmap.',
        'roadmap.error.conflict': 'Este roadmap cambió en otro lugar. Recarga antes de seguir editando.',
        'roadmap.error.locked': 'Este roadmap está bloqueado. Solo su creador puede editarlo.',
        'roadmap.error.notPersisted': 'Todavía no se guardó en el servidor. Tus cambios se conservan en este navegador y el guardado se reintenta solo.',
        'roadmap.error.readOnly': 'Este roadmap es de solo lectura aquí.',
        'roadmap.foot.tip': '<strong>Consejo:</strong> usa la rueda sobre la línea de tiempo para moverte a los lados y sobre los carriles para moverte arriba y abajo.',
        'roadmap.foot.viewHistory': 'Ver historial de cambios',
        'roadmap.foot.edited': 'Editado {when}',
        'roadmap.foot.editedBy': 'Editado {when} por {who}',
        'roadmap.save.saving': 'Guardando',
        'roadmap.save.error': 'Sin guardar',
        'roadmap.save.saved': 'Guardado',
        'roadmap.refreshing': 'Actualizando…',
        'roadmap.task.field': 'Tarea',
        'roadmap.task.searchPlaceholder': 'Buscar entre tus tareas',
        'roadmap.task.noResults': 'No hay tareas que coincidan.',
        'roadmap.task.notFound': 'Esta tarea ya no existe.',
        'roadmap.task.noProject': 'Sin proyecto',
        'roadmap.task.noDates': 'sin fechas',
        'roadmap.task.dates': 'Fechas',
        'roadmap.task.open': 'Abrir tarea',
        'roadmap.task.unlink': 'Desvincular',
        'roadmap.task.badgeTitle': '{title} · {status}',
        'roadmap.task.syncFailed': 'No se pudieron actualizar las fechas de la tarea.',
        'roadmap.task.pushDone': 'Fechas de la tarea actualizadas.',
        'roadmap.task.pushOne': 'Enviar a la tarea',
        'roadmap.task.pullOne': 'Traer de la tarea',
        'roadmap.task.undo': 'Deshacer',
        'roadmap.task.hasDate': 'Tarea: {date}',
        'roadmap.task.noDate': 'sin fecha',
        'roadmap.task.clearStart': 'Quitar inicio',
        'roadmap.date.placeholder': datePlaceholder('es'),
        'roadmap.date.today': 'Hoy',
        'roadmap.task.stale': 'Las fechas de la tarea cambiaron. Actualiza el roadmap para verlas.',
        'roadmap.task.refresh': 'Actualizar',
        'roadmap.task.linkPlaceholder': 'Vincular a una tarea (opcional)',
        'roadmap.task.titleDiffers': 'El título del ítem es distinto al de la tarea.',
        'roadmap.task.useTitle': 'Usar el de la tarea',
        'roadmap.group.subitemConflict.before.one': 'Un subelemento empieza antes que el grupo',
        'roadmap.group.subitemConflict.before.other': '{count} subelementos empiezan antes que el grupo',
        'roadmap.group.subitemConflict.after.one': 'Un subelemento termina después del grupo',
        'roadmap.group.subitemConflict.after.other': '{count} subelementos terminan después del grupo',
        'roadmap.group.subitemConflict.range.one': 'Un subelemento está fuera de las fechas del grupo',
        'roadmap.group.subitemConflict.range.other': '{count} subelementos están fuera de las fechas del grupo',
        'roadmap.group.conflictSummary.before.one': 'el subelemento “{title}” alarga la barra hacia atrás hasta el {date}.',
        'roadmap.group.conflictSummary.before.other': 'el subelemento “{title}” alarga la barra hacia atrás hasta el {date}.',
        'roadmap.group.conflictSummary.after.one': 'el subelemento “{title}” alarga la barra hasta el {date}.',
        'roadmap.group.conflictSummary.after.other': 'el subelemento “{title}” alarga la barra hasta el {date}.',
        'roadmap.group.conflictSummary.range': 'Los subelementos alargan la barra a Inicio: {start} → Fin: {end}.',
        'roadmap.task.pushBlocked': 'Estas fechas no se pueden enviar a la tarea vinculada mientras exista este conflicto.',
        'roadmap.item.titlePlaceholder': 'Título del ítem',
        'roadmap.item.addNote': 'Añadir nota',
        'roadmap.item.days.one': '{count} día',
        'roadmap.item.days.other': '{count} días',
        'roadmap.lane.project': 'Proyecto',
        'roadmap.lane.noProject': 'Sin proyecto',
        'roadmap.lane.importCount.one': '{count} tarea con fechas para importar.',
        'roadmap.lane.importCount.other': '{count} tareas con fechas para importar.',
        'roadmap.lane.importNone': 'Todas las tareas con fechas de este proyecto ya están en el roadmap.',
        'roadmap.lane.importOnCreate': 'Se agregan al crear el carril.',
        'roadmap.lane.undated.one': '{count} tarea sin fechas no se incluye',
        'roadmap.lane.undated.other': '{count} tareas sin fechas no se incluyen',
        'roadmap.lane.importButton.one': 'Importar {count} tarea',
        'roadmap.lane.importButton.other': 'Importar {count} tareas',
        'roadmap.lane.import': 'Importar',
        'roadmap.confirm.importTasks.one': '¿Importar {count} tarea de {project} a este carril?',
        'roadmap.confirm.importTasks.other': '¿Importar {count} tareas de {project} a este carril?',
        'roadmap.change.tasksImported.one': '{count} tarea importada: {title}',
        'roadmap.change.tasksImported.other': '{count} tareas importadas: {title}',
        'roadmap.change.laneImported.one': 'Carril creado con {count} tarea: {title}',
        'roadmap.change.laneImported.other': 'Carril creado con {count} tareas: {title}',
        'roadmap.change.synced.one': '{count} cambio sincronizado desde tareas',
        'roadmap.change.synced.other': '{count} cambios sincronizados desde tareas',
        'roadmap.sync.button': 'Sincronizar con tareas y proyectos',
        'roadmap.sync.eyebrow': 'SINCRONIZAR',
        'roadmap.sync.title': 'Sincronizar con tareas y proyectos',
        'roadmap.sync.lead': 'Cambios en tus tareas y proyectos vinculados. Solo se aplican los seleccionados; el resto del roadmap no cambia.',
        'roadmap.sync.kindTitle': 'Título',
        'roadmap.sync.kindDates': 'Fechas',
        'roadmap.sync.kindNew': 'Tarea nueva',
        'roadmap.sync.kindMissing': 'Tarea eliminada',
        'roadmap.sync.unlinkNote': 'el ítem se queda, sin el vínculo',
        'roadmap.sync.apply.one': 'Aplicar {count} cambio',
        'roadmap.sync.apply.other': 'Aplicar {count} cambios',
        'roadmap.sync.upToDate': 'El roadmap está al día con tus tareas y proyectos.',
        'docs.toolbar.callout': 'Llamada',
        'docs.toolbar.strike': 'Tachado',
        'docs.toolbar.inlineCode': 'Código en línea',
        'docs.mobile.insertTitle': 'Insertar',
        'docs.mobile.moreTitle': 'Más',
        'docs.mobile.historyLabel': 'Historial',
        'docs.mobile.formatLabel': 'Formato',
        'docs.mobile.linkTitle': 'Añadir enlace',
        'docs.mobile.linkPlaceholder': 'https://',
        'docs.mobile.linkApply': 'Aplicar',
        'docs.mobile.linkCancel': 'Cancelar',
        'docs.table.numberColumn': 'Filas numeradas',
        'docs.table.distributeEvenly': 'Distribuir columnas uniformemente',
        'docs.table.options': 'Opciones',
        'docs.table.rows': 'Filas',
        'docs.table.columns': 'Columnas',
        'docs.table.actions': 'Acciones de tabla',
        'docs.table.headerRow': 'Fila de encabezado',
        'docs.table.headerColumn': 'Columna de encabezado',
        'docs.table.mobileColumnLabel': 'Columna {number}',
        'docs.table.addRowAbove': 'Agregar fila arriba',
        'docs.table.addRowBelow': 'Agregar fila abajo',
        'docs.table.deleteRow': 'Eliminar fila',
        'docs.table.addColLeft': 'Agregar columna a la izquierda',
        'docs.table.addColRight': 'Agregar columna a la derecha',
        'docs.table.deleteColumn': 'Eliminar columna',
        'docs.table.copy': 'Copiar tabla',
        'docs.table.copied': 'Tabla copiada al portapapeles',
        'docs.table.delete': 'Eliminar tabla',
        'docs.videoModalTitle': 'Insertar video de YouTube',
        'docs.videoModalHint': 'Pega un enlace de YouTube para incrustarlo en la página.',
        'docs.videoUrlPlaceholder': 'https://www.youtube.com/watch?v=...',
        'docs.videoInsert': 'Insertar video',
        'docs.videoRequired': 'Primero pega una URL de YouTube.',
        'docs.videoInvalid': 'Solo se pueden incrustar enlaces de YouTube en docs.',
        'docs.statusModalTitle': 'Insertar chip de estado',
        'docs.statusModalHint': 'Añade una etiqueta breve y elige un color.',
        'docs.statusLabelPlaceholder': 'ESTADO',
        'docs.statusDefault': 'ESTADO',
        'docs.statusInsert': 'Insertar estado',
        'docs.templates.pickerTitle': 'Empezar desde una plantilla',
        'docs.templates.blank': 'Página en blanco',
        'docs.templates.blankDesc': 'Empieza con un documento vacío',
        'docs.templates.builtIn': 'Incorporadas',
        'docs.templates.myTemplates': 'Mis plantillas',
        'docs.templates.searchPlaceholder': 'Buscar plantillas...',
        'docs.templates.saveAsTemplate': 'Guardar como plantilla',
        'docs.templates.saveDialogTitle': 'Guardar como plantilla',
        'docs.templates.templateName': 'Nombre de la plantilla',
        'docs.templates.templateIcon': 'Icono',
        'docs.templates.templateDesc': 'Descripción (opcional)',
        'docs.templates.saveBtn': 'Guardar plantilla',
        'docs.templates.savedToast': 'Plantilla guardada',
        'docs.templates.deleteTemplate': 'Eliminar plantilla',
        'docs.templates.deleteConfirmTitle': '¿Eliminar la plantilla {name}?',
        'docs.templates.deleteConfirmWarning': 'Esta acción no se puede deshacer.',
        'docs.templates.deleteConfirmBtn': 'Eliminar',
        'docs.templates.noUserTemplates': 'Aún no tienes plantillas guardadas',
        'docs.templates.dismiss': 'Descartar',
        'docs.templates.bannerTitle': 'Empezar desde una plantilla',
        'docs.templates.bannerSubtitle': 'Elige un diseño o usa una de tus plantillas guardadas para arrancar esta página.',
        'docs.templates.browseBtn': 'Ver plantillas',
        'projects.details.startDate': 'Fecha de inicio',
        'projects.details.endDate': 'Fecha de fin',
        'projects.details.duration': 'Duración',
        'projects.details.timeline': 'Cronograma',
        'projects.details.durationDays': '{count} días',
        'projects.details.remainingDaysLeft': 'Quedan {count} días',
        'projects.details.remainingDueToday': 'Vence hoy',
        'projects.details.remainingOverdueDays': '{count} días de retraso',
        'projects.details.remainingCompleted': 'Completado',
        'projects.details.finishedOnTime': 'Terminado a tiempo',
        'projects.details.finishedLateDays': 'Terminado {count} días tarde',
        'projects.details.finishedEarlyDays': 'Terminado {count} días antes',
        'projects.details.created': 'Creado',
        'projects.details.calendarColor': 'Color del calendario',
        'projects.details.color': 'Color',
        'projects.details.customColor': 'Color personalizado',
        'projects.details.progressOverview': 'Resumen de progreso',
        'projects.details.taskProgress': 'Progreso de tareas',
        'projects.details.tasksTitle': 'Tareas ({count})',
        'projects.details.descriptionTitle': 'Descripción',
        'projects.details.tasksDoneSummary': '{done} de {total} hechas',
        'projects.details.overdueTasks': 'Tareas vencidas: {count}',
        'projects.details.viewOverdueTasks': 'Ver las tareas vencidas',
        'projects.details.moreActions': 'Más acciones',
        'projects.details.changeHistory': 'Historial de cambios',
        'projects.details.noChanges': 'Aún no hay cambios para este proyecto',
        'projects.details.noTasksFound': 'No se encontraron tareas',
        'projects.details.viewTodo': 'Ver tareas Por hacer de este proyecto',
        'projects.details.viewProgress': 'Ver tareas En progreso de este proyecto',
        'projects.details.viewReview': 'Ver tareas En revisión de este proyecto',
        'projects.details.viewCompleted': 'Ver tareas Completadas de este proyecto',
        'projects.details.viewBacklog': 'Ver tareas de backlog en vista de lista',
        'projects.details.viewInList': 'Ver todas las tareas en vista de lista filtradas por este proyecto',
        'projects.details.viewInListBtn': 'Ver en Lista',
        'projects.details.resetSort': 'Orden automático',
        'projects.details.groupBy.label': 'Agrupar por',
        'projects.details.groupBy.none': 'Ninguno',
        'projects.details.groupBy.status': 'Estado',
        'projects.details.groupBy.priority': 'Prioridad',
        'projects.details.resetSortTitle': 'Restablecer al orden automático por prioridad',
        'projects.details.editDescription': 'Editar',
        'projects.details.doneEditing': 'Listo',
        'projects.details.clickToEdit': 'Haz clic para editar',
        'projects.details.descriptionPlaceholder': 'Añade una descripción…',
        'projects.details.segmentTip': '{count} de {total} tareas',
        'projects.details.segmentHint': 'Clic para ver estas tareas',
        'projects.details.segmentHintTouch': 'Toca para ver estas tareas',
        'projects.untitled': 'Proyecto sin título',
        'tasks.title': 'Todas las tareas',
        'tasks.subtitle': 'Gestiona tareas de todos los proyectos',
        'tasks.searchPlaceholder': '\u{1F50D} Buscar tareas...',
        'tasks.filters.status': 'Estado',
        'tasks.filters.statusTitle': 'Estado',
        'tasks.status.backlog': 'Backlog',
        'tasks.status.todo': 'Por hacer',
        'tasks.status.progress': 'En progreso',
        'tasks.status.review': 'En revisión',
        'tasks.status.done': 'Hecho',
        'tasks.filters.tags': 'Etiquetas',
        'tasks.filters.tagsTitle': 'Etiquetas',
        'tasks.filters.searchTags': 'Buscar etiquetas...',
        'tasks.filters.priority': 'Prioridad',
        'tasks.filters.priorityTitle': 'Prioridad',
        'tasks.priority.high': 'Alta',
        'tasks.priority.medium': 'Media',
        'tasks.priority.low': 'Baja',
        'tasks.filters.project': 'Proyecto',
        'tasks.filters.projectTitle': 'Proyecto',
        'tasks.filters.searchProjects': 'Buscar proyectos...',
        'tasks.filters.selectAll': 'Seleccionar / deseleccionar todo',
        'tasks.filters.date': 'Fecha',
        'tasks.filters.endDate': 'Fecha de Fin',
        'tasks.filters.startDate': 'Fecha de Inicio',
        'tasks.filters.dateTitle': 'Filtros rápidos por fecha',
        'tasks.filters.endDateTitle': 'Filtros de Fecha de Fin',
        'tasks.filters.noDate': 'Sin fecha de fin',
        'tasks.filters.hasDate': 'Con fecha de fin',
        'tasks.filters.hasStartDate': 'Con fecha de inicio',
        'tasks.filters.overdue': 'Vencidas',
        'tasks.filters.endToday': 'Fecha de fin hoy',
        'tasks.filters.endTomorrow': 'Fecha de fin mañana',
        'tasks.filters.end7Days': 'Fecha de fin en 7 días',
        'tasks.filters.endThisWeek': 'Fecha de fin esta semana',
        'tasks.filters.endThisMonth': 'Fecha de fin este mes',
        'tasks.filters.startDateTitle': 'Filtros de Fecha de Inicio',
        'tasks.filters.noStartDate': 'Sin fecha de inicio',
        'tasks.filters.alreadyStarted': 'Ya comenzó',
        'tasks.filters.startToday': 'Fecha de inicio hoy',
        'tasks.filters.startTomorrow': 'Fecha de inicio mañana',
        'tasks.filters.start7Days': 'Fecha de inicio en 7 días',
        'tasks.filters.startThisWeek': 'Fecha de inicio esta semana',
        'tasks.filters.startThisMonth': 'Fecha de inicio este mes',
        'tasks.filters.updated': 'Actualizado',
        'tasks.filters.updatedTitle': 'Actualizados recientemente',
        'tasks.filters.links': 'Enlaces',
        'tasks.filters.linksTitle': 'Filtrar por tipo de enlace',
        'tasks.filters.blocksOthers': 'Bloquea a otros',
        'tasks.filters.isBlockedByOthers': 'Bloqueado por otros',
        'tasks.filters.relatesTo': 'Relacionado con',
        'tasks.filters.hasWebLinks': 'Tiene enlaces web',
        'tasks.filters.noLinks': 'Sin enlaces',
        'tasks.filters.dateFrom': 'Fecha de inicio',
        'tasks.filters.dateTo': 'Fecha de fin',
        'tasks.filters.specificEndDate': 'Fecha de fin concreta',
        'tasks.filters.specificStartDate': 'Fecha de inicio concreta',
        'tasks.filters.dateFromLabel': 'Desde',
        'tasks.filters.dateToLabel': 'Hasta',
        'tasks.filters.clear': 'Limpiar filtros',
        'tasks.filters.presetActive': 'Activas',
        'tasks.filters.presetNotDone': 'No hechas',
        'tasks.filters.presetInProgress': 'En progreso',
        'tasks.filters.include': 'Incluir',
        'tasks.filters.exclude': 'Excluir',
        'tasks.filters.excluding': 'Excluyendo',
        'tasks.filters.tagMatchOr': 'OR',
        'tasks.filters.tagMatchAnd': 'AND',
        'filters.noOtherProjects': 'No hay otros proyectos',
        'filters.noTags': 'Sin etiquetas',
        'filters.noOtherTags': 'No hay otras etiquetas',
        'filters.sheet.title': 'Opciones',
        'filters.chip.search': 'Búsqueda',
        'filters.chip.project': 'Proyecto',
        'filters.chip.tag': 'Etiqueta',
        'filters.chip.date': 'Fecha',
        'filters.chip.startDate': 'Fecha de inicio',
        'filters.chip.endDate': 'Fecha de fin',
        'filters.chip.updated': 'Actualizado',
        'filters.chip.created': 'Creado',
        'filters.chip.completed': 'Completado',
        'filters.chip.emailTasks': 'Tareas del email',
        'filters.chip.generatedTasks': 'Recién generadas',
        'filters.chip.reportTasks': 'Del reporte',
        'filters.chip.overdueTasks': 'Vencidas del proyecto',
        'filters.chip.removeAria': 'Quitar filtro {label}',
        'filters.dateRange.from': 'Desde',
        'filters.dateRange.until': 'Hasta',
        'filters.updated.week': 'Semana',
        'filters.updated.month': 'Mes',
        'tasks.kanban.tipLabel': 'Consejo:',
        'tasks.kanban.tipTextBefore': 'En el tablero Kanban manten ',
        'tasks.kanban.tipTextAfter': ' (Cmd en Mac) y haz clic para seleccionar varias tareas. Shift + clic selecciona un rango, luego arrastra para moverlas juntas',
        'tasks.view.kanban': 'Kanban',
        'tasks.view.list': 'Lista',
        'tasks.view.archived': 'Archivadas',
        'tasks.view.calendar': 'Calendario',
        'tasks.sort.label': 'Ordenar: Prioridad',
        'tasks.sort.orderByPriority': 'Ordenar por prioridad',
        'tasks.mobile.sortBy': 'Ordenar por',
        'tasks.mobile.sortApply': 'Aplicar',
        'tasks.mobile.sortName': 'Nombre',
        'tasks.kanban.showBacklog': 'Mostrar backlog',
        'tasks.kanban.showProjects': 'Mostrar proyectos',
        'tasks.kanban.showTags': 'Mostrar etiquetas',
        'tasks.kanban.showNoDate': 'Mostrar \"Sin fecha\"',
        'tasks.addButton': '+ Añadir tarea',
        'tasks.noProject': 'Sin proyecto',
        'tasks.projectIndicatorNone': 'Sin proyecto - ',
        'tasks.project.selectPlaceholder': 'Selecciona un proyecto',
        'tasks.tags.none': 'Sin etiquetas',
        'tasks.checklist.toggle': 'Alternar casilla',
        'tasks.untitled': 'Tarea sin título',
        'tasks.startDatePrefix': 'Fecha de inicio: ',
        'tasks.endDatePrefix': 'Fecha de fin: ',
        'tasks.noDate': 'Sin fecha',
        'tasks.noDatesSet': 'Sin fechas',
        'tasks.noEndDate': 'Sin fecha de fin',
        'tasks.due.yesterday': 'Ayer',
        'tasks.due.daysOverdue': '{count} días de atraso',
        'tasks.due.tomorrow': 'Mañana',
        'tasks.openTaskDetails': 'Abrir detalles de la tarea',
        'tasks.noTasksInProject': 'No hay tareas en este proyecto',
        'tasks.attachments.open': 'Abrir',
        'tasks.attachments.none': 'Sin archivos adjuntos',
        'tasks.attachments.remove': 'Quitar archivo adjunto',
        'tasks.attachments.removeTitle': 'Quitar',
        'tasks.attachments.removeLink': 'Quitar enlace',
        'tasks.attachments.googleDoc': 'Documento de Google',
        'tasks.attachments.googleSheet': 'Hoja de cálculo de Google',
        'tasks.attachments.googleSlides': 'Presentación de Google',
        'tasks.attachments.googleDriveFile': 'Archivo de Google Drive',
        'tasks.attachments.pdf': 'Documento PDF',
        'tasks.attachments.word': 'Documento de Word',
        'tasks.attachments.excel': 'Archivo de Excel',
        'tasks.attachments.powerpoint': 'PowerPoint',
        'tasks.empty.epic': 'Aún no hay tareas. Crea tu primera tarea para este épico.',
        'tasks.backlogQuickTitle': 'Abrir tareas de backlog en vista de lista',
        'tasks.backlogQuickLabel': 'Backlog',
        'tasks.kanban.columnBacklog': 'Backlog',
        'tasks.kanban.columnTodo': 'Por hacer',
        'tasks.kanban.overdue': 'Vencida',
        'tasks.kanban.addTask': '+ Añadir tarea',
        'tasks.kanban.addTaskAria': 'Añadir tarea',
        'tasks.kanban.columnProgress': 'En progreso',
        'tasks.kanban.columnReview': 'En revisión',
        'tasks.kanban.columnDone': 'Hecho',
        'tasks.table.task': 'Tarea',
        'tasks.table.priority': 'Prioridad',
        'tasks.table.status': 'Estado',
        'tasks.table.tags': 'Etiquetas',
        'tasks.table.project': 'Proyecto',
        'tasks.table.startDate': 'Fecha de inicio',
        'tasks.table.endDate': 'Fecha de fin',
        'tasks.table.updated': 'Actualizado',
        'tasks.list.count': '{count} resultados',
        'tasks.export.button': 'Exportar a Excel',
        'tasks.export.buttonAria': 'Exportar a Excel las tareas de esta lista',
        'tasks.export.sheetName': 'Tareas',
        'tasks.export.column.id': 'ID',
        'tasks.export.column.created': 'Creada',
        'tasks.export.column.description': 'Descripción',
        'tasks.export.success': '{count} tareas exportadas a Excel',
        'tasks.export.empty': 'No hay tareas para exportar con los filtros actuales',
        'tasks.export.failed': 'No se pudo crear el archivo de Excel. Inténtalo de nuevo.',
        'tasks.massEdit.title': 'Edición Masiva',
        'tasks.massEdit.selected': '{count} de {total} tareas seleccionadas',
        'tasks.massEdit.selectedHidden': '{count} de {total} seleccionadas ({hidden} ocultas)',
        'tasks.massEdit.status': 'Estado',
        'tasks.massEdit.priority': 'Prioridad',
        'tasks.massEdit.dates': 'Fechas',
        'tasks.massEdit.project': 'Proyecto',
        'tasks.massEdit.tags': 'Etiquetas',
        'tasks.massEdit.clear': 'Limpiar',
        'tasks.massEdit.applyChanges': 'Aplicar cambios',
        'tasks.massEdit.pendingChanges': 'cambios pendientes',
        'tasks.massEdit.changeQueued': 'Cambio en cola',
        'tasks.massEdit.apply': 'Aplicar',
        'tasks.massEdit.selectFirst': 'Selecciona un valor primero',
        'tasks.massEdit.cancel': 'Cancelar',
        'tasks.massEdit.selectAll': 'Seleccionar todo filtrado',
        'tasks.massEdit.startDate': 'Fecha de inicio',
        'tasks.massEdit.endDate': 'Fecha de fin',
        'tasks.massEdit.tags.add': 'Agregar etiquetas (mantener existentes)',
        'tasks.massEdit.tags.replace': 'Reemplazar todas las etiquetas',
        'tasks.massEdit.tags.remove': 'Eliminar etiquetas específicas',
        'tasks.massEdit.tags.noExisting': 'No hay etiquetas existentes',
        'tasks.massEdit.success': 'Se actualizaron {count} tareas',
        'tasks.massEdit.confirm.title': 'Confirmar edición masiva',
        'tasks.massEdit.confirm.summary': 'Estás a punto de actualizar {count} tareas:',
        'tasks.massEdit.confirm.field.status': 'Estado → {value}',
        'tasks.massEdit.confirm.field.priority': 'Prioridad → {value}',
        'tasks.massEdit.confirm.field.startDate': 'Fecha de inicio → {value}',
        'tasks.massEdit.confirm.field.endDate': 'Fecha de fin → {value}',
        'tasks.massEdit.confirm.field.project': 'Proyecto → {value}',
        'tasks.massEdit.confirm.field.tagsAdd': 'Agregar etiquetas: {value}',
        'tasks.massEdit.confirm.field.tagsReplace': 'Reemplazar etiquetas con: {value}',
        'tasks.massEdit.confirm.field.tagsRemove': 'Eliminar etiquetas: {value}',
        'tasks.massEdit.confirm.proceed': 'Sí, actualizar {count} tareas',
        'tasks.massEdit.confirm.confirm': 'Confirmar',
        'tasks.massEdit.confirm.movingFrom': 'de {from} a {to}',
        'tasks.massEdit.confirm.variousTo': 'de varios a {to}',
        'tasks.massEdit.multiple': 'Múltiples',
        'tasks.created.successMobile': 'Tarea creada',
        'tasks.created.viewAction': 'Ver',
        'tasks.modal.editTitle': 'Editar tarea',
        'tasks.modal.createTitle': 'Crear nueva tarea',
        'tasks.modal.duplicate': '📄 Duplicar tarea',
        'tasks.modal.makeRecurring': 'Hacer recurrente…',
        'tasks.modal.copyLink': '🔗 Copiar enlace',
        'tasks.modal.linkCopied': 'Enlace copiado al portapapeles',
        'tasks.modal.delete': '🗑️ Eliminar tarea',
        'tasks.modal.archive': '📦 Archivar tarea',
        'tasks.modal.restore': '↩️ Restaurar tarea',
        'tasks.contextMenu.open': 'Abrir',
        'tasks.contextMenu.duplicate': 'Duplicar',
        'tasks.contextMenu.copyLink': 'Copiar enlace',
        'tasks.contextMenu.status': 'Estado',
        'tasks.contextMenu.priority': 'Prioridad',
        'tasks.contextMenu.archive': 'Archivar',
        'tasks.contextMenu.delete': 'Eliminar',
        'tasks.contextMenu.selectedCount': '{{count}} tareas seleccionadas',
        'tasks.contextMenu.updatedCount': '{{count}} tareas actualizadas',
        'tasks.filters.showArchived': '📦 Archivadas',
        'tasks.archived.btnLabel': 'Archivadas',
        'tasks.archived.back': '← Volver',
        'tasks.archived.automated': 'Automatizada',
        'tasks.archived.automatedFilter': '🔄 Solo automatizadas',
        'tasks.archived.btnTitle': 'Ver tareas archivadas',
        'tasks.archived.modalTitle': 'Tareas Archivadas',
        'tasks.archived.modalSubtitle': 'Las tareas archivadas están ocultas de todas las vistas activas hasta que se restauren.',
        'tasks.archived.searchPlaceholder': 'Buscar tareas archivadas...',
        'tasks.archived.searchAria': 'Buscar tareas archivadas',
        'tasks.archived.count': '{{count}} tarea(s)',
        'tasks.archived.emptyTitle': 'No hay tareas archivadas',
        'tasks.archived.emptySubtitle': 'Las tareas que archives aparecerán aquí.',
        'tasks.archived.noResults': 'Sin resultados',
        'tasks.archived.noResultsSubtitle': 'Ninguna tarea archivada coincide con tu búsqueda.',
        'tasks.archived.selectAll': 'Seleccionar todas las tareas archivadas',
        'tasks.archived.colTask': 'Tarea',
        'tasks.archived.colProject': 'Proyecto',
        'tasks.archived.colStatus': 'Estado',
        'tasks.archived.colArchived': 'Archivada',
        'tasks.archived.colActions': 'Acciones',
        'tasks.massEdit.archive': '📦 Archivar',
        'tasks.massEdit.restore': '↩️ Restaurar',
        'toast.task.archived': 'Tarea archivada',
        'toast.task.restored': 'Tarea restaurada',
        'toast.task.restoredParentArchived': 'Tarea restaurada. El proyecto "{{project}}" sigue archivado.',
        'toast.tasks.archivedCount': '{{count}} tarea(s) archivada(s)',
        'toast.tasks.restoredCount': '{{count}} tarea(s) restaurada(s)',
        'toast.project.archived.none': 'Proyecto archivado. No se archivaron tareas.',
        'toast.project.archived.one': 'Proyecto archivado. También se archivó 1 tarea.',
        'toast.project.archived.many': 'Proyecto archivado. También se archivaron {count} tareas.',
        'toast.project.restored.none': 'Proyecto restaurado. No se restauraron tareas.',
        'toast.project.restored.one': 'Proyecto restaurado. También se restauró 1 tarea.',
        'toast.project.restored.many': 'Proyecto restaurado. También se restauraron {count} tareas.',
        'tasks.modal.tab.general': 'General',
        'tasks.modal.tab.details': 'Detalles',
        'tasks.modal.tab.history': 'Historial',
        'tasks.modal.titleLabel': 'Título de la tarea',
        'tasks.modal.attachmentsLabel': 'Adjuntos',
        'tasks.modal.attachmentsSupported': 'Compatible: Imágenes (25MB), PDF (750MB), Documentos (25MB)',
        'tasks.modal.attachmentsDropzoneDefault': 'Arrastra y suelta o haz clic para adjuntar (hasta 10)',
        'tasks.modal.attachmentsDropzoneTap': 'Toca para adjuntar (hasta 10)',
        'tasks.modal.linksLabel': 'Enlaces',
        'tasks.modal.attachmentNamePlaceholder': 'Nombre (opcional)',
        'tasks.modal.attachmentUrlPlaceholder': 'URL',
        'tasks.modal.addLink': 'Agregar enlace',
        'tasks.links.relatesTo': 'relacionado con',
        'tasks.links.blocks': 'bloquea',
        'tasks.links.isBlockedBy': 'bloqueado por',
        'tasks.links.dependsOn': 'depende de',
        'tasks.links.openTask': 'Abrir tarea',
        'tasks.links.removeDependency': 'Quitar dependencia',
        'tasks.links.webLink': 'Enlace web',
        'tasks.links.searchTasks': 'Buscar tareas...',
        'tasks.modal.statusLabel': 'Estado',
        'tasks.modal.priorityLabel': 'Prioridad',
        'tasks.modal.tagsLabel': 'Etiquetas',
        'tasks.modal.addTagPlaceholder': 'Añadir etiqueta',
        'tasks.modal.projectLabel': 'Proyecto',
        'tasks.modal.projectOpenTitle': 'Abrir detalles del proyecto',
        'tasks.modal.projectSelect': 'Selecciona un proyecto',
        'tasks.modal.submitCreate': 'Crear tarea',
        'tasks.modal.createAnother': 'Crear otra',
        'tasks.modal.listBulleted': '• Lista',
        'tasks.modal.listNumbered': '1. Lista',
        'tasks.modal.insertDivider': 'Insertar separador horizontal',
        'tasks.modal.insertCheckbox': 'Insertar casilla',
        'tasks.history.emptyTitle': 'Aún no hay cambios',
        'tasks.history.emptySubtitle': 'Los cambios en esta tarea aparecerán aquí',
        'calendar.title': 'Calendario',
        'calendar.today': 'Hoy',
        'calendar.prevMonth': 'Mes anterior',
        'calendar.nextMonth': 'Mes siguiente',
        'calendar.dayItemsTitle': 'Elementos para {date}',
        'calendar.dayItemsProjects': 'Proyectos',
        'calendar.dayItemsTasks': 'Tareas',
        'calendar.dayItems.close': 'Cerrar tareas del día',
        'calendar.dayItems.addTask': '+ Agregar Tarea',
        'calendar.filters.all': 'Todo',
        'calendar.filters.projects': 'Proyectos',
        'calendar.filters.tasks': 'Tareas',
        'calendar.filters.searchProjects': '🔍 Buscar proyectos...',
        'calendar.filters.filtersBtn': 'Filtros',
        'calendar.filters.clearAll': 'Limpiar',
        'calendar.filters.panelTitle': 'Filtros',
        'calendar.filters.drawerTitle': 'Filtros',
        'calendar.filters.sectionSuffix': 'Filtros',
        'calendar.filters.stepProjects': '¿Qué proyectos?',
        'calendar.filters.stepProjectsHint': 'Sus barras y sus tareas se muestran en el calendario',
        'calendar.filters.stepTasks': '¿Qué tareas?',
        'calendar.filters.scopeQuick': 'Atajos de proyectos',
        'calendar.filters.scopeAll': 'Todos',
        'calendar.filters.scopeActive': 'Activos',
        'calendar.filters.pickProjects': 'Elegir proyectos',
        'calendar.filters.projectsCount': 'Proyectos: {count} de {total}',
        'calendar.filters.visibleCount': 'Visibles: {count}',
        'calendar.filters.clearAllFilters': 'Limpiar todo',
        'feedback.title': 'Comentarios e incidencias',
        'feedback.subtitle': 'Reporta errores y sugiere funciones',
        'feedback.saveStatus.saved': 'Guardado',
        'feedback.saveStatus.saving': 'Guardando...',
        'feedback.saveStatus.error': 'No se pudo guardar',
        'feedback.saveStatus.offline': 'Sin conexion',
        'feedback.type.bugLabel': '\u{1F41E} Error',
        'feedback.type.title': 'Tipo de comentario',
        'feedback.type.bugOption': '\u{1F41E} Error',
        'feedback.type.improvementOption': '\u{1F4A1} Mejora',
        'feedback.descriptionPlaceholder': 'Describe el problema o la idea. Puedes pegar un archivo directamente en este campo.',
        'feedback.descriptionPlaceholderShort': 'Describe el problema o la idea.',
        'feedback.screenshotAttachTitle': 'Adjuntar archivo desde el dispositivo',
        'feedback.screenshotDropzoneTap': '\u{1F4F7} Adjuntar',
        'feedback.screenshotDropzoneDefault': '\u{1F4F7} Arrastra o haz clic para adjuntar',
        'feedback.attachmentPreviewTitle': 'Archivo adjunto',
        'feedback.screenshotPreviewTitle': 'Captura adjunta',
        'feedback.screenshotPreviewSubtitle': 'Se guardará con este comentario',
        'feedback.screenshotRemove': 'Eliminar',
        'feedback.screenshotPreviewAlt': 'Vista previa de la captura',
        'feedback.viewScreenshotTitle': 'Ver captura',
        'feedback.uploading': 'Subiendo...',
        'feedback.attachment': 'Adjunto',
        'feedback.pagination.first': 'Primera página',
        'feedback.pagination.prev': 'Página anterior',
        'feedback.pagination.next': 'Página siguiente',
        'feedback.pagination.last': 'Última página',
        'feedback.pagination.showing': 'Mostrando {start}-{end} de {total}',
        'feedback.pagination.pageOf': 'Página {current} de {total}',
        'feedback.addButton': 'Agregar',
        'feedback.pendingTitle': '? Pendiente',
        'feedback.doneTitle': '? Hecho',
        'feedback.delete.title': 'Eliminar comentario',
        'feedback.delete.body': '¿Seguro que deseas eliminar este comentario?',
        'feedback.empty.pending': 'No hay feedback pendiente',
        'feedback.empty.done': 'No hay feedback completado',
        'export.title': 'Exportar datos',
        'export.body': 'Esto descargará una copia completa de todas tus tareas, proyectos y ajustes como un archivo JSON. ¿Seguro que deseas exportar tus datos?',
        'export.confirm': 'Exportar',
        'import.title': 'Importar datos',
        'import.warningTitle': 'ADVERTENCIA: Esto es un REEMPLAZO COMPLETO',
        'import.warningBody': 'Importar datos <strong>reemplazará permanentemente</strong> todos tus datos actuales:',
        'import.warningTasks': 'Todas las tareas',
        'import.warningProjects': 'Todos los proyectos',
        'import.warningSettings': 'Todas las configuraciones y preferencias',
        'import.warningHistory': 'Todos los registros de historial',
        'import.warningIrreversible': 'Esta acción no se puede deshacer. Tus datos actuales se perderán.',
        'import.fileLabel': 'Selecciona el archivo de respaldo para importar:',
        'import.dropzoneDefault': 'Arrastra y suelta o haz clic para seleccionar el archivo de respaldo',
        'import.fileHint': 'Solo se admiten archivos JSON exportados desde Nautilus',
        'import.previewTitle': 'Vista previa de importación:',
        'import.confirmText': 'Para confirmar la importación y reemplazar todos tus datos, escribe <strong>import</strong> a continuación:',
        'import.inputPlaceholder': 'Escribe import aquí',
        'import.error': 'Escribe "import" exactamente para confirmar',
        'import.button': 'Importar datos',
        'import.processing': 'Importando datos...',
        'import.success': '¡Datos importados exitosamente! Todos los datos han sido reemplazados.',
        'import.errorInvalidFile': 'Tipo de archivo inválido. Por favor, selecciona un archivo JSON.',
        'import.errorInvalidFormat': 'Formato de archivo inválido. El archivo no parece ser un respaldo válido de Nautilus.',
        'import.errorParseFailed': 'Error al analizar el archivo. Por favor, asegúrate de que el archivo sea JSON válido.',
        'import.errorReadFailed': 'Error al leer el archivo. Por favor, inténtalo de nuevo.',
        'import.errorNoFile': 'Por favor, selecciona un archivo para importar.',
        'import.errorFailed': 'Error al importar datos',
        'confirm.deleteTask.title': 'Eliminar tarea',
        'confirm.deleteTask.body': 'Esta acción no se puede deshacer. Para confirmar, escribe delete abajo:',
        'confirm.deleteTask.inputPlaceholder': 'Escribe delete aquí',
        'confirm.deleteTask.error': 'Escribe \"delete\" exactamente para confirmar',
        'confirm.unsaved.title': 'Cambios sin guardar',
        'confirm.unsaved.body': 'Tienes cambios sin guardar. ¿Seguro que quieres cerrar y perderlos?',
        'confirm.unsaved.discard': 'Descartar cambios',
        'confirm.review.title': 'Desactivar estado \"En revisión\"',
        'error.saveDataFailed': 'No se pudieron guardar los datos. Inténtalo de nuevo.',
        'error.saveProjectsFailed': 'No se pudieron guardar los proyectos. Inténtalo de nuevo.',
        'error.saveTasksFailed': 'No se pudieron guardar las tareas. Inténtalo de nuevo.',
        'error.saveRetry': 'Reintentando guardado...',
        'error.saveFeedbackFailed': 'No se pudo guardar el feedback. Inténtalo de nuevo.',
        'error.saveProjectColorsFailed': 'No se pudieron guardar los colores del proyecto.',
        'error.saveTaskFailed': 'No se pudo guardar la tarea. Inténtalo de nuevo.',
        'error.saveChangesFailed': 'No se pudieron guardar los cambios. Inténtalo de nuevo.',
        'error.saveTaskPositionFailed': 'No se pudo guardar la posición de la tarea. Inténtalo de nuevo.',
        'error.saveProjectFailed': 'No se pudo guardar el proyecto. Inténtalo de nuevo.',
        'error.notLoggedInResetPin': 'Debes iniciar sesión para restablecer tu PIN',
        'error.resetPinFailed': 'No se pudo restablecer el PIN',
        'error.unauthorized': 'No estás autorizado para realizar esta acción',
        'error.deleteAccountFailed': 'No se pudo eliminar la cuenta. Por favor, inténtalo de nuevo.',
        'success.resetPin': '¡PIN restablecido con éxito!',
        'error.resetPinError': 'Ocurrió un error al restablecer el PIN',
        'error.userNameEmpty': 'El nombre de usuario no puede estar vacío.',
        'error.saveDisplayNameFailed': 'No se pudo guardar el nombre visible. Inténtalo de nuevo.',
        'error.invalidEmail': 'Por favor, introduce un correo válido.',
        'error.saveEmailFailed': 'No se pudo guardar el correo. Inténtalo de nuevo.',
        'error.saveAvatarFailed': 'No se pudo guardar el avatar. Inténtalo de nuevo.',
        'success.settingsSaved': '¡Ajustes guardados correctamente!',
        'error.logoSelectFile': 'Selecciona un archivo de imagen para el logo del espacio de trabajo.',
        'error.logoTooLarge': 'Usa una imagen menor de 2 MB para el logo del espacio de trabajo.',
        'error.imageReadFailed': 'No se pudo leer la imagen seleccionada.',
        'error.imageLoadFailed': 'No se pudo cargar la imagen seleccionada.',
        'error.logoUploadFailed': 'Error al subir el logo: {message}',
        'error.cropInvalid': 'Error: el estado del recorte no es válido.',
        'error.cropTooLarge': 'La imagen recortada es demasiado grande. Selecciona un área más pequeña o usa una imagen más pequeña.',
        'success.cropApplied': '¡Imagen recortada y aplicada correctamente!',
        'success.logoCroppedApplied': '¡Logo del espacio de trabajo recortado y aplicado correctamente!',
        'error.cropFailed': 'Error al recortar la imagen: {message}',
        'error.avatarSelectFile': 'Selecciona un archivo de imagen para tu avatar.',
        'error.avatarTooLarge': 'Usa una imagen menor de 2 MB para tu avatar.',
        'error.avatarUploadFailed': 'No se pudo subir el avatar. Inténtalo de nuevo.',
        'error.endDateBeforeStart': 'La fecha de fin no puede ser anterior a la fecha de inicio',
        'error.startDateAfterEnd': 'La fecha de inicio no puede ser posterior a la fecha de fin',
        'error.feedbackAttachImage': 'Adjunta un archivo de imagen.',
        'error.feedbackReadImage': 'No se pudo leer la imagen. Inténtalo de nuevo.',
        'error.feedbackStatusFailed': 'No se pudo actualizar el estado del feedback. Inténtalo de nuevo.',
        'error.attachmentSaveFailed': 'No se pudo guardar el adjunto. Inténtalo de nuevo.',
        'error.attachmentLoadFailed': 'No se pudo cargar la imagen: {message}',
        'success.fileDownloaded': '¡Archivo descargado!',
        'error.fileDownloadFailed': 'No se pudo descargar el archivo: {message}',
        'success.attachmentDeletedFromStorage': '{name} eliminado del almacenamiento',
        'error.fileDeleteFailed': 'No se pudo eliminar el archivo del almacenamiento',
        'success.attachmentRemoved': 'Adjunto eliminado',
        'success.linkAdded': '¡Enlace de tarea agregado correctamente!',
        'success.linkRemoved': '¡Enlace de tarea eliminado correctamente!',
        'error.linkAlreadyExists': 'Este enlace ya existe',
        'error.linkNotFound': 'Enlace no encontrado',
        'error.cannotLinkToSelf': 'No se puede enlazar una tarea consigo misma',
        'error.invalidTaskIds': 'IDs de tarea inválidos',
        'error.fileSizeTooLarge': 'El tamaño del archivo debe ser menor de {maxMB} MB. Elige uno más pequeño.',
        'error.tooManyFiles': 'Máximo {max} archivos por subida. Por favor, selecciona {max} o menos a la vez.',
        'success.fileUploaded': '¡Archivo subido correctamente!',
        'error.fileUploadFailed': 'Error al subir el archivo: {message}',
        'error.selectFile': 'Selecciona un archivo',
        'error.saveTagFailed': 'No se pudo guardar la etiqueta. Inténtalo de nuevo.',
        'error.removeTagFailed': 'No se pudo eliminar la etiqueta. Inténtalo de nuevo.',
        'error.openScreenshotFailed': 'No se pudo abrir la captura',
        'error.cfOutageBanner': 'Los cambios no se pueden guardar ahora. Evita crear contenido nuevo hasta que se resuelva.',
        'error.cfOutageContact': 'Contactar soporte',
        'error.cfOutageBlocked': 'El guardado no está disponible ahora mismo. Espera e inténtalo de nuevo.',
        'history.sort.newest': '↑ Más recientes primero',
        'history.sort.oldest': '↓ Más antiguos primero',
        'history.field.title': 'Título',
        'history.field.name': 'Nombre',
        'history.field.description': 'Descripción',
        'history.field.status': 'Estado',
        'history.field.priority': 'Prioridad',
        'history.field.category': 'Categoría',
        'history.field.startDate': 'Fecha de inicio',
        'history.field.endDate': 'Fecha de fin',
        'history.field.link': 'Enlace',
        'history.field.task': 'Enlace',
        'history.field.projectId': 'Proyecto',
        'history.field.tags': 'Etiquetas',
        'history.field.attachments': 'Adjuntos',
        'history.actor.kai': 'Kai',
        'history.actor.user': 'Usuario',
        'history.attribution.by': 'Por {name}',
        'history.action.created': 'Creado',
        'history.action.deleted': 'Eliminado',
        'history.link.added': 'Agregado',
        'history.link.removed': 'Eliminado',
        'history.entity.task': 'tarea',
        'history.entity.project': 'proyecto',
        'history.value.empty': 'vacío',
        'history.value.none': 'ninguno',
        'history.change.beforeLabel': 'Antes:',
        'history.change.afterLabel': 'Después:',
        'history.change.notSet': 'Sin establecer',
        'history.change.removed': 'Eliminado',
        'history.tags.none': 'Sin etiquetas',
        'history.attachments.none': 'Sin archivos adjuntos',
        'history.attachments.countSingle': '{count} archivo',
        'history.attachments.countPlural': '{count} archivos',
        'history.project.fallback': 'Proyecto #{id}',
        'history.change.arrow': '→',
        'menu.openMenu': 'Abrir menú',
        'menu.language': 'Idioma',
        'menu.darkMode': 'Modo oscuro',
        'menu.lightMode': 'Modo claro',
        'menu.settings': 'Configuración',
        'menu.help': 'Ayuda',
        'menu.signOut': 'Cerrar sesión',
        'nav.overview': 'Resumen',
        'nav.dashboard': 'Panel',
        'nav.collapseSidebar': 'Colapsar barra lateral',
        'nav.expandSidebar': 'Expandir barra lateral',
        'nav.updates': 'Notas de versión',
        'nav.calendar': 'Calendario',
        'nav.work': 'Trabajo',
        'nav.projects': 'Proyectos',
        'nav.docs': 'Docs',
        'nav.hideDocs': 'Ocultar docs',
        'nav.showDocs': 'Mostrar docs',
        'nav.allTasks': 'Todas las tareas',
        'nav.automations': 'Automatizaciones',
        'nav.feedback': 'Comentarios',
        'automations.title': 'Automatizaciones',
        'automations.subtitle': 'Reglas recurrentes y futuras automatizaciones del flujo de trabajo',
        'automations.newRule': '+ Nueva regla',
        'automations.emptyTitle': 'Todavía no hay automatizaciones',
        'automations.emptySubtitle': 'Crea una regla recurrente para generar tareas automáticamente según un calendario.',
        'automations.emptyCta': 'Crear recurrente',
        'automations.action.deleteConfirm': '¿Eliminar esta regla?',
        'automations.action.deleteConfirmYes': 'Eliminar',
        'automations.status.active': 'Activa',
        'automations.status.paused': 'Pausada',
        'automations.action.pause': 'Pausar',
        'automations.action.resume': 'Reanudar',
        'automations.action.edit': 'Editar',
        'automations.action.delete': 'Eliminar',
        'automations.action.audit': 'Ver tareas generadas',
        'automations.action.auditArchive': 'Ver tareas archivadas',
        'automations.audit.title': 'Tareas generadas',
        'automations.audit.archiveTitle': 'Tareas archivadas',
        'automations.audit.emptyTitle': 'Todavía no se generaron tareas',
        'automations.audit.emptyBody': 'Esta regla aún no ha creado tareas, o las tareas que creó ya se eliminaron.',
        'automations.audit.archiveEmptyTitle': 'Todavía no se archivaron tareas',
        'automations.audit.archiveEmptyBody': 'Esta regla aún no ha archivado tareas, o las tareas que archivó ya se restauraron o eliminaron.',
        'automations.audit.searchAria': 'Buscar tareas en este historial',
        'automations.audit.searchPlaceholder': 'Buscar tareas o proyectos...',
        'automations.audit.sortAria': 'Ordenar historial',
        'automations.audit.sortNewest': 'Más recientes primero',
        'automations.audit.sortOldest': 'Más antiguas primero',
        'automations.audit.sortTitle': 'Título de tarea A–Z',
        'automations.audit.sortProject': 'Proyecto A–Z',
        'automations.audit.count': '{count} tareas',
        'automations.audit.noResultsTitle': 'No hay tareas coincidentes',
        'automations.audit.noResultsBody': 'Prueba con otro nombre de tarea o proyecto.',
        'automations.audit.task': 'Tarea',
        'automations.audit.project': 'Proyecto',
        'automations.audit.archivedDate': 'Archivada',
        'automations.audit.createdDate': 'Creada',
        'automations.audit.status': 'Estado',
        'automations.summary.nextRun': 'Siguiente: {date}',
        'automations.recurrence.generatedBy': 'Generada por una automatización',
        'automations.group.recurringTask': 'Tareas recurrentes',
        'automations.type.recurringTask': 'Tarea recurrente',
        'automations.type.recurringTaskHint': 'Genera una tarea según un calendario repetitivo.',
        'automations.typePicker.title': 'Elige un tipo de automatización',
        'automations.typePicker.comingSoon': 'Próximamente más',
        'automations.typePicker.close': 'Cerrar',
        'automations.type.dueSoon': 'Recordatorio de vencimiento',
        'automations.type.autoArchive': 'Archivar automáticamente tareas hechas',
        'automations.type.autoArchiveHint': 'Archiva las tareas hechas automáticamente.',
        'automations.group.autoArchive': 'Auto-archivado',
        'autoArchive.unit.day': 'día',
        'autoArchive.unit.days': 'días',
        'autoArchive.unit.task': 'tarea',
        'autoArchive.unit.tasks': 'tareas',
        'autoArchive.createTitle': 'Crear regla de auto-archivado',
        'autoArchive.editTitle': 'Editar regla de auto-archivado',
        'autoArchive.modalSubtitle': 'Aparta las tareas terminadas automáticamente',
        'autoArchive.trigger': 'Cuándo archivar',
        'autoArchive.mode.doneAge': 'Tras un tiempo en Hecho',
        'autoArchive.mode.onDate': 'En una fecha concreta',
        'autoArchive.mode.periodic': 'Limpieza periódica',
        'autoArchive.doneAge.label': 'Archivar una tarea cuando lleve en Hecho',
        'autoArchive.doneAge.hint': 'Se comprueba cada vez que se carga Nautilus.',
        'autoArchive.onDate.label': 'Archivar todas las tareas hechas en esta fecha',
        'autoArchive.onDate.hint': 'Se ejecuta la próxima vez que Nautilus se abra en esa fecha o después, y luego se desactiva.',
        'autoArchive.periodic.label': 'Limpiar tareas hechas',
        'autoArchive.periodic.daily': 'A diario',
        'autoArchive.periodic.weekly': 'Semanal',
        'autoArchive.periodic.monthly': 'Mensual',
        'autoArchive.periodic.weeklyOn': 'El',
        'autoArchive.periodic.monthlyOnDay': 'El día',
        'autoArchive.periodic.monthlyLastDay': 'Último día del mes',
        'autoArchive.periodic.hint': 'Archiva todas las tareas hechas en cada ejecución.',
        'autoArchive.scope.includeProjectTasks': 'Incluir tareas dentro de proyectos',
        'autoArchive.scope.hint': 'Por defecto, solo se archivan las tareas sin proyecto.',
        'autoArchive.preview.empty': 'Elige un disparador arriba para previsualizar la regla.',
        'autoArchive.preview.doneAge': 'Archiva las tareas hechas {count} {unit} después de completarse.',
        'autoArchive.preview.onDate': 'Cuando Nautilus se abra el {date} o después, archivará todas las tareas hechas y luego se desactivará.',
        'autoArchive.preview.periodicDaily': 'Archiva todas las tareas hechas una vez al día.',
        'autoArchive.preview.periodicWeekly': 'Archiva todas las tareas hechas cada {days}.',
        'autoArchive.preview.periodicWeeklyNoDays': 'Archiva todas las tareas hechas una vez por semana.',
        'autoArchive.preview.periodicMonthly': 'Archiva todas las tareas hechas el día {day} de cada mes.',
        'autoArchive.preview.periodicMonthlyNoDay': 'Archiva todas las tareas hechas una vez al mes.',
        'autoArchive.preview.matchNow': 'Coincide con {count} {unit} ahora mismo.',
        'autoArchive.preview.matchNone': 'Ninguna tarea hecha coincide ahora mismo.',
        'autoArchive.save': 'Guardar regla',
        'autoArchive.summary.doneAge': 'Archivar tareas hechas tras {count} {unit}',
        'autoArchive.summary.onDate': 'Archivar tareas hechas el {date}',
        'autoArchive.summary.periodicDaily': 'Limpieza diaria de tareas hechas',
        'autoArchive.summary.periodicWeekly': 'Semanal · {days}',
        'autoArchive.summary.periodicWeeklyShort': 'Limpieza semanal de tareas hechas',
        'autoArchive.summary.periodicMonthly': 'Mensual · día {day}',
        'autoArchive.summary.periodicMonthlyShort': 'Limpieza mensual de tareas hechas',
        'autoArchive.meta.continuous': 'Se ejecuta al cargar la app',
        'autoArchive.meta.scheduled': 'Programada para el {date}',
        'autoArchive.meta.completed': 'Completada',
        'autoArchive.meta.nextRun': 'Próxima ejecución {date}',
        'autoArchive.meta.archivedTotal': '{count} archivadas hasta ahora',
        'autoArchive.validation.mode': 'Elige cuándo deben archivarse las tareas.',
        'autoArchive.validation.threshold': 'Introduce un número de días entre 1 y 3650.',
        'autoArchive.validation.date': 'Elige una fecha de archivado válida.',
        'autoArchive.validation.pattern': 'Elige una frecuencia de limpieza.',
        'toast.autoArchive.created': 'Regla de auto-archivado creada',
        'toast.autoArchive.updated': 'Regla de auto-archivado actualizada',
        'toast.autoArchive.archivedCount': '{count} {unit} auto-archivadas',
        'automations.deleted': 'Automatización eliminada',
        'recurrence.save': 'Guardar',
        'recurrence.modalSubtitle': 'Programa una tarea para que se repita automáticamente',
        'recurrence.section.source': 'Origen',
        'recurrence.source.createNewTask': '+ Crear una tarea nueva como plantilla',
        'recurrence.source.searchPlaceholder': 'Buscar tareas...',
        'recurrence.source.noMatches': 'Sin coincidencias',
        'recurrence.summary.label': 'Resumen',
        'recurrence.summary.empty': 'Completa los campos para previsualizar la regla.',
        'recurrence.summary.daily': 'Crea "{title}" todos los días',
        'recurrence.summary.weekly': 'Crea "{title}" cada {days}',
        'recurrence.summary.monthly': 'Crea "{title}" cada mes',
        'recurrence.summary.everyX': 'Crea "{title}" cada {count} {unit}',
        'recurrence.summary.firstOccurrence': 'primera ocurrencia {date}',
        'recurrence.summary.until': 'hasta el {date}',
        'recurrence.createTitle': 'Crear tarea recurrente',
        'recurrence.editTitle': 'Editar regla recurrente',
        'recurrence.sourceTask': 'Tarea base',
        'recurrence.ruleName': 'Nombre de la regla',
        'recurrence.ruleNamePlaceholder': 'De forma predeterminada se usa el título de la tarea base',
        'recurrence.sourceTaskHint': 'Elige la tarea plantilla que debe copiar esta regla.',
        'recurrence.sourceSummary': 'La tarea base original ya no existe.',
        'recurrence.sourceTaskMissingOption': 'Elige una tarea de reemplazo',
        'recurrence.sourceTaskMissingHelp': 'La tarea base original ya no existe. Elige una tarea de reemplazo antes de guardar.',
        'recurrence.repeat': 'Repetir',
        'recurrence.pattern.daily': 'Diaria',
        'recurrence.pattern.weekly': 'Semanal',
        'recurrence.pattern.monthly': 'Mensual',
        'recurrence.pattern.everyXDays': 'Personalizado',
        'recurrence.pattern.everyXDaysLabel': 'Cada {count} {unit}',
        'recurrence.pattern.everyXDaysPrefix': 'Cada',
        'recurrence.pattern.everyXDaysSuffix': 'días',
        'recurrence.intervalUnitAria': 'Unidad del intervalo',
        'recurrence.intervalUnit.days': 'Días',
        'recurrence.intervalUnit.months': 'Meses',
        'recurrence.intervalUnit.years': 'Años',
        'recurrence.section.dates': 'Fechas de tarea',
        'recurrence.taskDates.startLabel': 'Fecha de inicio',
        'recurrence.taskDates.selectOption': 'Selecciona una opción...',
        'recurrence.taskDates.startSameDay': 'Mismo día de creación',
        'recurrence.taskDates.startNone': 'Sin fecha de inicio',
        'recurrence.taskDates.endLabel': 'Fecha de fin',
        'recurrence.taskDates.endNone': 'Sin fecha de fin',
        'recurrence.taskDates.endSameDay': 'Mismo día de creación',
        'recurrence.taskDates.endOffset': 'Días desde el inicio',
        'recurrence.taskDates.endOffsetCreation': 'Días desde la creación',
        'recurrence.taskDates.days': 'días',
        'recurrence.automationEnd.label': 'Terminar el',
        'recurrence.automationEnd.never': 'Nunca',
        'recurrence.automationEnd.onDate': 'En fecha',
        'recurrence.summary.taskDates': '· empieza el {start}',
        'recurrence.summary.taskDuration': '· duración {n}d',
        'recurrence.weekday.sun': 'Dom',
        'recurrence.weekday.mon': 'Lun',
        'recurrence.weekday.tue': 'Mar',
        'recurrence.weekday.wed': 'Mié',
        'recurrence.weekday.thu': 'Jue',
        'recurrence.weekday.fri': 'Vie',
        'recurrence.weekday.sat': 'Sáb',
        'recurrence.endsOptional': 'Finaliza (opcional)',
        'recurrence.validation.noTask': 'Crea al menos una tarea antes de añadir una automatización.',
        'recurrence.validation.invalidRule': 'Completa los campos de recurrencia antes de guardar.',
        'recurrence.validation.sourceTaskMissing': 'Elige una tarea de reemplazo para esta automatización antes de guardar.',
        'recurrence.validation.taskStartDateRequired': 'Elige una opción para la fecha de inicio.',
        'recurrence.validation.taskStartDateRequiredForOffset': 'Elige el mismo día de la creación para la fecha de inicio cuando uses días después del inicio.',
        'recurrence.validation.taskEndDateRequired': 'Elige una opción para la fecha de fin.',
        'recurrence.validation.intervalDaysRequired': 'Introduce un intervalo de repetición válido.',
        'recurrence.validation.endDateRequired': 'Elige una fecha de fin.',
        'recurrence.validation.endDateOffsetRequired': 'Indica cuántos días después del inicio debe terminar la tarea.',
        'recurrence.saved.created': 'Regla recurrente creada',
        'recurrence.saved.updated': 'Regla recurrente actualizada',
        'recurrence.saved.deleted': 'Regla recurrente eliminada',
        'recurrence.modalSubtitle.redesign': 'Programa una tarea para que se repita automáticamente',
        'recurrence.searchExisting': 'Buscar existente',
        'recurrence.createNew': 'Crear nueva',
        'recurrence.searchPlaceholder': 'Buscar tareas…',
        'recurrence.newTaskPlaceholder': 'Título de nueva tarea…',
        'recurrence.repeats': 'Se repite',
        'recurrence.freq.daily': 'Diario',
        'recurrence.freq.weekly': 'Semanal',
        'recurrence.freq.monthly': 'Mensual',
        'recurrence.freq.custom': 'Personalizado',
        'recurrence.every': 'Cada',
        'recurrence.on': 'El',
        'recurrence.after': 'Después de',
        'recurrence.occurrences': 'ocurrencias',
        'recurrence.unit.days': 'días',
        'recurrence.unit.weeks': 'semanas',
        'recurrence.unit.months': 'meses',
        'recurrence.monthly.sameDate': 'El mismo día de cada mes',
        'recurrence.monthly.sameWeekday': 'El mismo día de la semana cada mes',
        'recurrence.day.mo': 'Lu',
        'recurrence.day.tu': 'Ma',
        'recurrence.day.we': 'Mi',
        'recurrence.day.th': 'Ju',
        'recurrence.day.fr': 'Vi',
        'recurrence.day.sa': 'Sá',
        'recurrence.day.su': 'Do',
        'recurrence.taskDates': 'Fechas de tarea',
        'recurrence.firstOccurrence': 'Primera ocurrencia',
        'recurrence.duration': 'Duración (días)',
        'recurrence.ends': 'Finaliza',
        'recurrence.end.never': 'Nunca',
        'recurrence.end.onDate': 'En fecha',
        'recurrence.end.afterN': 'Después de N veces',
        'recurrence.saveRule': 'Guardar regla',
        'recurrence.errorNoDays': 'Selecciona al menos un día',
        'recurrence.summary.pickDay': 'Elige al menos un día de la semana',
        'recurrence.preview.repeats': 'Se repite {repeat}',
        'recurrence.preview.everyDay': 'todos los días',
        'recurrence.preview.everyNDays': 'cada {count} días',
        'recurrence.preview.everyWeekOn': 'cada {days}',
        'recurrence.preview.everyNWeeksOn': 'cada {count} semanas los {days}',
        'recurrence.preview.monthlyDate': 'el mismo día de cada mes',
        'recurrence.preview.monthlyWeekday': 'el mismo día de la semana cada mes',
        'recurrence.preview.everyNUnit': 'cada {count} {unit}',
        'recurrence.preview.firstOccurrence': 'empezando {date}',
        'recurrence.preview.until': 'hasta el {date}',
        'recurrence.preview.forNOccurrences': 'durante {count} ocurrencias',
        'recurrence.preview.forever': 'para siempre',
        'recurrence.validation.firstOccurrenceRequired': 'Elige una fecha de primera ocurrencia.',
        'recurrence.validation.firstOccurrencePast': 'La primera ocurrencia no puede estar en el pasado.',
        'updates.title': 'Notas de versión',
        'updates.subtitle': 'Último registro de cambios en Nautilus',
        'updates.latestLabel': 'Ultima version',
        'updates.historyLabel': 'Registro de versiones',
        'updates.sections.new': 'Novedades',
        'updates.sections.improvements': 'Mejoras',
        'updates.sections.fixes': 'Correcciones',
        'updates.empty': 'Todavia no hay notas de version.',
        'updates.sectionEmpty': 'Todavía no hay entradas.',
        'updates.historyEmpty': 'Sin versiones anteriores.',
        'notifications.title': 'Notificaciones',
        'notifications.toggle': 'Notificaciones',
        'notifications.today': 'Hoy',
        'notifications.yesterday': 'Ayer',
        'notifications.clearAll': 'Limpiar todo',
        'notifications.releaseTitle': 'Nueva versión',
        'notifications.releaseCta': 'Ver actualizaciones',
        'notifications.releaseMeta': 'Publicado {date}',
        'notifications.dueTodayTitle': 'Vence hoy',
        'notifications.dueTodayCta': 'Ver tareas',
        'notifications.dueTodayMetaOne': '{count} tarea vence hoy',
        'notifications.dueTodayMetaMany': '{count} tareas vencen hoy',
        'notifications.dueTodayMore': 'y {count} tareas más',
        'notifications.sectionStarting': 'POR EMPEZAR',
        'notifications.sectionDue': 'VENCEN',
        'notifications.moreStarting': '+{count} por empezar más',
        'notifications.moreDue': '+{count} por vencer más',
        'notifications.viewStarting': 'Ver {count} por empezar',
        'notifications.viewDue': 'Ver {count} por vencer',
        'notifications.dismiss': 'Descartar',
        'notifications.empty': 'Todo al día.',
        'settings.title': 'Configuración',
        'settings.subtitle': 'Administra tus preferencias y la configuración de la aplicación',
        'settings.tabs.ariaLabel': 'Secciones de configuración',
        'settings.tabs.account': 'Cuenta',
        'settings.tabs.preferences': 'Preferencias',
        'settings.tabs.notifications': 'Notificaciones',
        'settings.tabs.security': 'Seguridad',
        'settings.tabs.data': 'Datos',
        'settings.tabs.advanced': 'Avanzado',
        'settings.panel.account.subtitle': 'Tu nombre, foto y correo',
        'settings.panel.preferences.subtitle': 'Idioma y comportamiento de las tareas',
        'settings.panel.notifications.subtitle': 'Cuándo y cómo te avisa Nautilus',
        'settings.panel.security.subtitle': 'Cómo inicias sesión',
        'settings.panel.data.subtitle': 'Haz copia, restaura o elimina tus datos',
        'settings.panel.advanced.subtitle': 'Herramientas de diagnóstico',
        'settings.advancedNote': 'Solo es necesario al diagnosticar problemas con soporte.',
        'settings.tabs.general': 'General',
        'settings.mobileBack': 'Toda la configuración',
        'settings.group.signInSecurity': 'Inicio de sesión y seguridad',
        'settings.group.languageWorkspace': 'Idioma y espacio de trabajo',
        'settings.group.tasks': 'Tareas',
        'settings.group.reminderOptions': 'Opciones de recordatorio',
        'settings.group.backup': 'Copia de seguridad',
        'settings.group.docs': 'Docs',
        'settings.unsavedChanges': 'Cambios sin guardar',
        'settings.deleteAccount.backupHint': '¿Podrías necesitar tu trabajo más adelante? Descarga antes una copia desde Datos.',
        'settings.deleteAccount.keep': 'Conservar mi cuenta',
        'settings.section.profile': 'Perfil',
        'settings.displayName': 'Nombre para mostrar',
        'settings.displayNameHint': 'Se muestra en toda la aplicación',
        'settings.placeholder.displayName': 'Escribe tu nombre para mostrar',
        'settings.email': 'Correo electrónico',
        'settings.emailHint': 'Para tu cuenta y los recordatorios',
        'settings.placeholder.email': 'Escribe tu correo',
        'settings.avatar': 'Avatar',
        'settings.avatarHint': 'Máx. 2 MB, se muestra en círculo',
        'settings.avatarRemoveTitle': 'Eliminar avatar',
        'settings.workspaceLogo': 'Logo',
        'settings.workspaceLogoHint': 'Imagen cuadrada, máx. 2 MB',
        'settings.workspaceLogoRemoveTitle': 'Eliminar logo personalizado',
        'settings.section.application': 'Aplicación',
        'settings.enableReviewStatus': 'Habilitar estado En revisión',
        'settings.enableReviewStatusHintPrefix': 'Añade el estado',
        'settings.enableReviewStatusHintSuffix': 'a columnas y filtros',
        'settings.calendarIncludeBacklog': 'Mostrar backlog en calendario',
        'settings.autoStartDate': 'Autocompletar fecha de inicio',
        'settings.autoStartDateHint': 'Al pasar a "En progreso", si está vacía',
        'settings.autoEndDate': 'Autocompletar fecha de fin',
        'settings.autoEndDateHint': 'Al pasar a "Hecho", si está vacía',
        'settings.debugLogs': 'Habilitar logs de depuración',
        'settings.debugLogsHint': 'Logs de diagnóstico en la consola del navegador',
        'settings.diagOverlay': 'Diagnóstico en pantalla',
        'settings.diagOverlayHint': 'Panel de diagnóstico solo en este dispositivo. Se aplica al instante.',
        'settings.historySortOrder': 'Orden de historial',
        'settings.historySortOrderHint': 'Para tareas y proyectos',
        'settings.historySortNewest': 'Más reciente primero',
        'settings.historySortOldest': 'Más antiguo primero',
        'settings.language': 'Idioma',
        'settings.section.notifications': 'Notificaciones',
        'settings.emailNotifications': 'Notificaciones por correo',
        'settings.emailNotificationsHint': 'Correos de recordatorio de vencimientos',
        'settings.weekdaysOnly': 'Solo días laborables',
        'settings.weekdaysOnlyHint': 'Omitir correos sábado y domingo',
        'settings.includeWeekAhead': 'Avisar una semana antes',
        'settings.includeWeekAheadHint': 'Para tareas que vencen en 7 días',
        'settings.includeStartDates': 'Avisar al empezar una tarea',
        'settings.includeStartDatesHint': 'Recordatorio el día de inicio',
        'settings.includeBacklog': 'Incluir tareas en backlog',
        'settings.includeBacklogHint': 'En correos y notificaciones de la app',
        'settings.sendTime': 'Hora de envío',
        'settings.sendTimeHint': '08:00–18:00, cada 30 min',
        'settings.timeZone': 'Zona horaria',
        'settings.timeZoneHint': 'Se ajusta al horario de verano',
        'settings.timeZoneSearch': 'Buscar zona horaria',
        'settings.timeZoneNoResults': 'Sin resultados',
        'settings.timeZone.option.newYork': 'Nueva York, EE. UU.',
        'settings.timeZone.option.chicago': 'Chicago, EE. UU.',
        'settings.timeZone.option.denver': 'Denver, EE. UU.',
        'settings.timeZone.option.phoenix': 'Phoenix, EE. UU.',
        'settings.timeZone.option.losAngeles': 'Los Ángeles, EE. UU.',
        'settings.timeZone.option.anchorage': 'Anchorage, EE. UU.',
        'settings.timeZone.option.honolulu': 'Honolulu, EE. UU.',
        'settings.timeZone.option.mexicoCity': 'Ciudad de México, México',
        'settings.timeZone.option.saoPaulo': 'Sao Paulo, Brasil',
        'settings.timeZone.option.argentina': 'Buenos Aires, Argentina',
        'settings.timeZone.option.canary': 'Islas Canarias',
        'settings.timeZone.option.london': 'Londres, Reino Unido',
        'settings.timeZone.option.spain': 'Madrid, España',
        'settings.timeZone.option.paris': 'París, Francia',
        'settings.timeZone.option.utc': 'UTC',
        'settings.section.security': 'Seguridad',
        'settings.authMethod': 'Método de autenticación',
        'settings.authMethodHintPin': 'Actualmente usando PIN',
        'settings.authMethodHintPassword': 'Actualmente usando contraseña',
        'settings.switchToPassword': 'Cambiar a contraseña',
        'settings.switchToPin': 'Cambiar a PIN',
        'settings.pinManagement': 'Gestión de PIN',
        'settings.pinManagementHint': 'Elige un nuevo PIN de 4 dígitos',
        'settings.passwordManagement': 'Gestión de contraseña',
        'settings.passwordManagementHint': 'Cambia tu contraseña',
        'settings.resetPinButton': 'Restablecer PIN',
        'settings.changePasswordButton': 'Cambiar contraseña',
        'settings.switchAuth.title': 'Cambiar método de autenticación',
        'settings.switchAuth.verifyCurrentPin': 'Verifica tu PIN actual',
        'settings.switchAuth.verifyCurrentPassword': 'Verifica tu contraseña actual',
        'settings.switchAuth.enterNewPassword': 'Ingresa tu nueva contraseña',
        'settings.switchAuth.confirmNewPassword': 'Confirma tu nueva contraseña',
        'settings.switchAuth.enterNewPin': 'Ingresa tu nuevo PIN',
        'settings.switchAuth.confirmNewPin': 'Confirma tu nuevo PIN',
        'settings.switchAuth.success': '¡Método de autenticación cambiado!',
        'settings.switchAuth.successPassword': 'Cambiado a autenticación con contraseña',
        'settings.switchAuth.successPin': 'Cambiado a autenticación con PIN',
        'settings.changePassword.title': 'Cambiar contraseña',
        'settings.changePassword.currentLabel': 'Contraseña actual',
        'settings.changePassword.newLabel': 'Nueva contraseña',
        'settings.changePassword.confirmLabel': 'Confirmar nueva contraseña',
        'settings.changePassword.success': '¡Contraseña cambiada con éxito!',
        'settings.confirmIdentity.title': 'Confirma que eres tú',
        'settings.confirmIdentity.emailHint': 'Para cambiar tu correo necesitas tu PIN o contraseña actual.',
        'settings.confirmIdentity.deleteHint': 'Para eliminar tu cuenta necesitas tu PIN o contraseña actual.',
        'settings.confirmIdentity.pinLabel': 'PIN actual',
        'settings.confirmIdentity.passwordLabel': 'Contraseña actual',
        'settings.confirmIdentity.confirm': 'Confirmar',
        'settings.confirmIdentity.required': 'Introduce tu PIN o contraseña actual.',
        'settings.confirmIdentity.incorrect': 'PIN o contraseña incorrectos.',
        'settings.confirmIdentity.tooManyAttempts': 'Demasiados intentos. Inténtalo más tarde.',
        'settings.section.dataManagement': 'Gestión de datos',
        'settings.exportData': 'Exportar datos',
        'settings.exportDataHint': 'Tareas, proyectos y configuración en JSON',
        'settings.exportButton': 'Exportar',
        'settings.importData': 'Importar datos',
        'settings.importDataHint': 'Reemplaza todos tus datos actuales. No se puede deshacer.',
        'settings.importButton': 'Importar datos',
        'settings.docsTrash': 'Papelera de docs',
        'settings.docsTrashHint': 'Restaura páginas de docs eliminadas',
        'settings.docsTrashButton': 'Abrir papelera',
        'settings.section.dangerZone': 'Zona de peligro',
        'settings.deleteAccount': 'Eliminar cuenta',
        'settings.deleteAccountHint': 'Elimina tu cuenta y todos sus datos. No se puede deshacer.',
        'settings.deleteAccountButton': 'Eliminar cuenta',
        'settings.deleteAccount.title': 'Eliminar cuenta',
        'settings.deleteAccount.warning': 'Esta acción <strong>no se puede deshacer</strong>. Perderás permanentemente:',
        'settings.deleteAccount.loseTasks': 'Todas tus tareas',
        'settings.deleteAccount.loseProjects': 'Todos tus proyectos',
        'settings.deleteAccount.loseData': 'Todos tus datos de cuenta y configuración',
        'settings.deleteAccount.confirmText': 'Para confirmar la eliminación de la cuenta, escribe <strong>delete</strong> a continuación:',
        'settings.deleteAccount.inputPlaceholder': 'Escribe delete aquí',
        'settings.deleteAccount.error': 'Escribe "delete" exactamente para confirmar',
        'settings.deleteAccount.button': 'Eliminar cuenta',
        'settings.deleteAccount.success': 'Cuenta eliminada exitosamente. Redirigiendo al inicio de sesión...',
        'settings.cancelButton': 'Cancelar',
        'settings.saveButton': 'Guardar configuración',
        'settings.avatarUploadDefault': 'Arrastra y suelta o haz clic para subir un avatar',
        'settings.avatarUploadChange': 'Cambiar avatar',
        'settings.avatarUploadAriaUpload': 'Subir avatar',
        'settings.avatarUploadAriaChange': 'Cambiar avatar',
        'settings.logoUploadDefault': 'Arrastra y suelta o haz clic para subir un logo',
        'settings.logoUploadChange': 'Cambiar logo',
        'settings.logoUploadAriaUpload': 'Subir logo',
        'settings.logoUploadAriaChange': 'Cambiar logo',
        'dashboard.title': 'Panel',
        'dashboard.greeting.morning': 'Buenos días',
        'dashboard.greeting.afternoon': 'Buenas tardes',
        'dashboard.greeting.evening': 'Buenas noches',
        'dashboard.greeting.morningName': 'Buenos días, {name}',
        'dashboard.greeting.afternoonName': 'Buenas tardes, {name}',
        'dashboard.greeting.eveningName': 'Buenas noches, {name}',
        'dashboard.greeting.summary': '{overdue} vencidas · {today} vencen hoy · {high} alta prioridad',
        'dashboard.greeting.summaryOne': '{count} tarea necesita atención antes de que se retrase más.',
        'dashboard.greeting.summaryMany': '{count} tareas necesitan atención antes de que se retrasen más.',
        'dashboard.greeting.allClear': 'Estás al día por hoy.',
        'dashboard.metrics.aria': 'Métricas prioritarias del panel',
        'dashboard.summary.overdue': 'Vencidas',
        'dashboard.summary.dueToday': 'Vencen hoy',
        'dashboard.summary.highPriority': 'Alta prioridad',
        'dashboard.summary.inProgress': 'En progreso',
        'dashboard.summary.completed': 'Completadas',
        'dashboard.summary.sub.open': 'de {count} abiertas',
        'dashboard.summary.sub.thisMonth': 'este mes',
        'dashboard.summary.sub.highOverdue': '{count} vencidas',
        'dashboard.kai.title': 'Ask Kai',
        'dashboard.kai.launch': 'Abrir Kai',
        'dashboard.kai.subtitle': 'Planifica, crea tareas, redacta documentos',
        'dashboard.kai.placeholder': 'Planifica mi día, redacta un documento o resume un proyecto...',
        'dashboard.kai.submit': 'Preguntar a Kai',
        'dashboard.kai.chipPlanWeek': 'Planifica mi día',
        'dashboard.kai.chipSummarize': 'Resumir proyectos',
        'dashboard.kai.chipAddTasks': 'Agregar tareas',
        'dashboard.kai.promptPlanWeek': 'Planifica mi semana: revisa mis tareas vencidas, las que vencen hoy y las de alta prioridad, y propón un plan enfocado para los próximos 7 días.',
        'dashboard.kai.promptSummarize': 'Resume el estado actual de mis proyectos activos, destacando el progreso, los bloqueos y lo que necesita atención.',
        'dashboard.kai.promptAddTasks': 'Agregar tareas: ',
        'dashboard.cols.due': 'Vence',
        'dashboard.cols.status': 'Estado',
        'dashboard.cols.priority': 'Prioridad',
        'dashboard.attention.title': 'Necesita atención',
        'dashboard.attention.viewAll': 'Ver todo en Tareas',
        'dashboard.attention.reason.overdue': 'Vencida',
        'dashboard.attention.reason.overdueDays': 'Vencida {count}d',
        'dashboard.attention.reason.today': 'Vence hoy',
        'dashboard.attention.reason.high': 'Alta prioridad',
        'dashboard.attention.emptyTitle': 'Todo en orden',
        'dashboard.attention.emptySubtitle': 'Nada vencido, que venza hoy ni de alta prioridad. ¡Buen trabajo!',
        'dashboard.attention.completeAria': 'Marcar como completada',
        'dashboard.attention.completeAction': 'Marcar hecha',
        'dashboard.attention.completedToast': '"{title}" marcada como completada.',
        'dashboard.attention.openTask': 'Abrir tarea',
        'dashboard.attention.openAction': 'Abrir',
        'dashboard.glance.title': 'Resumen rápido',
        'dashboard.today.title': 'Hoy',
        'dashboard.today.openCalendar': 'Abrir calendario',
        'dashboard.today.dueToday': 'Termina hoy',
        'dashboard.today.endsToday': 'Termina hoy',
        'dashboard.today.startsToday': 'Empieza hoy',
        'dashboard.today.startsEndsToday': 'Empieza y termina hoy',
        'dashboard.today.emptyTitle': 'Sin tareas con fecha hoy',
        'dashboard.today.emptySubtitle': 'Tu calendario está despejado para esta fecha.',
        'dashboard.today.moreUpcoming': '+ {count} más próximas esta semana',
        'dashboard.dueOverdue.title': 'Necesita atención',
        'dashboard.dueOverdue.overdue': 'Vencida',
        'dashboard.dueOverdue.emptyTitle': 'Todo al día',
        'dashboard.dueOverdue.emptySubtitle': 'Nada vencido. ¡Buen trabajo!',
        'dashboard.upcoming.title': 'Próximas',
        'dashboard.upcoming.tomorrow': 'Mañana',
        'dashboard.upcoming.emptyTitle': 'Sin vencimientos próximos',
        'dashboard.upcoming.emptySubtitle': 'Las tareas futuras con fecha aparecerán aquí.',
        'dashboard.hero.activeProjectsLabel': 'Proyectos activos',
        'dashboard.hero.completionRateLabel': 'Tasa de finalización',
        'dashboard.hero.tasksCompletionRateLabel': 'Tareas',
        'dashboard.hero.projectsCompletionRateLabel': 'Proyectos',
        'dashboard.hero.projectsTrend': '📈 +2 este mes',
        'dashboard.hero.completionTrend': '🎯 Objetivo: 80%',
        'dashboard.projectAnalytics': '📊 Analítica proyectos',
        'dashboard.period.week': 'Semana',
        'dashboard.period.month': 'Mes',
        'dashboard.period.quarter': 'Trimestre',
        'dashboard.stat.pendingTasks': 'Tareas pendientes',
        'dashboard.stat.inProgress': 'En progreso',
        'dashboard.stat.highPriority': 'Alta prioridad',
        'dashboard.stat.overdue': 'Vencidas',
        'dashboard.stat.completed': 'Completadas',
        'dashboard.stat.projects': 'Proyectos',
        'dashboard.highPriorityHint': 'Tareas de alta prioridad con vencimiento en 7 días (o vencidas)',
        'dashboard.projectProgress': '🌊 Progreso del proyecto',
        'dashboard.legend.todo': 'Por hacer',
        'dashboard.legend.progress': 'En progreso',
        'dashboard.legend.review': 'En revisión',
        'dashboard.legend.complete': 'Completo',
        'dashboard.viewAll': 'Ver todo',
        'dashboard.quickActions': '⚡ Acciones rápidas',
        'dashboard.action.generateReport': 'Generar reporte',
        'dashboard.action.generateReportTitle': 'Abre el reporte de situación de tus proyectos y tareas, listo para descargar en PDF o guardar en Docs',
        'report.summary.toast.generating': 'Generando reporte…',
        'report.summary.toast.created': 'Reporte creado en Docs',
        'report.summary.toast.failed': 'No se pudo crear el reporte: {error}',
        'report.summary.toast.noPage': 'Docs no devolvió la página nueva',
        'report.summary.docTitle': 'Reporte de situación · {date}',
        'report.summary.meta': 'Datos al {date} · {projects} proyectos · {tasks} tareas',
        'report.summary.common.none': '—',
        'report.summary.common.andMore': '{names} y {count} más',
        'report.summary.kpis.heading': 'Indicadores',
        'report.summary.kpis.colMetric': 'Indicador',
        'report.summary.kpis.colValue': 'Valor',
        'report.summary.kpis.colDetail': 'Detalle',
        'report.summary.kpis.tasks': 'Tareas',
        'report.summary.kpis.tasksDetail': '{count} creadas este mes',
        'report.summary.kpis.completed': 'Completadas',
        'report.summary.kpis.countPct': '{count} ({pct}%)',
        'report.summary.kpis.completedDetail': '{count} completadas este mes',
        'report.summary.kpis.inProgress': 'En curso',
        'report.summary.kpis.inProgressDetail': 'Incluye las tareas en revisión · en {count} proyectos',
        'report.summary.kpis.overdue': 'Tareas vencidas',
        'report.summary.kpis.overdueDetail': '{pct}% de las tareas abiertas',
        'report.summary.kpis.lateProjects': 'Proyectos vencidos',
        'report.summary.kpis.lateProjectsDetail': 'Fecha de fin pasada con tareas abiertas',
        'report.summary.kpis.nextMilestone': 'Próximo hito',
        'report.summary.kpis.daysLeft': '{days} días',
        'report.summary.kpis.noMilestone': 'Ningún proyecto tiene una fecha de fin próxima',
        'report.summary.highlights.heading': 'Destacados',
        'report.summary.highlights.lateOne': '{name} venció hace {days} días y todavía tiene {open} tareas abiertas.',
        'report.summary.highlights.lateMany': '{count} proyectos vencieron y todavía tienen {open} tareas abiertas: {names}.',
        'report.summary.highlights.behindOne': '{name} lleva el {time}% de su plazo, pero solo el {done}% de sus tareas completadas.',
        'report.summary.highlights.behindMany': '{count} proyectos van más lentos que su plazo: {names}.',
        'report.summary.highlights.overdue': 'Hay {count} tareas vencidas; la más antigua lleva {days} días de retraso.',
        'report.summary.highlights.startOne': '{name} empieza el {date} con {tasks} tareas.',
        'report.summary.highlights.startMany': '{count} proyectos empiezan en los próximos {days} días, con {tasks} tareas en total.',
        'report.summary.highlights.milestone': 'Próximo hito: {name} el {date} (faltan {days} días).',
        'report.summary.highlights.allClear': 'No hay tareas vencidas ni proyectos atrasados.',
        'report.summary.state.late': 'Vencido',
        'report.summary.state.behind': 'Atrasado',
        'report.summary.state.onTrack': 'En plazo',
        'report.summary.state.ahead': 'Adelantado',
        'report.summary.state.notStarted': 'No iniciado',
        'report.summary.state.noDates': 'Sin fechas',
        'report.summary.state.noTasks': 'Sin tareas',
        'report.summary.state.done': 'Completado',
        'report.summary.projects.heading': 'Estado por proyecto',
        'report.summary.projects.intro': 'Primero los proyectos que necesitan atención. Tiempo es la parte del plazo del proyecto que ya pasó.',
        'report.summary.projects.empty': 'No hay proyectos activos.',
        'report.summary.projects.colProject': 'Proyecto',
        'report.summary.projects.colState': 'Estado',
        'report.summary.projects.colCompleted': 'Completadas',
        'report.summary.projects.colProgress': 'Avance',
        'report.summary.projects.colTime': 'Tiempo',
        'report.summary.projects.colEnd': 'Fin',
        'report.summary.projects.doneOf': '{done} de {total}',
        'report.summary.projects.lateBy': 'vencido hace {days} días',
        'report.summary.projects.unassigned': 'Sin proyecto',
        'report.summary.projects.archived': 'Proyectos archivados',
        'report.summary.throughput.heading': 'Ritmo de trabajo',
        'report.summary.throughput.intro': 'Tareas creadas y completadas cada mes. {month} cuenta hasta el {date}.',
        'report.summary.throughput.colMonth': 'Mes',
        'report.summary.throughput.colCreated': 'Creadas',
        'report.summary.throughput.colCompleted': 'Completadas',
        'report.summary.throughput.colOpen': 'Sin completar al cierre',
        'report.summary.throughput.pace': 'En los últimos 3 meses completos se completaron en promedio {avg} tareas al mes. A ese ritmo, las {remaining} tareas sin completar llevarían unos {months} meses.',
        'report.summary.throughput.noPace': 'No se completó ninguna tarea en los últimos 3 meses completos, así que no hay ritmo que proyectar.',
        'report.summary.deadlines.heading': 'Plazos',
        'report.summary.deadlines.agingHeading': 'Tareas vencidas por retraso',
        'report.summary.deadlines.noOverdue': 'No hay tareas vencidas.',
        'report.summary.deadlines.colDelay': 'Retraso',
        'report.summary.deadlines.colTasks': 'Tareas',
        'report.summary.deadlines.bucket.1-7': '1–7 días',
        'report.summary.deadlines.bucket.8-14': '8–14 días',
        'report.summary.deadlines.bucket.15-30': '15–30 días',
        'report.summary.deadlines.bucket.31+': 'Más de 30 días',
        'report.summary.deadlines.upcomingHeading': 'Próximos {days} días',
        'report.summary.deadlines.upcomingEmpty': 'No hay nada programado.',
        'report.summary.deadlines.projectStart': '{name} empieza · {count} tareas',
        'report.summary.deadlines.projectEnd': '{name} termina · {count} tareas sin completar',
        'report.summary.deadlines.more': '{count} más sin mostrar.',
        'report.summary.attention.heading': 'Requiere atención',
        'report.summary.attention.intro': 'Tareas abiertas con la fecha de fin vencida, de mayor a menor retraso. Las tareas con el mismo título en el mismo proyecto se agrupan.',
        'report.summary.attention.empty': 'No hay nada vencido.',
        'report.summary.attention.colTask': 'Tarea',
        'report.summary.attention.colProject': 'Proyecto',
        'report.summary.attention.colStatus': 'Estado',
        'report.summary.attention.colPriority': 'Prioridad',
        'report.summary.attention.colOverdue': 'Vencida',
        'report.summary.attention.days': '{days} días',
        'report.summary.attention.more': 'Se muestran las {shown} más atrasadas de {total}.',
        'report.summary.method.heading': 'Cómo se calcula',
        'report.summary.method.body': 'Datos al {date}. Avance = tareas completadas / tareas del proyecto. Tiempo = días transcurridos / duración del proyecto. Un proyecto está atrasado cuando su avance queda más de {tolerance} puntos por debajo de su tiempo, y vencido cuando pasó su fecha de fin con tareas abiertas. Una tarea está vencida cuando está abierta (ni completada ni en backlog) y pasó su fecha de fin. No se cuentan las tareas archivadas ni las plantillas recurrentes.',
        'report.summary.quality.heading': 'Calidad de datos',
        'report.summary.quality.none': 'No se encontraron huecos.',
        'report.summary.quality.projectsWithoutTasks': 'Proyectos sin tareas',
        'report.summary.quality.projectsWithoutDates': 'Proyectos sin fecha de inicio o de fin',
        'report.summary.quality.tasksWithoutProject': 'Tareas sin proyecto',
        'report.summary.quality.tasksInArchivedProjects': 'Tareas en proyectos archivados',
        'report.summary.quality.doneWithoutCompletedDate': 'Tareas completadas sin fecha de cierre (no cuentan en el ritmo de trabajo)',
        'report.summary.quality.throughputHidden': 'El ritmo de trabajo no se muestra porque hay menos de 2 meses de actividad.',
        'report.view.toolbarTitle': 'Reporte de situación',
        'report.view.back': 'Volver',
        'report.view.refresh': 'Actualizar',
        'report.view.link.tasks': 'Abrir en Tareas',
        'report.view.link.projects': 'Abrir en Proyectos',
        'report.view.refreshing': 'Actualizando…',
        'report.view.refreshHint': 'Trae los datos más recientes del servidor y vuelve a calcular el reporte',
        'report.view.toast.updated': 'Reporte actualizado con los últimos datos',
        'report.view.toast.upToDate': 'El reporte ya estaba al día',
        'report.view.toast.offline': 'No se pudieron traer datos nuevos. El reporte usa los datos ya cargados.',
        'report.view.meta.dateTime': '{date}, {time}',
        'report.view.saveDocs': 'Guardar en Docs',
        'report.view.downloadPdf': 'Descargar PDF',
        'report.view.pdfHint': 'Abre el diálogo de impresión. Elige “Guardar como PDF” como destino.',
        'report.view.eyebrow': 'Reporte de situación · Nautilus',
        'report.view.defaultTitle': 'Todos los proyectos',
        'report.view.titleHint': 'Haz clic para cambiar el título del reporte',
        'report.view.meta.asOf': 'Datos al',
        'report.view.meta.period': 'Periodo',
        'report.view.meta.counts': '{projects} proyectos · {tasks} tareas',
        'report.view.kpi.createdMonth': '+{count} este mes',
        'report.view.kpi.completedMonth': '{count} este mes',
        'report.view.kpi.inProjects': 'en {count} proyectos',
        'report.view.kpi.lateProjectsNote': 'fin pasado con tareas abiertas',
        'report.view.kpi.days': 'días',
        'report.view.highlights.source': 'Calculado a partir de tus datos',
        'report.view.portfolio.heading': 'Estado por proyecto',
        'report.view.portfolio.sub': 'Tareas por estado. Misma escala en todas las filas.',
        'report.view.portfolio.ends': 'fin {date}',
        'report.view.portfolio.tip': '{status}: {count} de {total}',
        'report.view.portfolio.clickHint': 'Clic para ver estas tareas',
        'report.view.portfolio.activeOnly': 'Solo se muestran los proyectos en curso.',
        'report.view.portfolio.hidden': 'Se ocultan {count} proyectos en planificación o backlog: todas sus tareas están por hacer o en backlog, o no tienen tareas.',
        'report.view.portfolio.hiddenOne': 'Se oculta 1 proyecto en planificación o backlog: todas sus tareas están por hacer o en backlog, o no tiene tareas.',
        'report.view.portfolio.noneActive': 'Todavía no hay proyectos en curso.',
        'report.view.pace.heading': 'Avance frente al tiempo',
        'report.view.pace.sub': 'Cada punto es un proyecto. Por debajo de la diagonal va más lento de lo que exige su plazo.',
        'report.view.pace.empty': 'Ningún proyecto tiene fecha de inicio y de fin, así que no se puede comparar el avance con el tiempo.',
        'report.view.pace.diagonal': 'ritmo justo',
        'report.view.pace.zone': 'más lento que el plazo',
        'report.view.pace.axisX': 'Tiempo transcurrido del plazo',
        'report.view.pace.axisY': 'Tareas completadas',
        'report.view.pace.joinTwo': '{a} y {b}',
        'report.view.pace.many': '{count} proyectos',
        'report.view.pace.lateTag': 'vencido · {days} d',
        'report.view.pace.tip': '{done}% completado · {time}% del tiempo',
        'report.view.pace.ahead': '{name} va por delante de su plazo ({done}% hecho, {time}% del tiempo).',
        'report.view.pace.outsideNote': 'Solo se grafican proyectos ya iniciados, con fecha de inicio y fin y al menos una tarea.',
        'report.view.pace.excluded': 'Se excluyen {count} proyectos: {parts}.',
        'report.view.pace.excludedOne': 'Se excluye 1 proyecto: {parts}.',
        'report.view.pace.outsideCount.notStarted': '{count} que aún no empiezan',
        'report.view.pace.outsideCount.noDates': '{count} sin alguna de las fechas',
        'report.view.pace.outsideCount.noTasks': '{count} sin tareas',
        'report.view.timeline.heading': 'Línea de tiempo',
        'report.view.timeline.sub': 'Relleno = % completado. Línea vertical = fecha de corte.',
        'report.view.timeline.empty': 'Ningún proyecto tiene fecha de inicio y de fin.',
        'report.view.timeline.noEnd': 'sin fecha de fin',
        'report.view.timeline.late': '{pct}% · vencido {days} d',
        'report.view.timeline.endsLater': '{pct} · fin {date} →',
        'report.view.timeline.tipDone': '{pct}% completado ({done} de {total})',
        'report.view.timeline.more': 'Se muestran {shown} de {total} proyectos.',
        'report.view.timeline.datedOnly': 'Solo se muestran proyectos con fecha de inicio y fin.',
        'report.view.timeline.excluded': 'Se excluyen {count} proyectos a los que les falta al menos una de estas fechas.',
        'report.view.timeline.excludedOne': 'Se excluye 1 proyecto al que le falta al menos una de estas fechas.',
        'report.view.throughput.sub': 'Tareas acumuladas: creadas y completadas por mes.',
        'report.view.throughput.gapLabel': 'sin completar',
        'report.view.throughput.tipCum': 'Acumulado: {created} creadas, {completed} completadas',
        'report.view.throughput.tipMonth': 'En el mes: {created} creadas, {completed} completadas',
        'report.view.throughput.partial': '* {month} hasta el día {day}.',
        'report.view.throughput.peak': 'En {month} se crearon {count} tareas. La brecha entre las dos curvas es el trabajo pendiente.',
        'report.view.throughput.gap': 'La brecha entre las dos curvas es el trabajo pendiente.',
        'report.view.throughput.avgBig': '≈{avg} / mes',
        'report.view.throughput.avgNote': 'completadas en promedio en los últimos 3 meses completos',
        'report.view.throughput.remainingBig': '{count} sin completar',
        'report.view.throughput.remainingNote': 'al ritmo actual, unos {months} meses de trabajo',
        'report.view.throughput.remainingNoPace': 'sin ritmo reciente para proyectar',
        'report.view.deadlines.agingTip': '{count} tareas vencidas',
        'report.view.deadlines.none': 'ninguna',
        'dashboard.action.addTask': 'Agregar tarea',
        'dashboard.action.viewCalendar': 'Ver calendario',
        'dashboard.action.newProject': 'Nuevo proyecto',
        'dashboard.quickActions.openTasks': 'Abrir tareas',
        'dashboard.quickActions.openProjects': 'Abrir proyectos',
        'dashboard.recentActivity': 'Actividad reciente',
        'dashboard.researchInsights': '🧠 Perspectivas',
        'dashboard.activity.emptyTitle': 'Bienvenido a tu panel',
        'dashboard.activity.emptySubtitle': 'Empieza a crear proyectos y tareas para ver actividad',
        'dashboard.activity.completed': 'Completaste "{title}" {projectPart}',
        'dashboard.activity.createdProject': 'Creaste el nuevo proyecto "{project}"',
        'dashboard.activity.addedTask': 'Agregaste la nueva tarea "{title}" {projectPart}',
        'dashboard.activity.completedAction': 'Completaste',
        'dashboard.activity.createdProjectAction': 'Creaste proyecto',
        'dashboard.activity.addedTaskAction': 'Agregaste tarea',
        'dashboard.activity.updatedDocAction': 'Actualizaste documento',
        'dashboard.activity.inLabel': 'en',
        'dashboard.activity.toLabel': 'a',
        'dashboard.activity.inProject': 'en {project}',
        'dashboard.activity.toProject': 'a {project}',
        'dashboard.activity.recently': 'Recientemente',
        'dashboard.activity.justNow': 'Justo ahora',
        'dashboard.activity.today': 'Hoy',
        'dashboard.activity.yesterday': 'Ayer',
        'dashboard.activity.daysAgoShort': 'hace {count}d',
        'dashboard.activity.minuteAgo': 'hace {count} minuto',
        'dashboard.activity.minutesAgo': 'hace {count} minutos',
        'dashboard.activity.hourAgo': 'hace {count} hora',
        'dashboard.activity.hoursAgo': 'hace {count} horas',
        'dashboard.activity.dayAgo': 'hace {count} día',
        'dashboard.activity.daysAgo': 'hace {count} días',
        'dashboard.trend.thisWeek': 'esta semana',
        'dashboard.trend.thisMonth': 'este mes',
        'dashboard.trend.thisQuarter': 'este trimestre',
        'dashboard.trend.dueTodayOne': '{count} vence hoy',
        'dashboard.trend.dueTodayMany': '{count} vencen hoy',
        'dashboard.trend.onTrack': 'En buen camino',
        'dashboard.trend.needsAttention': 'Necesita atención',
        'dashboard.trend.allOnTrack': 'Todo en buen camino',
        'dashboard.trend.criticalOne': '{count} crítica',
        'dashboard.trend.criticalMany': '{count} críticas',
        'dashboard.trend.completedOne': '{count} completada',
        'dashboard.trend.completedMany': '{count} completadas',
        'dashboard.trend.inProgress': 'En progreso',
        'dashboard.emptyProjects.title': 'Aún no hay proyectos',
        'dashboard.emptyProjects.subtitle': 'Crea tu primer proyecto para ver la visualización de progreso',
        'dashboard.tasks': 'tareas',
        'dashboard.activity.allTitle': 'Toda la actividad reciente',
        'dashboard.activity.allSubtitle': 'Historial completo de tu trabajo reciente',
        'dashboard.activity.backToDashboard': '← Volver al panel',
        'dashboard.insights.excellentTitle': 'Progreso excelente',
        'dashboard.insights.excellentDesc': '{percent}% de finalización supera el objetivo. Gran impulso.',
        'dashboard.insights.goodTitle': 'Buen progreso',
        'dashboard.insights.goodDesc': '{percent}% de finalización es sólido. Intenta llegar al objetivo de 80%.',
        'dashboard.insights.opportunityTitle': 'Oportunidad de progreso',
        'dashboard.insights.opportunityDesc': '{percent}% de finalización. Enfócate en completar tareas actuales para ganar impulso.',
        'dashboard.insights.actionTitle': 'Acción necesaria',
        'dashboard.insights.actionDesc': '{percent}% de finalización es bajo. Divide tareas grandes y empieza con las pequeñas.',
        'dashboard.insights.todayTitle': 'Enfoque de hoy',
        'dashboard.insights.todayDescOne': '{count} tarea vence hoy. Priorízala para mayor impacto.',
        'dashboard.insights.todayDescMany': '{count} tareas vencen hoy. Priorízalas para mayor impacto.',
        'dashboard.insights.overdueTitle': 'Elementos vencidos',
        'dashboard.insights.overdueDescOne': '{count} tarea vencida necesita atención. Atiéndela para evitar retrasos.',
        'dashboard.insights.overdueDescMany': '{count} tareas vencidas necesitan atención. Atiéndelas para evitar retrasos.',
        'dashboard.insights.highPriorityTitle': 'Enfoque de alta prioridad',
        'dashboard.insights.highPriorityDescOne': '{count} tarea de alta prioridad necesita atención inmediata.',
        'dashboard.insights.highPriorityDescMany': '{count} tareas de alta prioridad necesitan atención inmediata.',
        'dashboard.insights.emptyProjectsTitle': 'Proyectos sin tareas',
        'dashboard.insights.emptyProjectsDescOne': '{count} proyecto no tiene tareas aún. Agrega tareas para seguir el progreso.',
        'dashboard.insights.emptyProjectsDescMany': '{count} proyectos no tienen tareas aún. Agrega tareas para seguir el progreso.',
        'dashboard.insights.momentumTitle': 'Buen impulso',
        'dashboard.insights.momentumDescOne': '{count} tarea completada esta semana. Vas en buen ritmo.',
        'dashboard.insights.momentumDescMany': '{count} tareas completadas esta semana. Vas en buen ritmo.',
        'dashboard.insights.readyTitle': 'Listo para empezar',
        'dashboard.insights.readyDesc': 'Crea tu primer proyecto y agrega tareas para comenzar a seguir tu progreso.',
        'dashboard.insights.caughtUpTitle': 'Todo al día',
        'dashboard.insights.caughtUpDesc': 'Buen trabajo. No hay urgencias. Considera planificar tus próximos hitos.'
    }
};
